import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAllAssignments, updateAssignmentStatus, getYoxlamaStats, getAllTeachers, getAllStudents, StudentDetailData } from '../../services/adminService';
import { getSelectedExam } from '../../services/examService';
import { YoklaAssignment, Teacher, PoolTeacher } from '../../types';
import { ALL_FENNLER, SINIFLAR, QRUPLAR, normalizeQrup, qrupEquals, getQrupConfig } from '../../constants/fennConfig';
import { getFennFromQuestionNumberForBlok } from '../../services/apellyasiyaService';
import { blankCtxFromExam, getBuraxilisSubjectsForGrade } from '../../constants/buraxilisConfig';
import { displayFenn } from '../../utils/fennUtils';
import { buildApellyasiyaWhatsAppMessage, openWhatsAppWithMessage } from '../../utils/apellyasiyaWhatsApp';
import { detectBlokMode, getActualBlokQuestions } from '../../services/yoklaService';
import { createPoolAssignment, toggleTeacherInPool, addTeacherToPool, removeTeacherFromPool } from '../../services/poolAssignmentService';
import * as XLSX from 'xlsx';
import { collection, query, where, getDocs } from 'firebase/firestore';
import Icon from '../icons/Icon';
import { db } from '../../firebase';

const Container = styled.div`
  min-height: 100vh;
  background: #1e3a8a;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Header = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }

  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 14px;
  }
`;

const ExportButton = styled.button`
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  }

  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 14px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const StatCard = styled.div<{ color?: string }>`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: ${props => props.color || 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'};
  }

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #64748b;
  margin-bottom: 8px;
  font-weight: 500;
`;

const StatValue = styled.div`
  font-size: 36px;
  font-weight: 700;
  color: #1e293b;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const StatDescription = styled.div`
  font-size: 13px;
  color: #94a3b8;
  margin-top: 4px;
`;

const FilterSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FilterLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 8px;
`;

const FilterSelect = styled.select`
  width: 100%;
  padding: 10px 14px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  color: #1e293b;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 10px 14px;
  }
`;

const AssignmentsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const AssignmentCard = styled.div<{ status?: string }>`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: ${props => {
      switch (props.status) {
        case 'completed': return 'linear-gradient(135deg, #10b981, #059669)';
        case 'passive': return 'linear-gradient(135deg, #ef4444, #dc2626)';
        default: return 'linear-gradient(135deg, #3b82f6, #2563eb)';
      }
    }};
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const AssignmentHeader = styled.div`
  margin-bottom: 16px;
`;

const AssignmentTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const AssignmentSubtitle = styled.div`
  font-size: 14px;
  color: #64748b;
  margin-bottom: 12px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InfoLabel = styled.span`
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
`;

const InfoValue = styled.span`
  font-size: 16px;
  color: #1e293b;
  font-weight: 600;
`;

const ProgressSection = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 2px solid #f1f5f9;
`;

const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 14px;
  color: #64748b;
`;

const ProgressBar = styled.div<{ percentage: number }>`
  width: 100%;
  height: 10px;
  background: #e2e8f0;
  border-radius: 5px;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${props => props.percentage}%;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    transition: width 0.3s ease;
  }
`;

const QuestionProgressItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  padding: 8px;
  background: #f8fafc;
  border-radius: 8px;
`;

const QuestionBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  min-width: 45px;
  text-align: center;
`;

const QuestionProgressBar = styled.div<{ percentage: number }>`
  flex: 1;
  height: 6px;
  background: #e2e8f0;
  border-radius: 3px;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${props => props.percentage}%;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    transition: width 0.3s ease;
  }
`;

const QuestionProgressText = styled.span`
  font-size: 11px;
  color: #64748b;
  font-weight: 600;
  min-width: 60px;
  text-align: right;
`;

const StatusBadge = styled.div<{ status?: string }>`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    switch (props.status) {
      case 'completed': return 'linear-gradient(135deg, #10b981, #059669)';
      case 'passive': return 'linear-gradient(135deg, #ef4444, #dc2626)';
      default: return 'linear-gradient(135deg, #3b82f6, #2563eb)';
    }
  }};
  color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'danger' }>`
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  margin-top: 12px;
  width: 100%;
  
  ${props => props.variant === 'danger' ? `
    background: linear-gradient(135deg, #ef4444, #dc2626);
    color: white;
  ` : `
    background: linear-gradient(135deg, #10b981, #059669);
    color: white;
  `}

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: white;
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: white;
  font-size: 18px;
`;

const TabContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 8px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  display: flex;
  gap: 8px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const LegacyModeMessage = styled.div`
  background: white;
  border-radius: 16px;
  padding: 40px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
`;

const LegacyModeIcon = styled.div`
  font-size: 64px;
  margin-bottom: 20px;
`;

const LegacyModeTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 12px 0;
`;

const LegacyModeText = styled.p`
  font-size: 16px;
  color: #64748b;
  margin: 0 0 24px 0;
  max-width: 500px;
  line-height: 1.6;
`;

const LegacyModeButton = styled.button`
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  }
`;

const TabButton = styled.button<{ active?: boolean }>`
  flex: 1;
  padding: 14px 24px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  
  ${props => props.active ? `
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  ` : `
    background: transparent;
    color: #64748b;
    
    &:hover {
      background: #f1f5f9;
      color: #1e293b;
    }
  `}

  @media (max-width: 768px) {
    padding: 12px 20px;
    font-size: 14px;
  }
`;

const StatisticsTable = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
  overflow-x: auto;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const AssignmentsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.06);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 800px;
`;

const TableHead = styled.thead`
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
`;

const TableRow = styled.tr`
  border-bottom: 2px solid #f1f5f9;
  
  &:hover {
    background: #f8fafc;
  }
`;

const TableHeader = styled.th`
  padding: 16px;
  text-align: left;
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableCell = styled.td`
  padding: 16px;
  font-size: 15px;
  color: #475569;
`;

const ProgressCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const MiniProgressBar = styled.div<{ percentage: number }>`
  flex: 1;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${props => props.percentage}%;
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    transition: width 0.3s ease;
  }
`;

const PercentageText = styled.span`
  font-weight: 600;
  color: #1e293b;
  min-width: 50px;
`;

const ViewToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
  flex-wrap: wrap;
  margin-top: 12px;
`;

const ViewToggleButton = styled.button<{ active?: boolean }>`
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  background: ${props => props.active ? '#1d4ed8' : '#e5e7eb'};
  color: ${props => props.active ? 'white' : '#4b5563'};
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.active ? '#1d4ed8' : '#d1d5db'};
  }
`;

const WhatsAppButton = styled.button`
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  background: #25D366;
  color: white;
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(37, 211, 102, 0.3);
    background: #20BD5A;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const SearchInput = styled.input`
  padding: 10px 12px;
  border-radius: 10px;
  border: 2px solid #e2e8f0;
  font-size: 14px;
  width: 100%;
  max-width: 260px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

// Blok Statistics Card Component
// Blok Apellyasiya Table Components
const BlokApellyasiyaTableContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const BlokApellyasiyaSummary = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const BlokApellyasiyaSummaryCard = styled.div<{ variant: 'pending' | 'approved' | 'rejected' | 'total' }>`
  padding: 16px;
  border-radius: 12px;
  background: ${props => {
    switch (props.variant) {
      case 'pending': return '#fef3c7';
      case 'approved': return '#f0fdf4';
      case 'rejected': return '#fef2f2';
      default: return '#f8fafc';
    }
  }};
  border: 2px solid ${props => {
    switch (props.variant) {
      case 'pending': return '#fbbf24';
      case 'approved': return '#86efac';
      case 'rejected': return '#fca5a5';
      default: return '#e2e8f0';
    }
  }};

  .count {
    font-size: 24px;
    font-weight: 700;
    color: ${props => {
      switch (props.variant) {
        case 'pending': return '#92400e';
        case 'approved': return '#166534';
        case 'rejected': return '#991b1b';
        default: return '#1e293b';
      }
    }};
  }

  .label {
    font-size: 13px;
    color: #64748b;
    margin-top: 4px;
  }
`;

const BlokApellyasiyaTable = styled.div`
  overflow-x: auto;
`;

const BlokApellyasiyaTableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1.5fr 1fr 1fr 1.5fr 1fr 1fr 1fr 1fr;
  gap: 12px;
  padding: 12px 16px;
  background: #f8fafc;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 12px;
`;

const BlokApellyasiyaRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1.5fr 1fr 1fr 1.5fr 1fr 1fr 1fr 1fr;
  gap: 12px;
  padding: 12px 16px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  color: #1e293b;
  margin-bottom: 8px;
  transition: all 0.2s;
  cursor: pointer;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    transform: translateY(-1px);
  }
`;

const BlokApellyasiyaStatus = styled.span<{ status: 'pending' | 'approved' | 'rejected' }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  background: ${props => {
    switch (props.status) {
      case 'pending': return '#fef3c7';
      case 'approved': return '#f0fdf4';
      case 'rejected': return '#fef2f2';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'pending': return '#92400e';
      case 'approved': return '#166534';
      case 'rejected': return '#991b1b';
    }
  }};
`;

const BlokScoreChange = styled.span<{ change: number | undefined }>`
  font-weight: 600;
  color: ${props => {
    if (props.change === undefined) return '#64748b';
    if (props.change > 0) return '#10b981';
    if (props.change < 0) return '#ef4444';
    return '#64748b';
  }};
`;

// ============================================
// TEACHER STATISTICS SECTION COMPONENTS
// ============================================

const TeacherStatsContainer = styled.div`
  background: white;
  border-radius: 16px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;
`;

const TeacherStatsHeader = styled.div`
  padding: 20px 24px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background 0.2s ease;

  &:hover {
    background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
  }
`;

const TeacherStatsHeaderTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TeacherStatsHeaderIcon = styled.span<{ expanded: boolean }>`
  font-size: 20px;
  transition: transform 0.3s ease;
  transform: ${props => props.expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const TeacherStatsContent = styled.div<{ expanded: boolean }>`
  max-height: ${props => props.expanded ? '10000px' : '0'};
  overflow: hidden;
  transition: max-height 0.4s ease;
`;

const TeacherStatsSummary = styled.div`
  padding: 20px 24px;
  background: #f8fafc;
  border-bottom: 2px solid #e2e8f0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

const TeacherStatsSummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TeacherStatsSummaryLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TeacherStatsSummaryValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
`;

const TeacherStatsGrid = styled.div`
  padding: 24px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 16px;
  }
`;

const TeacherStatsCard = styled.div`
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  &:hover {
    border-color: #3b82f6;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
    transform: translateY(-2px);
  }
`;

const TeacherStatsCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #f1f5f9;
`;

const TeacherStatsCardName = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
`;

const TeacherStatsCardProgress = styled.div<{ percentage: number }>`
  font-size: 14px;
  font-weight: 600;
  color: ${props => {
    if (props.percentage === 100) return '#10b981';
    if (props.percentage > 0) return '#f59e0b';
    return '#ef4444';
  }};
`;

const TeacherStatsCardNumbers = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 16px;
`;

const TeacherStatsCardNumberItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TeacherStatsCardNumberLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
`;

const TeacherStatsCardNumberValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
`;

const TeacherStatsProgressBar = styled.div<{ percentage: number }>`
  width: 100%;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 16px;
`;

const TeacherStatsProgressFill = styled.div<{ percentage: number }>`
  height: 100%;
  width: ${props => props.percentage}%;
  background: ${props => {
    if (props.percentage === 100) return 'linear-gradient(90deg, #10b981, #059669)';
    if (props.percentage > 0) return 'linear-gradient(90deg, #f59e0b, #d97706)';
    return 'linear-gradient(90deg, #ef4444, #dc2626)';
  }};
  transition: width 0.3s ease;
`;

const TeacherStatsAssignmentsList = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f1f5f9;
`;

const TeacherStatsAssignmentsTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  margin-bottom: 8px;
`;

const TeacherStatsAssignmentItem = styled.div`
  font-size: 13px;
  color: #475569;
  padding: 6px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TeacherStatsAssignmentInfo = styled.span`
  font-weight: 500;
`;

const TeacherStatsAssignmentCount = styled.span`
  font-weight: 600;
  color: #3b82f6;
`;

// ============================================
// TEACHER-GROUPED APELLYASIYA VIEW COMPONENTS
// ============================================

// Task 1.1: TeacherGroupCard - Main container for teacher section
const TeacherGroupCard = styled.div`
  background: white;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  }
`;

// Task 1.2: TeacherGroupHeader - Teacher info summary card
const TeacherGroupHeader = styled.div`
  padding: 20px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background 0.2s ease;

  &:hover {
    background: #f1f5f9;
  }
`;

const TeacherInfoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
`;

const TeacherAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #3b82f6;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 18px;
  flex-shrink: 0;
`;

const TeacherNameSection = styled.div`
  flex: 1;
`;

const TeacherName = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 4px;
`;

const TeacherId = styled.div`
  font-size: 13px;
  color: #64748b;
`;

// Task 1.3: TeacherStatsRow - Statistics display row
const TeacherStatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-top: 12px;
`;

// Task 1.4: TeacherStatItem - Individual stat with icon and value
const TeacherStatItem = styled.div<{ variant: 'total' | 'completed' | 'pending' | 'rejected' }>`
  text-align: center;
  padding: 12px;
  border-radius: 8px;
  background: ${props => {
    switch (props.variant) {
      case 'total': return '#eff6ff';
      case 'completed': return '#f0fdf4';
      case 'pending': return '#fef3c7';
      case 'rejected': return '#fef2f2';
    }
  }};
  border: 1px solid ${props => {
    switch (props.variant) {
      case 'total': return '#bfdbfe';
      case 'completed': return '#bbf7d0';
      case 'pending': return '#fde68a';
      case 'rejected': return '#fecaca';
    }
  }};

  .stat-value {
    font-size: 24px;
    font-weight: 700;
    color: ${props => {
      switch (props.variant) {
        case 'total': return '#1e40af';
        case 'completed': return '#166534';
        case 'pending': return '#92400e';
        case 'rejected': return '#991b1b';
      }
    }};
  }

  .stat-label {
    font-size: 12px;
    color: #64748b;
    margin-top: 4px;
  }
`;

// Task 1.11: OverallProgressBar - Teacher-level progress bar
const OverallProgressBar = styled.div<{ percentage: number; color: string }>`
  width: 100%;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-top: 12px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${props => props.percentage}%;
    background: ${props => props.color};
    transition: width 0.3s ease;
  }
`;

const TeacherProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
`;

// Task 1.10: ExpandCollapseButton - Toggle button for sections
const ExpandCollapseButton = styled.button<{ expanded?: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: none;
  background: #e2e8f0;
  color: #475569;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: #cbd5e1;
  }

  ${props => props.expanded && `
    transform: rotate(180deg);
  `}
`;

// Task 1.5: StudentSubgroup - Collapsible student list container
const StudentSubgroup = styled.div<{ expanded?: boolean }>`
  max-height: ${props => props.expanded ? '2000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

// Task 1.6: StudentSubgroupHeader - Subgroup header with expand/collapse
const StudentSubgroupHeader = styled.div<{ variant: 'pending' | 'completed' | 'rejected' }>`
  padding: 12px 20px;
  background: ${props => {
    switch (props.variant) {
      case 'pending': return '#fef3c7';
      case 'completed': return '#f0fdf4';
      case 'rejected': return '#fef2f2';
    }
  }};
  border-bottom: 1px solid ${props => {
    switch (props.variant) {
      case 'pending': return '#fde68a';
      case 'completed': return '#bbf7d0';
      case 'rejected': return '#fecaca';
    }
  }};
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    filter: brightness(0.95);
  }
`;

const SubgroupTitle = styled.div<{ variant: 'pending' | 'completed' | 'rejected' }>`
  font-size: 14px;
  font-weight: 600;
  color: ${props => {
    switch (props.variant) {
      case 'pending': return '#92400e';
      case 'completed': return '#166534';
      case 'rejected': return '#991b1b';
    }
  }};
  display: flex;
  align-items: center;
  gap: 8px;
`;

// Task 1.9: SubgroupProgress - Subgroup-level progress indicator
const SubgroupProgress = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #64748b;
`;

const SubgroupMiniProgress = styled.div<{ count: number; total: number }>`
  width: 60px;
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${props => (props.count / props.total) * 100}%;
    background: #10b981;
  }
`;

// Task 1.7: StudentApellyasiyaCard - Individual student apellyasiya item
const StudentApellyasiyaCard = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.2s ease;
  cursor: pointer;

  &:hover {
    background: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }
`;

// Task 1.8: StudentDetailsGrid - Student information display
const StudentDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  margin-bottom: 8px;
`;

const StudentDetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StudentDetailLabel = styled.span`
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StudentDetailValue = styled.span`
  font-size: 14px;
  color: #1e293b;
  font-weight: 600;
`;

const StudentScoresRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #f1f5f9;
  flex-wrap: wrap;
`;

const ScoreComparison = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
`;

const OriginalScore = styled.span`
  color: #94a3b8;
  text-decoration: line-through;
`;

const NewScore = styled.span<{ change: number }>`
  color: ${props => {
    if (props.change > 0) return '#10b981';
    if (props.change < 0) return '#ef4444';
    return '#1e293b';
  }};
  font-weight: 700;
`;

const ScoreChangeIndicator = styled.span<{ change: number }>`
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 600;
  background: ${props => {
    if (props.change > 0) return '#dcfce7';
    if (props.change < 0) return '#fee2e2';
    return '#f1f5f9';
  }};
  color: ${props => {
    if (props.change > 0) return '#166534';
    if (props.change < 0) return '#991b1b';
    return '#64748b';
  }};
`;

const ApellyasiyaStatusBadge = styled.span<{ status: 'pending' | 'completed' | 'approved' | 'rejected' }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  background: ${props => {
    switch (props.status) {
      case 'pending': return '#fef3c7';
      case 'completed':
      case 'approved': return '#f0fdf4';
      case 'rejected': return '#fef2f2';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'pending': return '#92400e';
      case 'completed':
      case 'approved': return '#166534';
      case 'rejected': return '#991b1b';
    }
  }};
`;

const TeachersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const EmptyTeachersState = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  .icon {
    font-size: 64px;
    margin-bottom: 16px;
  }

  .title {
    font-size: 20px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 8px;
  }

  .subtitle {
    font-size: 14px;
    color: #64748b;
  }
`;

// Pool Assignment Modal Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 600px;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;

  h2 {
    font-size: 20px;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 10px;
  }
`;

const ModalCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
  line-height: 1;

  &:hover {
    color: #1e293b;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;

  label {
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 8px;
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  color: #1e293b;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 15px;
  color: #1e293b;
  background: white;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const TeacherCheckboxList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px;
`;

const TeacherCheckboxItem = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #f1f5f9;
  }

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }

  .teacher-info {
    flex: 1;

    .name {
      font-weight: 600;
      color: #1e293b;
    }

    .fenn {
      font-size: 12px;
      color: #64748b;
    }
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e2e8f0;
`;

const ModalButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  ${props => props.$variant === 'primary' ? `
    background: linear-gradient(135deg, #a855f7 0%, #7c3aed 100%);
    color: white;
    border: none;
    box-shadow: 0 2px 8px rgba(168, 85, 247, 0.3);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(168, 85, 247, 0.4);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }
  ` : `
    background: white;
    color: #64748b;
    border: 2px solid #e2e8f0;

    &:hover {
      background: #f8fafc;
      border-color: #cbd5e1;
    }
  `}
`;

const PoolButton = styled.button`
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  background: linear-gradient(135deg, #a855f7 0%, #7c3aed 100%);
  color: white;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(168, 85, 247, 0.3);
  }

  @media (max-width: 768px) {
    padding: 10px 20px;
    font-size: 14px;
  }
`;

const PoolBadgeStyled = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 999px;
  background: linear-gradient(135deg, #a855f7 0%, #7c3aed 100%);
  color: white;
  font-size: 11px;
  font-weight: 600;
`;

const PoolTeacherList = styled.div`
  padding: 16px;
  background: linear-gradient(135deg, #faf5ff, #f3e8ff);
  border-radius: 8px;
  margin-bottom: 12px;
  border-left: 4px solid #a855f7;
`;

const PoolTeacherItem = styled.div<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: ${props => props.$isActive ? 'white' : '#fee2e2'};
  border-radius: 8px;
  margin-bottom: 8px;
  border: 1px solid ${props => props.$isActive ? '#e9d5ff' : '#fecaca'};

  &:last-child {
    margin-bottom: 0;
  }

  .teacher-info {
    display: flex;
    align-items: center;
    gap: 10px;

    .name {
      font-weight: 600;
      color: #1e293b;
    }

    .status {
      font-size: 11px;
      padding: 2px 8px;
      border-radius: 999px;
      background: ${props => props.$isActive ? '#dcfce7' : '#fee2e2'};
      color: ${props => props.$isActive ? '#166534' : '#991b1b'};
    }
  }
`;

const FreezeButton = styled.button<{ $isFrozen: boolean }>`
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  background: ${props => props.$isFrozen ? '#10b981' : '#ef4444'};
  color: white;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
`;

const AssignmentManagement: React.FC = () => {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState<YoklaAssignment[]>([]);
  const [filteredAssignments, setFilteredAssignments] = useState<YoklaAssignment[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [buraxilisStudents, setBuraxilisStudents] = useState<StudentDetailData[]>([]);
  const [teachers, setTeachers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [fennFilter, setFennFilter] = useState('');
  const [sinifFilter, setSinifFilter] = useState('');
  const [qrupFilter, setQrupFilter] = useState('');
  const [altqrupFilter, setAltqrupFilter] = useState(''); // For Blok I/III qrup
  const [variantFilter, setVariantFilter] = useState(''); // For buraxilis
  const [progressFilter, setProgressFilter] = useState(''); // '', 'completed', 'not-started', 'in-progress'
  const [scoreChangeFilter, setScoreChangeFilter] = useState(''); // '', 'increased', 'decreased', 'unchanged'
  const [isBuraxilis, setIsBuraxilis] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'assignments' | 'apellyasiyas' | 'statistics'>('overview');
  const [expandedStatsCards, setExpandedStatsCards] = useState<string[]>([]);
  const [isTeacherStatsExpanded, setIsTeacherStatsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Task 2.1: Update viewMode to support three modes including 'teachers'
  const [viewMode, setViewMode] = useState<'cards' | 'list' | 'teachers'>('cards');
  const [sortField, setSortField] = useState<'createdAt' | 'fenn'>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Apellyasiya filter states
  const [apellyasiyaStatusFilter, setApellyasiyaStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [apellyasiyaFennFilter, setApellyasiyaFennFilter] = useState('');
  const [apellyasiyaQrupFilter, setApellyasiyaQrupFilter] = useState('');
  const [selectedApellyasiya, setSelectedApellyasiya] = useState<BlokApellyasiya | null>(null);

  // Task 2.2 & 2.4: Teacher-grouped view expansion states
  const [expandedTeacherGroups, setExpandedTeacherGroups] = useState<Set<string>>(new Set());
  const [expandedStudentSubgroups, setExpandedStudentSubgroups] = useState<Map<string, Set<'pending' | 'completed' | 'rejected'>>>(new Map());

  // Pool assignment modal state
  const [showPoolModal, setShowPoolModal] = useState(false);
  const [poolFormData, setPoolFormData] = useState({
    sinif: '',
    fenn: '',
    variant: '',
    selectedTeacherIds: [] as string[]
  });
  const [poolCreating, setPoolCreating] = useState(false);
  const [poolError, setPoolError] = useState<string | null>(null);
  const [poolSuccess, setPoolSuccess] = useState<string | null>(null);

  // Buraxılış pool — grade 11 blank from selected exam (Sept 2026 cutoff)
  const getPoolBlankCtx = () => blankCtxFromExam(getSelectedExam());

  // Get available subjects for selected sinif
  const getPoolSubjectsForSinif = (sinif: string): string[] => {
    const grade = parseInt(sinif, 10);
    return getBuraxilisSubjectsForGrade(grade, getPoolBlankCtx()).map((c) => c.subject);
  };

  // Get question numbers for selected sinif + fenn
  const getPoolQuestionsForSelection = (sinif: string, fenn: string): string[] => {
    const grade = parseInt(sinif, 10);
    const subjectConfig = getBuraxilisSubjectsForGrade(grade, getPoolBlankCtx()).find(
      (c) => c.subject === fenn
    );
    return subjectConfig ? subjectConfig.questions : [];
  };

  // Helper function to calculate progress percentage (same as UI display)
  const calculateProgressPercentage = (assignment: YoklaAssignment): number => {
    const totalCount = assignment.totalCount || 0;
    const completedCount = assignment.completedCount || 0;

    // For Buraxılış and Blok with questionProgress, use question-level data
    if ((assignment.examType === 'buraxilis' || assignment.examType === 'Blok') &&
        assignment.questionNumbers &&
        assignment.questionNumbers.length > 0 &&
        assignment.questionProgress) {
      let totalQCompleted = 0;
      let totalQTotal = 0;

      assignment.questionNumbers.forEach((q: string) => {
        const qProgress = assignment.questionProgress?.[q];
        totalQCompleted += qProgress?.completed ?? 0;
        totalQTotal += qProgress?.total ?? totalCount;
      });

      return totalQTotal > 0 ? (totalQCompleted / totalQTotal) * 100 : 0;
    }

    // Default: use aggregate values
    return totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  };

  useEffect(() => {
    loadData();
  }, []);

  // Handle URL parameter to auto-open pool modal
  const [searchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('openPoolModal') === 'true') {
      setShowPoolModal(true);
      // Clear the URL parameter
      navigate('/admin/assignments', { replace: true });
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    filterAssignments();
  }, [
    statusFilter,
    fennFilter,
    sinifFilter,
    qrupFilter,
    variantFilter,
    progressFilter,
    scoreChangeFilter,
    assignments,
    activeTab,
    searchQuery,
    sortField,
    sortDirection
  ]);

  // Hook: Detect if Blok exam uses new format (check exam.mode field)
  const useBlokModeDetection = (selectedExam: any): { hasNewBlokFormat: boolean } => {
    return useMemo(() => {
      // Only check for Blok exams
      if (isBuraxilis) {
        return { hasNewBlokFormat: false };
      }

      // Check exam.mode field from Firebase
      // mode === 'legacy' → legacy format (hide new UI)
      // mode !== 'legacy' or undefined → normal mode (show new UI)
      const isLegacy = selectedExam?.mode === 'legacy';

      return { hasNewBlokFormat: !isLegacy };
    }, [selectedExam, isBuraxilis]);
  };

  const selectedExam = getSelectedExam();
  const { hasNewBlokFormat } = useBlokModeDetection(selectedExam);

  // Hook: Group Blok students by Sinif + Qrup + Altqrup
  interface BlokGroup {
    groupKey: string;
    grade: string;
    qrup: string;
    subGroup?: string;
    students: StudentDetailData[];
  }

  const useBlokGrouping = (students: StudentDetailData[]): BlokGroup[] => {
    return useMemo(() => {
      // Only for Blok NEW format
      if (isBuraxilis || !hasNewBlokFormat) {
        return [];
      }

      // Create group key for each student
      const getBlokGroupKey = (student: StudentDetailData): string => {
        const grade = String(student.grade || '');
        const qrup = student.specialtyGroup || '';
        const subGroup = student.subGroup || '';

        // I & III qrup have subGroups
        const nQrup = normalizeQrup(qrup) || qrup;
        const hasSubGroup = (nQrup === 'I' || nQrup === 'III') && subGroup;

        return hasSubGroup
          ? `${grade} ${nQrup} ${subGroup}`  // e.g., "11 I RK"
          : `${grade} ${nQrup}`;             // e.g., "10 II"
      };

      // Group students by key
      const groupMap = new Map<string, StudentDetailData[]>();

      students.forEach(student => {
        const groupKey = getBlokGroupKey(student);

        if (!groupMap.has(groupKey)) {
          groupMap.set(groupKey, []);
        }

        groupMap.get(groupKey)!.push(student);
      });

      // Convert to array and add metadata
      const groups: BlokGroup[] = Array.from(groupMap.entries()).map(([groupKey, students]) => {
        const firstStudent = students[0];
        const grade = String(firstStudent.grade || '');
        const qrup = normalizeQrup(firstStudent.specialtyGroup) || firstStudent.specialtyGroup || '';
        const subGroup = firstStudent.subGroup || undefined;

        return {
          groupKey,
          grade,
          qrup,
          subGroup,
          students
        };
      });

      // Sort by grade (8, 9, 10, 11) then qrup (I, II, III, IV)
      const gradeOrder: { [key: string]: number } = { '8': 0, '9': 1, '10': 2, '11': 3 };
      const qrupOrder: { [key: string]: number } = { 'I': 1, 'II': 2, 'III': 3, 'IV': 4 };

      groups.sort((a, b) => {
        const gradeDiff = (gradeOrder[a.grade] || 0) - (gradeOrder[b.grade] || 0);
        if (gradeDiff !== 0) return gradeDiff;

        const aQ = normalizeQrup(a.qrup) || a.qrup;
        const bQ = normalizeQrup(b.qrup) || b.qrup;
        return (qrupOrder[aQ] || 0) - (qrupOrder[bQ] || 0);
      });

      return groups;
    }, [students, isBuraxilis, hasNewBlokFormat]);
  };

  const blokGroups = useBlokGrouping(buraxilisStudents);

  // Hook: Calculate statistics for each Blok group
  interface BlokFennStats {
    fenn: string;
    totalQuestions: number;
    teacherChecked: number;
    adminVerified: number;
    emptyDetected: number;
    questions: {
      [qNum: string]: {
        total: number;
        graded: number;
        verified: number;
      };
    };
  }

  interface BlokGroupStats {
    groupKey: string;
    grade: string;
    qrup: string;
    subGroup?: string;
    studentCount: number;
    fennler: {
      [fennName: string]: BlokFennStats;
    };
    overall: {
      totalQuestions: number;
      totalTeacherChecked: number;
      totalAdminVerified: number;
      totalEmptyDetected: number;
    };
  }

  // Blok Apellyasiya Types
  interface BlokApellyasiya {
    type: 'apellyasiya';
    id: string;
    studentId: string;
    studentName: string;
    studentSurname: string;
    grade: string;
    sinif: string;
    qrup: string;
    subGroup?: string;
    questionNum: string;
    fenn: string;
    originalScore: number;
    newScore?: number;
    scoreChange?: number;
    originalTeacher?: string;
    reviewTeacher?: string;
    hocaId?: string;
    status: 'pending' | 'approved' | 'rejected';
    totalCount: number;
    completedCount: number;
    date: Date;
  }

  // Utility function to map Blok questions to fənn (1/2/3/4 və I/II/III/IV)
  const getFennNameForBlokQuestion = (questionNum: string, qrup: string, subGroup?: string): string => {
    return getFennFromQuestionNumberForBlok(questionNum, normalizeQrup(qrup) || qrup, subGroup) || 'Unknown';
  };

  // Hook to detect and extract Blok apellyasiyas
  const useBlokApellyasiyas = (students: StudentDetailData[]): BlokApellyasiya[] => {
    return useMemo(() => {
      if (isBuraxilis || !hasNewBlokFormat) return [];

      const apellyasiyas: BlokApellyasiya[] = [];

      students.forEach(student => {
        const questions = getActualBlokQuestions(student);

        questions.forEach(q => {
          const qNumStr = `Q${q.qNum}`;
          const checkedField = `${qNumStr}_c` as keyof typeof student;

          // Check if this question has apellyasiya
          if (student[checkedField] && typeof student[checkedField] === 'object') {
            const checkedData = student[checkedField] as any;

            // Check for apellyasiya_status or apellyasiya_result
            const hasApellyasiya = checkedData.apellyasiya_status === true ||
                                   checkedData.apellyasiya_status === 'pending' ||
                                   checkedData.apellyasiya_result === true ||
                                   checkedData.apellyasiya_result === false;

            if (hasApellyasiya) {
              const fenn = getFennNameForBlokQuestion(
                qNumStr,
                student.specialtyGroup || '',
                student.subGroup
              );

              const status: 'pending' | 'approved' | 'rejected' =
                checkedData.apellyasiya_result === true ? 'approved' :
                checkedData.apellyasiya_result === false ? 'rejected' :
                'pending';

              const originalScore = checkedData.net || 0;
              const newScore = checkedData.apellyasiya_net ?? undefined;
              const scoreChange = newScore !== undefined ? newScore - originalScore : undefined;

              apellyasiyas.push({
                type: 'apellyasiya',
                id: `${student._id}-${qNumStr}`,
                studentId: student._id || '',
                studentName: student.name || '',
                studentSurname: student.surname || '',
                grade: String(student.grade || ''),
                sinif: String(student.grade || ''),
                qrup: student.specialtyGroup || '',
                subGroup: student.subGroup,
                questionNum: qNumStr,
                fenn,
                originalScore,
                newScore,
                scoreChange,
                originalTeacher: checkedData.hocaName,
                reviewTeacher: checkedData.apellyasiya_hocaName,
                status,
                totalCount: 1,
                completedCount: status === 'approved' ? 1 : 0,
                date: new Date(checkedData.apellyasiya_date || Date.now())
              });
            }
          }
        });
      });

      return apellyasiyas;
    }, [students, isBuraxilis, hasNewBlokFormat]);
  };

  const useBlokStatistics = (groups: BlokGroup[]): Map<string, BlokGroupStats> => {
    return useMemo(() => {
      if (isBuraxilis || !hasNewBlokFormat || groups.length === 0) {
        return new Map();
      }

      console.log('📊 Blok Statistics - Total students:', groups.reduce((sum, g) => sum + g.students.length, 0));

      const statsMap = new Map<string, BlokGroupStats>();

      groups.forEach(group => {
        // Get fənn configuration for this qrup
        const qrupConfig = getQrupConfig(group.qrup);

        if (!qrupConfig) {
          console.warn(`No qrup config found for ${group.qrup}`);
          return;
        }

        // Initialize stats map for this group
        const fennStats: { [fennName: string]: BlokFennStats } = {};

        // Initialize each fənn with zero stats
        qrupConfig.fennler.forEach(fennConfig => {
          const fennKey = fennConfig.subGroup
            ? `${fennConfig.fenn} (${fennConfig.subGroup})`
            : fennConfig.fenn;

          fennStats[fennKey] = {
            fenn: fennConfig.fenn,
            totalQuestions: 0,
            teacherChecked: 0,
            adminVerified: 0,
            emptyDetected: 0,
            questions: {}
          };

          // Initialize questions array for this fənn
          for (let q = fennConfig.start; q <= fennConfig.end; q++) {
            const qNum = `Q${q}`;
            fennStats[fennKey].questions[qNum] = {
              total: 0,
              graded: 0,
              verified: 0
            };
          }
        });

        // Calculate stats for each student in group
        group.students.forEach(student => {
          const actualQuestions = getActualBlokQuestions(student);

          actualQuestions.forEach(q => {
            // Determine which fənn this question belongs to
            let fennName = 'Unknown';

            // Map question to fənn based on qrup and question number
            const gQrup = normalizeQrup(group.qrup) || group.qrup;
            if (gQrup === 'I') {
              if (q.qNum >= 1 && q.qNum <= 30) fennName = 'Riyaziyyat';
              else if (q.qNum >= 31 && q.qNum <= 60) fennName = 'Fizika';
              else if (q.qNum >= 61 && q.qNum <= 90) {
                fennName = group.subGroup === 'RK' ? 'Kimya (RK)' : 'İnformatika (Rİ)';
              }
            } else if (gQrup === 'II') {
              if (q.qNum >= 1 && q.qNum <= 30) fennName = 'Riyaziyyat';
              else if (q.qNum >= 31 && q.qNum <= 60) fennName = 'Coğrafiya';
              else if (q.qNum >= 61 && q.qNum <= 90) fennName = 'Tarix';
            } else if (gQrup === 'III') {
              if (q.qNum >= 1 && q.qNum <= 30) fennName = 'Azərbaycan dili';
              else if (q.qNum >= 31 && q.qNum <= 60) fennName = 'Tarix';
              else if (q.qNum >= 61 && q.qNum <= 90) {
                fennName = group.subGroup === 'DT' ? 'Ədəbiyyat (DT)' : 'Coğrafiya (TC)';
              }
            } else if (gQrup === 'IV') {
              if (q.qNum >= 1 && q.qNum <= 30) fennName = 'Biologiya';
              else if (q.qNum >= 31 && q.qNum <= 60) fennName = 'Kimya';
              else if (q.qNum >= 61 && q.qNum <= 90) fennName = 'Fizika';
            }

            if (!fennStats[fennName]) {
              console.warn(`No fenn stats found for ${fennName}`);
              return;
            }

            // Increment totals
            fennStats[fennName].totalQuestions++;
            fennStats[fennName].questions[`Q${q.qNum}`].total++;

            // Check teacher grading
            const gradingData = (student as any)[`Q${q.qNum}_c`];
            if (gradingData?.hocaId) {
              fennStats[fennName].teacherChecked++;
              fennStats[fennName].questions[`Q${q.qNum}`].graded++;
            }

            // Check admin verification (for new format)
            const qData = (student as any)[`Q${q.qNum}`];
            if (qData?.is_empty) {
              fennStats[fennName].emptyDetected++;
            }
            if (qData?.double_check_empty === true) {
              fennStats[fennName].adminVerified++;
              fennStats[fennName].questions[`Q${q.qNum}`].verified++;
            }
          });
        });

        // Calculate overall stats
        const overallStats = {
          totalQuestions: 0,
          totalTeacherChecked: 0,
          totalAdminVerified: 0,
          totalEmptyDetected: 0
        };

        Object.values(fennStats).forEach(fennStat => {
          overallStats.totalQuestions += fennStat.totalQuestions;
          overallStats.totalTeacherChecked += fennStat.teacherChecked;
          overallStats.totalAdminVerified += fennStat.adminVerified;
          overallStats.totalEmptyDetected += fennStat.emptyDetected;
        });

        // Create group stats entry
        statsMap.set(group.groupKey, {
          groupKey: group.groupKey,
          grade: group.grade,
          qrup: group.qrup,
          subGroup: group.subGroup,
          studentCount: group.students.length,
          fennler: fennStats,
          overall: overallStats
        });
      });

      // Calculate and log totals
      const totalEmptyDetected = Array.from(statsMap.values()).reduce((sum, stat) => sum + stat.overall.totalEmptyDetected, 0);
      const totalAdminVerified = Array.from(statsMap.values()).reduce((sum, stat) => sum + stat.overall.totalAdminVerified, 0);
      console.log('📊 Blok Statistics - Total empty detected:', totalEmptyDetected);
      console.log('📊 Blok Statistics - Total admin verified:', totalAdminVerified);

      return statsMap;
    }, [groups, isBuraxilis, hasNewBlokFormat]);
  };

  const blokStatistics = useBlokStatistics(blokGroups);

  // Hook: Get all Blok apellyasiyas
  const blokApellyasiyas = useBlokApellyasiyas(buraxilisStudents);

  // Merge hook-generated apellyasiyas with Firestore-loaded apellyasiyas
  const allBlokApellyasiyas = useMemo(() => {
    // Get Firestore-loaded Blok apellyasiyas from assignments state
    const firestoreApellyasiyas = assignments
      .filter(a => (a as any).type === 'apellyasiya' && (a as any).examType === 'Blok')
      .map(a => {
        const fa = a as any;
        // Handle Firestore Timestamp conversion
        let date = new Date();
        if (fa.createdAt) {
          // Firestore Timestamp has toDate() method
          if (typeof fa.createdAt.toDate === 'function') {
            date = fa.createdAt.toDate();
          } else if (fa.createdAt instanceof Date) {
            date = fa.createdAt;
          } else if (typeof fa.createdAt === 'number') {
            date = new Date(fa.createdAt);
          }
        }

        return {
          type: 'apellyasiya' as const,
          id: fa.id,
          studentId: fa.studentId,
          studentName: fa.studentName || '',
          studentSurname: '',
          grade: fa.sinif || '',
          sinif: fa.sinif || '',
          qrup: fa.qrup || '',
          subGroup: fa.subGroup,
          questionNum: fa.questionNumber,
          fenn: fa.fenn,
          originalScore: 0,
          newScore: undefined,
          scoreChange: undefined,
          originalTeacher: fa.hocaName,
          reviewTeacher: undefined,
          status: (fa.apellyasiya_status === 'completed' ? 'approved' :
                   fa.apellyasiya_status === 'pending' ? 'pending' :
                   'pending') as 'pending' | 'approved' | 'rejected',
          totalCount: 1,
          completedCount: fa.status === 'completed' ? 1 : 0,
          date
        };
      });

    // Combine with hook-generated apellyasiyas
    // DEDUPE: Use Map to remove duplicates by composite key (studentId + questionNum)
    // This prevents the same apellyasiya from appearing twice when it exists in both
    // the hook-generated data (from student Q##_c fields) and Firestore assignments
    const apellyasiyaMap = new Map<string, BlokApellyasiya>();

    // Add hook-generated apellyasiyas first (from student data)
    blokApellyasiyas.forEach(apa => {
      const key = `${apa.studentId}-${apa.questionNum}`;
      // Hook-generated data takes precedence as it has real-time status from Q##_c fields
      apellyasiyaMap.set(key, apa);
    });

    // Add Firestore apellyasiyas only if not already present
    firestoreApellyasiyas.forEach(apa => {
      const key = `${apa.studentId}-${apa.questionNum}`;
      if (!apellyasiyaMap.has(key)) {
        apellyasiyaMap.set(key, apa);
      }
    });

    return Array.from(apellyasiyaMap.values());
  }, [blokApellyasiyas, assignments]);

  // ============================================
  // Task 3: TEACHER GROUPING HOOK
  // useTeacherGroupedApellyasiyas - Groups apellyasiyas by teacher
  // ============================================

  interface StudentApellyasiya {
    id: string;
    studentId: string;
    studentName: string;
    studentSurname: string;
    grade: string;
    sinif: string;
    qrup: string;
    subGroup?: string;
    questionNum: string;
    fenn: string;
    originalScore: number;
    newScore?: number;
    scoreChange?: number;
    originalTeacher?: string;
    reviewTeacher?: string;
    status: 'pending' | 'approved' | 'rejected';
    date: Date;
  }

  interface TeacherGroup {
    teacherId: string;
    teacherName: string;
    totalApellyasiyas: number;
    completedCount: number;
    pendingCount: number;
    rejectedCount: number;
    completionPercentage: number;
    subgroups: {
      status: 'pending' | 'completed' | 'rejected';
      count: number;
      students: StudentApellyasiya[];
    }[];
  }

  const useTeacherGroupedApellyasiyas = (
    blokApellyasiyas: BlokApellyasiya[],
    buraxilisAssignments: YoklaAssignment[],
    statusFilter: 'all' | 'pending' | 'approved' | 'rejected',
    fennFilter: string,
    qrupFilter: string
  ): TeacherGroup[] => {
    return useMemo(() => {
      // Task 3.5: Handle both Blok and Buraxılıš apellyasiya structures
      const allApellyasiyas: StudentApellyasiya[] = [];

      // Process Blok apellyasiyas
      blokApellyasiyas.forEach(app => {
        // Apply filters
        if (statusFilter !== 'all' && app.status !== statusFilter) return;
        if (fennFilter && app.fenn !== fennFilter) return;
        if (qrupFilter && app.qrup !== qrupFilter) return;

        // Get teacher info from review teacher first, then original teacher
        const teacherId = app.reviewTeacher || app.originalTeacher || 'unknown';
        const teacherName = app.reviewTeacher || app.originalTeacher || 'Unknown';

        allApellyasiyas.push({
          id: app.id,
          studentId: app.studentId,
          studentName: app.studentName,
          studentSurname: app.studentSurname,
          grade: app.grade,
          sinif: app.sinif,
          qrup: app.qrup,
          subGroup: app.subGroup,
          questionNum: app.questionNum,
          fenn: app.fenn,
          originalScore: app.originalScore,
          newScore: app.newScore,
          scoreChange: app.scoreChange,
          originalTeacher: app.originalTeacher,
          reviewTeacher: app.reviewTeacher,
          status: app.status,
          date: app.date
        });
      });

      // Process Buraxılıš apellyasiyas from assignments
      buraxilisAssignments.forEach(a => {
        if ((a as any).type !== 'apellyasiya') return;

        const status: 'pending' | 'approved' | 'rejected' =
          (a as any).apellyasiya_status === 'completed' ? 'approved' :
          (a as any).apellyasiya_status === 'rejected' ? 'rejected' : 'pending';

        // Apply filters
        if (statusFilter !== 'all' && status !== statusFilter) return;
        if (fennFilter && a.fenn !== fennFilter) return;
        if (qrupFilter && (a.variant || a.qrup) !== qrupFilter) return;

        const teacherId = a.hocaId || 'unknown';
        const teacherName = a.hocaName || 'Unknown';

        allApellyasiyas.push({
          id: a.id,
          studentId: (a as any).studentId || '',
          studentName: (a as any).studentName || '',
          studentSurname: '',
          grade: a.sinif || '',
          sinif: a.sinif || '',
          qrup: a.qrup || '',
          subGroup: (a as any).subGroup,
          questionNum: (a as any).questionNumber || '',
          fenn: a.fenn || '',
          originalScore: (a as any).apellyasiyaResult?.previous_score || 0,
          newScore: (a as any).apellyasiyaResult?.new_score,
          scoreChange: (a as any).apellyasiyaResult?.new_score !== undefined
            ? (a as any).apellyasiyaResult.new_score - ((a as any).apellyasiyaResult.previous_score || 0)
            : undefined,
          originalTeacher: a.hocaName,
          reviewTeacher: (a as any).apellyasiyaResult?.apellyasiya_hoca,
          status,
          date: new Date()
        });
      });

      // DEDUPE: Remove duplicates before grouping by teacher
      // Use composite key: studentId + questionNum to identify unique apellyasiyas
      const uniqueApellyasiyasMap = new Map<string, StudentApellyasiya>();
      allApellyasiyas.forEach(app => {
        const key = `${app.studentId}-${app.questionNum}`;
        // Only keep the first occurrence (hook-generated data takes precedence)
        if (!uniqueApellyasiyasMap.has(key)) {
          uniqueApellyasiyasMap.set(key, app);
        }
      });
      const uniqueApellyasiyas = Array.from(uniqueApellyasiyasMap.values());

      // DEBUG: Log if duplicates were removed
      if (allApellyasiyas.length !== uniqueApellyasiyas.length) {
        console.log(`🔧 Teacher grouping: Removed ${allApellyasiyas.length - uniqueApellyasiyas.length} duplicates`);
        console.log(`   Before: ${allApellyasiyas.length}, After: ${uniqueApellyasiyas.length}`);
      }

      // Task 3.2: Group apellyasiyas by teacherId
      const teacherMap = new Map<string, StudentApellyasiya[]>();

      uniqueApellyasiyas.forEach(app => {
        const teacherId = app.reviewTeacher || app.originalTeacher || 'unknown';
        if (!teacherMap.has(teacherId)) {
          teacherMap.set(teacherId, []);
        }
        teacherMap.get(teacherId)!.push(app);
      });

      // Task 3.3: Calculate per-teacher statistics and create teacher groups
      const teacherGroups: TeacherGroup[] = [];

      teacherMap.forEach((apellyasiyas, teacherId) => {
        const teacherName = apellyasiyas[0]?.reviewTeacher || apellyasiyas[0]?.originalTeacher || 'Unknown';

        // Task 3.3: Calculate statistics
        const total = apellyasiyas.length;
        const completed = apellyasiyas.filter(a => a.status === 'approved').length;
        const pending = apellyasiyas.filter(a => a.status === 'pending').length;
        const rejected = apellyasiyas.filter(a => a.status === 'rejected').length;
        const percentage = total > 0 ? (completed / total) * 100 : 0;

        // Task 4.1-4.4: Group student apellyasiyas by status into subgroups
        const pendingStudents = apellyasiyas.filter(a => a.status === 'pending');
        const completedStudents = apellyasiyas.filter(a => a.status === 'approved');
        const rejectedStudents = apellyasiyas.filter(a => a.status === 'rejected');

        teacherGroups.push({
          teacherId,
          teacherName,
          totalApellyasiyas: total,
          completedCount: completed,
          pendingCount: pending,
          rejectedCount: rejected,
          completionPercentage: percentage,
          subgroups: [
            { status: 'pending', count: pending, students: pendingStudents },
            { status: 'completed', count: completed, students: completedStudents },
            { status: 'rejected', count: rejected, students: rejectedStudents }
          ]
        });
      });

      // Task 3.4: Sort teachers by pending count (descending), then name (ascending)
      teacherGroups.sort((a, b) => {
        if (a.pendingCount !== b.pendingCount) {
          return b.pendingCount - a.pendingCount;
        }
        return a.teacherName.localeCompare(b.teacherName);
      });

      return teacherGroups;
    }, [blokApellyasiyas, buraxilisAssignments, statusFilter, fennFilter, qrupFilter]);
  };

  // Get filtered assignments for Buraxılıš apellyasiyas
  const buraxilisApellyasiyaAssignments = useMemo(() => {
    return assignments.filter(a =>
      (a as any).type === 'apellyasiya' &&
      activeTab === 'apellyasiyas'
    );
  }, [assignments, activeTab]);

  // Use the teacher grouping hook for Blok
  const teacherGroups = useTeacherGroupedApellyasiyas(
    allBlokApellyasiyas,
    buraxilisApellyasiyaAssignments,
    apellyasiyaStatusFilter,
    apellyasiyaFennFilter,
    apellyasiyaQrupFilter
  );

  // Filter mapping for Buraxılış teacher grouping
  // Map Buraxılış filters (statusFilter: 'active' | 'completed' | '') to hook format
  const buraxilisStatusForGrouping = useMemo(() => {
    if (!statusFilter) return 'all';
    if (statusFilter === 'completed') return 'approved';
    if (statusFilter === 'active') return 'pending';
    return 'all';
  }, [statusFilter]);

  // Use the teacher grouping hook for Buraxılış with mapped filters
  const buraxilisTeacherGroups = useTeacherGroupedApellyasiyas(
    [], // No Blok apellyasiyas for Buraxılış mode
    buraxilisApellyasiyaAssignments,
    buraxilisStatusForGrouping,
    fennFilter, // Use fennFilter directly
    variantFilter // Maps to qrupFilter in hook (variant for Buraxılış)
  );

  // Task 8: Helper functions for expand/collapse
  const toggleTeacherGroup = (teacherId: string) => {
    setExpandedTeacherGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(teacherId)) {
        newSet.delete(teacherId);
      } else {
        newSet.add(teacherId);
      }
      return newSet;
    });
  };

  const toggleStudentSubgroup = (teacherId: string, status: 'pending' | 'completed' | 'rejected') => {
    setExpandedStudentSubgroups(prev => {
      const newMap = new Map(prev);
      const currentSet = newMap.get(teacherId) || new Set();

      if (currentSet.has(status)) {
        currentSet.delete(status);
      } else {
        currentSet.add(status);
      }

      newMap.set(teacherId, currentSet);
      return newMap;
    });
  };

  const isSubgroupExpanded = (teacherId: string, status: 'pending' | 'completed' | 'rejected'): boolean => {
    return expandedStudentSubgroups.get(teacherId)?.has(status) || false;
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      const selectedExam = getSelectedExam();
      const collectionFilter = selectedExam?.collection || 'scanned2610';
      const examType = selectedExam?.type === 'buraxilis' ? 'buraxilis' : 'blok';
      const isBuraxilisExam = selectedExam?.type === 'buraxilis';
      
      setIsBuraxilis(isBuraxilisExam);
      
      // Set default tab based on exam type
      if (isBuraxilisExam && activeTab === 'overview') {
        setActiveTab('overview');
      }
      
      console.log('📊 AssignmentManagement: Seçili imtahan:', selectedExam);
      console.log('🔍 AssignmentManagement: Collection filter:', collectionFilter);
      console.log('🎓 AssignmentManagement: Exam type:', examType);
      
      const [assignmentsData, statsData, studentsData, teachersData] = await Promise.all([
        getAllAssignments(collectionFilter),
        getYoxlamaStats(collectionFilter, examType),
        getAllStudents(collectionFilter),  // Load for BOTH Buraxılısh AND Blok
        getAllTeachers()
      ]);
      
      setTeachers(teachersData);

      // Apellyasiya atamalarını yüklə
      let apellyasiyaAssignments: any[] = [];
      if (examType === 'buraxilis') {
        try {
          const { getDoc, doc } = await import('firebase/firestore');
          const apellyasiyaQuery = query(
            collection(db, 'yoxla-s'),
            where('type', '==', 'apellyasiya'),
            where('collectionName', '==', collectionFilter)
          );
          const apellyasiyaSnapshot = await getDocs(apellyasiyaQuery);

          // Paralel olarak öğrenci document'lerini yüklə ve Q##_c field'ından status kontrol et
          const apellyasiyaPromises = apellyasiyaSnapshot.docs.map(async (apDoc) => {
            const apData = apDoc.data();
            try {
              // Öğrenci document'ini yüklə
              const studentDoc = await getDoc(doc(db, apData.collectionName, apData.studentDocumentId));
              if (studentDoc.exists()) {
                const studentData = studentDoc.data();
                const qField = `${apData.questionNumber}_c`;
                const gradingData: any = studentData?.[qField];
                
                // Q##_c field'ından apellyasiya status'unu kontrol et
                const isApellyasiyaCompleted = gradingData && 
                                              typeof gradingData === 'object' && 
                                              (gradingData.apellyasiya_result === true || 
                                               gradingData.apellyasiya_status === true);
                
                return {
                  id: apDoc.id,
                  ...apData,
                  type: 'apellyasiya',
                  // Status'u Q##_c field'ından al
                  apellyasiya_status: isApellyasiyaCompleted ? 'completed' : 'pending',
                  status: isApellyasiyaCompleted ? 'completed' : 'active',
                  // Apellyasiya için progress: 1 öğrenci, tamamlandıysa 1/1, değilse 0/1
                  totalCount: 1,
                  completedCount: isApellyasiyaCompleted ? 1 : 0,
                  // Öğrenci bilgileri
                  studentName: `${studentData.firstName || ''} ${studentData.lastName || ''}`.trim(),
                  studentId: apData.studentId,
                  // Öğrenci sinif və variant bilgileri
                  sinif: studentData.grade ? String(studentData.grade) : '',
                  variant: studentData.variant || '',
                  // Q##_c field'ından apellyasiya nəticəsi
                  apellyasiyaResult: gradingData
                };
              }
            } catch (error) {
              console.error(`❌ Student document yüklənərkən xəta (${apData.studentDocumentId}):`, error);
            }
            
            // Fallback: yoxla-s document'indeki status'u kullan
            const fallbackCompleted = apData.apellyasiya_status === 'completed';
            return {
              id: apDoc.id,
              ...apData,
              type: 'apellyasiya',
              apellyasiya_status: apData.apellyasiya_status || 'pending',
              status: fallbackCompleted ? 'completed' : 'active',
              // Apellyasiya için progress: 1 öğrenci, tamamlandıysa 1/1, değilse 0/1
              totalCount: 1,
              completedCount: fallbackCompleted ? 1 : 0,
              // Öğrenci bilgileri (fallback)
              studentName: '',
              studentId: apData.studentId,
              sinif: '',
              variant: '',
              apellyasiyaResult: null
            };
          });
          
          apellyasiyaAssignments = await Promise.all(apellyasiyaPromises);
        } catch (error) {
          console.error('Apellyasiya atamaları yüklənərkən xəta:', error);
        }
      }

      // Load Blok apellyasiya assignments
      let blokApellyasiyaAssignments: any[] = [];
      if (examType === 'blok') {
        try {
          const { getDoc, doc } = await import('firebase/firestore');
          const apellyasiyaQuery = query(
            collection(db, 'yoxla-s'),
            where('type', '==', 'apellyasiya'),
            where('collectionName', '==', collectionFilter),
            where('examType', '==', 'Blok')
          );
          const apellyasiyaSnapshot = await getDocs(apellyasiyaQuery);

          // Process each Blok apellyasiya assignment
          const apellyasiyaPromises = apellyasiyaSnapshot.docs.map(async (apDoc) => {
            const apData = apDoc.data();
            console.log('🔍 Processing Blok apellyasiya doc:', apDoc.id, 'apData:', apData);

            // Always return a valid apellyasiya object
            try {
              // Load student document
              const studentDoc = await getDoc(doc(db, apData.collectionName, apData.studentDocumentId));
              if (studentDoc.exists()) {
                const studentData = studentDoc.data();
                const qField = `${apData.questionNumber}_c`;
                const gradingData: any = studentData?.[qField];

                const isApellyasiyaCompleted = gradingData &&
                      typeof gradingData === 'object' &&
                      (gradingData.apellyasiya_result === true ||
                       gradingData.apellyasiya_status === true);

                const result = {
                  id: apDoc.id,
                  ...apData,
                  type: 'apellyasiya' as const,
                  apellyasiya_status: isApellyasiyaCompleted ? 'completed' : 'pending',
                  status: isApellyasiyaCompleted ? 'completed' : 'active',
                  totalCount: 1,
                  completedCount: isApellyasiyaCompleted ? 1 : 0,
                  studentName: `${studentData.firstName || ''} ${studentData.lastName || ''}`.trim(),
                  studentId: apData.studentId,
                  sinif: studentData.grade ? String(studentData.grade) : '',
                  qrup: apData.qrup || '',
                  subGroup: apData.subGroup,
                  apellyasiyaResult: gradingData,
                  fenn: apData.fenn,
                  questionNumber: apData.questionNumber,
                  hocaId: apData.hocaId,
                  hocaName: apData.hocaName,
                  collectionName: apData.collectionName,
                  examType: apData.examType
                };
                console.log('✅ Student doc found, returning:', result);
                return result;
              }
            } catch (error) {
              console.error(`❌ Student document yüklənərkən xəta (${apData.studentDocumentId}):`, error);
            }

            // Fallback - always return a valid object
            const fallback = {
              id: apDoc.id,
              ...apData,
              type: 'apellyasiya' as const,
              apellyasiya_status: apData.apellyasiya_status || 'pending',
              status: 'active',
              totalCount: 1,
              completedCount: 0,
              studentName: '',
              studentId: apData.studentId,
              sinif: '',
              qrup: apData.qrup || '',
              subGroup: apData.subGroup || '',
              apellyasiyaResult: null,
              fenn: apData.fenn,
              questionNumber: apData.questionNumber,
              examType: apData.examType
            };
            console.log('⚠️ Using fallback for apellyasiya:', fallback);
            return fallback;
          });

          blokApellyasiyaAssignments = await Promise.all(apellyasiyaPromises);
        } catch (error) {
          console.error('Blok apellyasiyaları yüklənərkən xəta:', error);
        }
      }

      console.log('✅ AssignmentManagement: Yüklenen atamalar:', assignmentsData.length);
      console.log('✅ AssignmentManagement: Apellyasiya atamaları:', apellyasiyaAssignments.length);
      console.log('✅ AssignmentManagement: Blok apellyasiya atamaları:', blokApellyasiyaAssignments.length);
      console.log('✅ AssignmentManagement: Statistikalar:', statsData);

      // Debug: Her assignment'ın totalCount'unu kontrol et
      assignmentsData.forEach((a: any) => {
        console.log(`📝 Assignment: ${a.fenn} - ${a.sinif} - ${a.variant || a.qrup}: ${a.completedCount}/${a.totalCount}`);
      });

      // Debug: Log blok apellyasiya assignments
      blokApellyasiyaAssignments.forEach((a: any) => {
        console.log(`📝 Blok Apellyasiya: ${a.fenn} - Q${a.questionNumber} - type: ${a.type} - studentId: ${a.studentId} - sinif: ${a.sinif} - totalCount: ${a.totalCount}`);
      });

      // Apellyasiya atamalarını normal atamalarla birləşdir (hem Buraxılısh hem Blok)
      const allApellyasiyaAssignments = [...apellyasiyaAssignments, ...blokApellyasiyaAssignments];
      const allAssignments = [...allApellyasiyaAssignments, ...assignmentsData];
      console.log('✅ Setting assignments state. Total:', allAssignments.length, 'Apellyasiyas:', allApellyasiyaAssignments.length);
      setAssignments(allAssignments);
      setStats(statsData);
      // Hem Buraxılış hem Blok için students set et (Statistics tab için gerekli)
      if (examType === 'buraxilis' || examType === 'blok') {
        setBuraxilisStudents(studentsData as StudentDetailData[]);
      }
    } catch (error) {
      console.error('Veriler yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to calculate statistics for grade/variant/xdil combinations
  const calculateStatisticsTable = () => {
    if (!isBuraxilis) return [];

    console.log('📊 Starting statistics calculation...');
    console.log('📊 Total assignments:', assignments.length);
    console.log('📊 Sample assignments:', assignments.slice(0, 3));

    const statistics: any[] = [];
    const grades = [8, 9, 10, 11];
    const variants = ['A', 'B'];
    const xdilOptions = ['İngilis dili', 'Rus dili', 'Alman dili', 'Fransız dili'];

    grades.forEach(grade => {
      variants.forEach(variant => {
        xdilOptions.forEach(xdil => {
          console.log(`\n🔍 Checking: Grade ${grade}, Variant ${variant}, Xdil ${xdil}`);
          
          // Find ALL assignments for this xdil (both teachers), exclude admin
          const allXdilAssignments = assignments.filter(a => {
            const match = a.sinif === String(grade) &&
              (a.variant || a.qrup) === variant &&
              a.fenn === xdil &&
              a.hocaId !== 'admin';
            
            if (match) {
              console.log(`   ✅ Found assignment: ${a.hocaName} (${a.hocaId}), Questions: ${a.questionNumbers?.join(', ')}`);
            }
            return match;
          });

          if (allXdilAssignments.length === 0) {
            console.log(`   ❌ No assignments found`);
            return;
          }

          // Get total students (should be same for all assignments in this grade/variant)
          const totalStudents = allXdilAssignments[0]?.totalCount || 0;
          console.log(`   👥 Total students: ${totalStudents}`);
          
          // Calculate questions per student for this xdil
          const allQuestions = new Set<string>();
          allXdilAssignments.forEach(a => {
            a.questionNumbers?.forEach(q => allQuestions.add(q));
          });
          
          const questionsPerStudent = allQuestions.size;
          const totalQuestions = questionsPerStudent * totalStudents;
          console.log(`   ❓ Questions per student: ${questionsPerStudent}, Total questions: ${totalQuestions}`);
          
          // Calculate checked questions across all teachers
          let checkedQuestions = 0;
          const questionCheckedCount: { [key: string]: number } = {};
          
          allXdilAssignments.forEach(a => {
            console.log(`      📝 Assignment by ${a.hocaName}:`);
            if (a.questionProgress) {
              a.questionNumbers?.forEach(qNum => {
                const qProgress = a.questionProgress![qNum];
                const completed = qProgress?.completed ?? 0;
                questionCheckedCount[qNum] = (questionCheckedCount[qNum] || 0) + completed;
                console.log(`         ${qNum}: ${completed}/${qProgress?.total ?? 0} completed`);
              });
            }
          });
          
          // Sum up all checked questions
          checkedQuestions = Object.values(questionCheckedCount).reduce((sum, val) => sum + val, 0);
          console.log(`   ✅ Total checked: ${checkedQuestions}/${totalQuestions}`);
          
          const percentage = totalQuestions > 0 ? Math.round((checkedQuestions / totalQuestions) * 100) : 0;

          statistics.push({
            grade,
            variant,
            xdil,
            totalStudents,
            totalQuestions,
            checkedQuestions,
            percentage
          });
        });
      });
    });

    console.log('📊 Final Statistics:', statistics);
    return statistics;
  };

  // Helper function to convert grade number to Roman numeral
  const gradeToRoman = (grade: string): string => {
    const gradeNum = parseInt(grade);
    const romanMap: { [key: number]: string } = {
      1: 'I', 2: 'II', 3: 'III', 4: 'IV', 5: 'V',
      6: 'VI', 7: 'VII', 8: 'VIII', 9: 'IX', 10: 'X', 11: 'XI'
    };
    return romanMap[gradeNum] || grade;
  };

  // Helper function to aggregate all assignments for a teacher
  interface TeacherAssignmentSummary {
    sinif: string;
    qrupOrVariant: string;
    fenn: string;
    questionCount: number;
  }

  const getTeacherAllAssignments = (
    teacherId: string,
    examCollection: string
  ): TeacherAssignmentSummary[] => {
    // Filter assignments for this teacher in this exam (exclude apellyasiya and admin)
    const teacherAssignments = assignments.filter(a =>
      a.hocaId === teacherId &&
      a.collectionName === examCollection &&
      (a as any).type !== 'apellyasiya' &&
      a.hocaId !== 'admin'
    );

    // Aggregate by unique sinif + qrup/variant + fenn combination
    const aggregationMap = new Map<string, TeacherAssignmentSummary>();

    teacherAssignments.forEach(assignment => {
      const sinif = assignment.sinif || '';
      const qrupOrVariant = assignment.variant || assignment.qrup || '';
      const fenn = assignment.fenn || '';

      // Calculate total questions from questionProgress (questions × students)
      let questionCount = 0;
      if (assignment.questionProgress && Object.keys(assignment.questionProgress).length > 0) {
        // Sum up the total students for each question (matches Excel export logic)
        Object.values(assignment.questionProgress).forEach((qProgress: any) => {
          questionCount += qProgress.total || 0;
        });
      } else if (assignment.questionNumbers && assignment.questionNumbers.length > 0 && assignment.totalCount) {
        // Fallback: if no questionProgress, use totalCount (which is already total questions)
        questionCount = assignment.totalCount;
      } else {
        // Last fallback: use questionNumbers.length
        questionCount = assignment.questionNumbers?.length || 0;
      }

      // Create unique key
      const key = `${sinif}-${qrupOrVariant}-${fenn}`;

      if (aggregationMap.has(key)) {
        // Add to existing entry
        const existing = aggregationMap.get(key)!;
        existing.questionCount += questionCount;
      } else {
        // Create new entry
        aggregationMap.set(key, {
          sinif,
          qrupOrVariant,
          fenn,
          questionCount
        });
      }
    });

    // Convert to array and sort by sinif, then qrup/variant
    return Array.from(aggregationMap.values()).sort((a, b) => {
      // Sort by sinif (numeric)
      const sinifA = parseInt(a.sinif) || 0;
      const sinifB = parseInt(b.sinif) || 0;
      if (sinifA !== sinifB) {
        return sinifA - sinifB;
      }

      // Then by qrup/variant (A, B, I, II, III, IV)
      const qrupOrder: { [key: string]: number } = {
        'A': 1, 'B': 2,
        'I': 1, 'II': 2, 'III': 3, 'IV': 4
      };
      const orderA = qrupOrder[a.qrupOrVariant] || 99;
      const orderB = qrupOrder[b.qrupOrVariant] || 99;

      return orderA - orderB;
    });
  };

  // Teacher statistics calculation function
  interface TeacherStatistics {
    teacherId: string;
    teacherName: string;
    totalAssignments: number;
    totalQuestions: number;
    completedQuestions: number;
    remainingQuestions: number;
    progressPercentage: number;
    assignments: TeacherAssignmentSummary[];
  }

  const calculateTeacherStatistics = (): TeacherStatistics[] => {
    const selectedExam = getSelectedExam();
    const examCollection = selectedExam?.collection || 'scanned2610';

    // Get all unique teachers (exclude admin and apellyasiya)
    const teacherIds = new Set<string>();
    assignments.forEach(a => {
      if (a.hocaId && 
          a.hocaId !== 'admin' && 
          a.collectionName === examCollection &&
          (a as any).type !== 'apellyasiya') {
        teacherIds.add(a.hocaId);
      }
    });

    const teacherStats: TeacherStatistics[] = [];

    teacherIds.forEach(teacherId => {
      const teacher = teachers.find(t => t.id === teacherId);
      if (!teacher) return;

      // Get all assignments for this teacher
      const teacherAssignments = getTeacherAllAssignments(teacherId, examCollection);
      
      // Get original assignment data for progress calculation
      const teacherOriginalAssignments = assignments.filter(a =>
        a.hocaId === teacherId &&
        a.collectionName === examCollection &&
        (a as any).type !== 'apellyasiya' &&
        a.hocaId !== 'admin'
      );

      // Calculate total questions
      const totalQuestions = teacherAssignments.reduce(
        (sum, a) => sum + a.questionCount, 0
      );

      // Calculate completed questions
      let totalCompleted = 0;
      teacherOriginalAssignments.forEach((assignmentData: any) => {
        // Try to get completed count from questionProgress first
        if (assignmentData.questionProgress && Object.keys(assignmentData.questionProgress).length > 0) {
          Object.values(assignmentData.questionProgress).forEach((qProgress: any) => {
            totalCompleted += qProgress.completed || 0;
          });
        } else {
          // Fallback: use completedCount
          totalCompleted += assignmentData.completedCount || 0;
        }
      });

      const remainingQuestions = totalQuestions - totalCompleted;
      const progressPercentage = totalQuestions > 0 ? (totalCompleted / totalQuestions) * 100 : 0;

      teacherStats.push({
        teacherId,
        teacherName: teacher.name,
        totalAssignments: teacherAssignments.length,
        totalQuestions,
        completedQuestions: totalCompleted,
        remainingQuestions,
        progressPercentage,
        assignments: teacherAssignments
      });
    });

    // Sort by teacher name
    return teacherStats.sort((a, b) => a.teacherName.localeCompare(b.teacherName));
  };

  // WhatsApp mesaj gönderme fonksiyonu
  const sendWhatsAppMessage = (assignment: any) => {
    const teacher = teachers.find(t => t.id === assignment.hocaId);
    if (!teacher || !teacher.phoneNumber) {
      alert('Bu müəllimin telefon nömrəsi sistemdə qeydiyyata alınmayıb. Zəhmət olmasa əvvəlcə telefon nömrəsini əlavə edin.');
      return;
    }

    const isApellyasiya = (assignment as any).type === 'apellyasiya';
    let message = '';

    if (isApellyasiya) {
      // Apellyasiya mesajı – modul: bütün təyin edilən apellyasiyalar, yoxlanmış/qalmış sayı
      const teacherApellyasiyas = assignments.filter(
        (a) => (a as any).type === 'apellyasiya' && a.hocaId === assignment.hocaId
      );
      const total = teacherApellyasiyas.length;
      const completedCount = teacherApellyasiyas.filter(
        (a) => (a as any).apellyasiya_status === 'completed'
      ).length;
      const pendingCount = total - completedCount;
      const fenns = Array.from(new Set(teacherApellyasiyas.map((a) => a.fenn).filter(Boolean))).join(', ');
      const sinifs = Array.from(new Set(teacherApellyasiyas.map((a) => a.sinif).filter(Boolean))).join(', ');
      const variant = assignment.variant;

      message = buildApellyasiyaWhatsAppMessage(teacher.name, {
        total,
        completedCount,
        pendingCount,
        fenns: fenns || (assignment.fenn || '—'),
        sinifs: sinifs || (assignment.sinif || '—'),
        variant: variant || undefined
      });
    } else {
      // Normal atama mesajı - NEW TEMPLATE with all assignments
      const selectedExam = getSelectedExam();
      const examName = selectedExam?.name || selectedExam?.examName || 'İmtahan';
      const examCollection = selectedExam?.collection || 'scanned2610';

      // Get all assignments for this teacher
      const teacherAssignments = getTeacherAllAssignments(
        assignment.hocaId,
        examCollection
      );

      if (teacherAssignments.length === 0) {
        alert('Bu müəllim üçün heç bir tapşırıq tapılmadı.');
        return;
      }

      // Format assignment list with Roman numerals
      const assignmentList = teacherAssignments
        .map(a => `*${gradeToRoman(a.sinif)} sinif* - ${a.qrupOrVariant} qrup - *${a.fenn}*`)
        .join(', ');

      // Calculate total questions
      const totalQuestions = teacherAssignments.reduce(
        (sum, a) => sum + a.questionCount, 0
      );

      // Calculate completed questions - use original assignments data
      const teacherOriginalAssignments = assignments.filter(a =>
        a.hocaId === assignment.hocaId &&
        a.collectionName === examCollection &&
        (a as any).type !== 'apellyasiya' &&
        a.hocaId !== 'admin'
      );

      let totalCompleted = 0;
      teacherOriginalAssignments.forEach((assignmentData: any) => {
        // Try to get completed count from questionProgress first
        if (assignmentData.questionProgress && Object.keys(assignmentData.questionProgress).length > 0) {
          Object.values(assignmentData.questionProgress).forEach((qProgress: any) => {
            totalCompleted += qProgress.completed || 0;
          });
        } else {
          // Fallback: use completedCount
          totalCompleted += assignmentData.completedCount || 0;
        }
      });

      const remainingQuestions = totalQuestions - totalCompleted;

      // Build professional message template with bold formatting
      message = `*Hörmətli ${teacher.name}*,

https://yoxla.istedad21.edu.az saytına keçid edərək, Sizə verilən istifadəçi adı və şifrə ilə daxil olub adınıza təyin edilən *${examName}* qiymətləndirmələrini (${assignmentList}, ümumi *${totalQuestions} sual*) həyata keçirməyinizi xahiş edirik.

${totalCompleted > 0 || remainingQuestions > 0
  ? remainingQuestions === 0
    ? `💡 *Təbriklər!* Bütün suallar tamamlanıb (${totalCompleted}/${totalQuestions}).`
    : totalCompleted === 0
      ? `💡 *Qeyd:* Hələ heç bir sual tamamlanmayıb. Ümumi ${totalQuestions} sual qalıb.`
      : `💡 *Qeyd:* Hal-hazırda *${totalCompleted}* sual tamamlanıb, *${remainingQuestions}* sual qalıb.`
  : ''}

Ətraflı təlimat İstedad Kurslarının WhatsApp qrupunda verilib. Hər hansı bir problemlə qarşılaşdıqda bizimlə əlaqə saxlayın.

(Və ya müəllim paneli ilə əlaqələndiridiyiniz Google hesabınızla giriş edə bilərsiniz.)

*Hörmətlə,*
*İstedad Kursları Qiymətləndirmə və Layihələrlə İş Mərkəzi*`;
    }

    if (!teacher.phoneNumber) return;
    openWhatsAppWithMessage(teacher.phoneNumber, message);
  };

  const filterAssignments = () => {
    let filtered = [...assignments];

    console.log('🔍 filterAssignments called. activeTab:', activeTab, 'Total assignments:', filtered.length);
    console.log('🔍 Apellyasiya count in assignments:', filtered.filter(a => (a as any).type === 'apellyasiya').length);

    // Apellyasiya tab'ında sadece apellyasiyalar göster
    if (activeTab === 'apellyasiyas') {
      filtered = filtered.filter(a => (a as any).type === 'apellyasiya');
      console.log('🔍 After filtering for apellyasiyas tab:', filtered.length);
    } else {
      // Diğer tab'larda apellyasiyaları gizle
      filtered = filtered.filter(a => (a as any).type !== 'apellyasiya');
      console.log('🔍 After filtering out apellyasiyas for', activeTab, 'tab:', filtered.length);
    }

    // For assignments and overview tabs, exclude admin assignments (BOTH Buraxılış and Blok)
    if (activeTab === 'assignments' || activeTab === 'overview') {
      console.log('🔍 Filtering admin assignments for', activeTab, 'tab. Before:', filtered.length);
      filtered = filtered.filter(a => a.hocaId !== 'admin');
      console.log('🔍 After filtering admin:', filtered.length);
    }

    if (statusFilter) {
      filtered = filtered.filter(a => a.status === statusFilter);
    }

    if (fennFilter) {
      const toBaseFenn = (f: string) => {
        const t = (f || '').trim();
        if (t === 'İngilis dili' || t === 'İngilis') return 'İngilis';
        if (t === 'Rus dili' || t === 'Rus') return 'Rus';
        if (t === 'Alman dili' || t === 'Alman') return 'Alman';
        if (t === 'Fransız dili' || t === 'Fransız') return 'Fransız';
        return t;
      };
      filtered = filtered.filter(a => toBaseFenn(a.fenn) === toBaseFenn(fennFilter));
    }

    if (sinifFilter) {
      filtered = filtered.filter(a => a.sinif === sinifFilter);
    }

    if (isBuraxilis && variantFilter) {
      filtered = filtered.filter(a => (a.variant || a.qrup) === variantFilter);
    } else if (!isBuraxilis && qrupFilter) {
      filtered = filtered.filter(a => qrupEquals(a.qrup, qrupFilter));
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(a => {
        const teacher = (a.hocaName || '').toLowerCase();
        const teacherId = (a.hocaId || '').toLowerCase();
        const fenn = (a.fenn || '').toLowerCase();
        const isApellyasiya = (a as any).type === 'apellyasiya';
        
        // Apellyasiya için öğrenci adı ve ID'si de aramaya dahil et
        if (isApellyasiya) {
          const studentId = String((a as any).studentId || '').toLowerCase();
          const studentName = ((a as any).studentName || '').toLowerCase();
          const questionNum = ((a as any).questionNumber || '').toLowerCase();
          return teacher.includes(q) || teacherId.includes(q) || fenn.includes(q) || 
                 studentId.includes(q) || studentName.includes(q) || questionNum.includes(q);
        }
        
        return teacher.includes(q) || teacherId.includes(q) || fenn.includes(q);
      });
    }

    // Sorting
    filtered.sort((a, b) => {
      if (sortField === 'fenn') {
        const fa = (a.fenn || '').toLowerCase();
        const fb = (b.fenn || '').toLowerCase();
        if (fa < fb) return sortDirection === 'asc' ? -1 : 1;
        if (fa > fb) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      } else {
        // createdAt
        const ta =
          (a.createdAt && (a.createdAt as any).toMillis && (a.createdAt as any).toMillis()) ||
          (a.createdAt instanceof Date ? a.createdAt.getTime() : 0);
        const tb =
          (b.createdAt && (b.createdAt as any).toMillis && (b.createdAt as any).toMillis()) ||
          (b.createdAt instanceof Date ? b.createdAt.getTime() : 0);
        if (ta < tb) return sortDirection === 'asc' ? -1 : 1;
        if (ta > tb) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      }
    });

    if (progressFilter) {
      filtered = filtered.filter(a => {
        const percentage = calculateProgressPercentage(a);

        if (progressFilter === 'completed') {
          return percentage === 100;
        } else if (progressFilter === 'not-started') {
          return percentage === 0;
        } else if (progressFilter === 'in-progress') {
          return percentage > 0 && percentage < 100;
        }
        return true;
      });
    }

    // Apellyasiya bal değişikliği filtresi
    if (activeTab === 'apellyasiyas' && scoreChangeFilter) {
      filtered = filtered.filter(a => {
        const isApellyasiya = (a as any).type === 'apellyasiya';
        if (!isApellyasiya) return true;
        
        const apellyasiyaResult = (a as any).apellyasiyaResult;
        if (!apellyasiyaResult || apellyasiyaResult.apellyasiya_status !== true) {
          // Tamamlanmamış apellyasiyalar için filtre uygulanmaz
          return scoreChangeFilter === '' || false;
        }
        
        const previousScore = apellyasiyaResult.previous_score;
        const newScore = apellyasiyaResult.new_score;
        
        // Eğer skorlar yoksa, filtreleme yapma
        if (previousScore === undefined || newScore === undefined) {
          return false;
        }
        
        if (scoreChangeFilter === 'increased') {
          return newScore > previousScore;
        } else if (scoreChangeFilter === 'decreased') {
          return newScore < previousScore;
        } else if (scoreChangeFilter === 'unchanged') {
          return newScore === previousScore;
        }
        
        return true;
      });
    }

    setFilteredAssignments(filtered);
  };

  const handleToggleStatus = async (assignmentId: string, currentStatus: string) => {
    try {
      const newStatus = currentStatus === 'active' ? 'passive' : 'active';
      await updateAssignmentStatus(assignmentId, newStatus);
      alert(`Atama ${newStatus === 'passive' ? 'pasif' : 'aktif'} edildi`);
      await loadData();
    } catch (error) {
      console.error('Atama statusu değiştirilirken hata:', error);
      alert('Bir hata oluştu');
    }
  };

  // Apellyasiya nəticələrini Excel olaraq export et
  const exportApellyasiyasToExcel = async () => {
    try {
      const selectedExam = getSelectedExam();
      const collectionFilter = selectedExam?.collection || 'scanned2610';
      
      // Tüm apellyasiya atamalarını al
      const allAssignments = await getAllAssignments(collectionFilter);
      const apellyasiyaAssignments = allAssignments.filter(a => (a as any).type === 'apellyasiya');
      
      if (apellyasiyaAssignments.length === 0) {
        alert('Apellyasiya tapılmadı');
        return;
      }
      
      // Apellyasiya nəticələrini topla
      const { getDoc, doc } = await import('firebase/firestore');
      const { db } = await import('../../firebase');
      
      const excelData: (string | number)[][] = [
        ['Apellyasiya Nəticələri', ''],
        ['Export Tarixi', new Date().toLocaleString('az-AZ')],
        ['', ''],
        ['Şagird ID', 'Şagird Adı', 'Sual', 'Fənn', 'Sinif', 'Variant', 'Əvvəlki Bal', 'Yeni Bal', 'Dəyişiklik', 'Müəllim Qeydi', 'Apellyasiyanı Yoxlayan', 'Təsdiqlənmə Tarixi', 'Status']
      ];
      
      for (const assignment of apellyasiyaAssignments) {
        const apData = assignment as any;
        
        try {
          // Öğrenci document'ini yüklə
          const studentDoc = await getDoc(doc(db, apData.collectionName, apData.studentDocumentId));
          if (!studentDoc.exists()) continue;
          
          const studentData = studentDoc.data();
          const qField = `${apData.questionNumber}_c`;
          const gradingData: any = studentData?.[qField];
          
          // Apellyasiya tamamlandıysa
          const isApellyasiyaCompleted = gradingData && 
                                        typeof gradingData === 'object' && 
                                        (gradingData.apellyasiya_status === true || 
                                         gradingData.apellyasiya_result === true);
          
          if (isApellyasiyaCompleted && gradingData.previous_score !== undefined && gradingData.new_score !== undefined) {
            const diff = Number(gradingData.new_score) - Number(gradingData.previous_score);
            const diffText = diff > 0 ? `+${diff}` : diff < 0 ? `${diff}` : 'Dəyişməyib';
            
            const onaylanmaTarihi = gradingData.apellyasiya_onaylanma_tarihi 
              ? new Date(
                  gradingData.apellyasiya_onaylanma_tarihi.toDate ? 
                  gradingData.apellyasiya_onaylanma_tarihi.toDate() : 
                  gradingData.apellyasiya_onaylanma_tarihi
                ).toLocaleString('az-AZ')
              : '';
            
            excelData.push([
              apData.studentId || '',
              `${studentData.firstName || ''} ${studentData.lastName || ''}`.trim() || '',
              apData.questionNumber || '',
              apData.fenn || '',
              String(studentData.grade || '') || '',
              studentData.variant || '',
              gradingData.previous_score || '0',
              gradingData.new_score || '0',
              diffText,
              gradingData.apellyasiya_note || '-',
              gradingData.apellyasiya_hoca || '-',
              onaylanmaTarihi,
              'Təsdiqləndi'
            ]);
          } else {
            // Tamamlanmamış apellyasiyalar
            excelData.push([
              apData.studentId || '',
              `${studentData.firstName || ''} ${studentData.lastName || ''}`.trim() || '',
              apData.questionNumber || '',
              apData.fenn || '',
              String(studentData.grade || '') || '',
              studentData.variant || '',
              gradingData?.score || '-',
              '-',
              '-',
              apData.apellyasiya_səbəbi || '-',
              apData.hocaName || '-',
              '-',
              'Gözləyir'
            ]);
          }
        } catch (error) {
          console.error(`Apellyasiya export xəta (${apData.studentDocumentId}):`, error);
        }
      }
      
      // Excel faylını yüklə
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet(excelData);
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Apellyasiya Nəticələri');
      
      const fileName = `Apellyasiya_Neticeleri_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      
      alert('Apellyasiya nəticələri Excel formatında uğurla yükləndi!');
    } catch (error) {
      console.error('Apellyasiya export edilərkən xəta:', error);
      alert('Export edilərkən xəta baş verdi. Zəhmət olmasa yenidən cəhd edin.');
    }
  };

  // Pool assignment creation handler
  const handleCreatePool = async () => {
    const { sinif, fenn, variant, selectedTeacherIds } = poolFormData;

    // Validation
    if (!sinif || !fenn || !variant) {
      setPoolError('Zəhmət olmasa sinif, fənn və variant seçin');
      return;
    }

    // Get auto-selected question numbers
    const qNums = getPoolQuestionsForSelection(sinif, fenn);
    if (qNums.length === 0) {
      setPoolError('Bu sinif və fənn üçün sual tapılmadı');
      return;
    }

    if (selectedTeacherIds.length === 0) {
      setPoolError('Zəhmət olmasa ən azı bir müəllim seçin');
      return;
    }

    const selectedExam = getSelectedExam();
    if (!selectedExam || selectedExam.type !== 'buraxilis') {
      setPoolError('Hovuz atamaları yalnız Buraxılış imtahanları üçün mümkündür');
      return;
    }

    setPoolCreating(true);
    setPoolError(null);
    setPoolSuccess(null);

    try {
      const result = await createPoolAssignment(
        fenn,
        sinif,
        variant,
        qNums,
        selectedExam.collection,
        'Hamısı', // Building filter - all buildings
        selectedTeacherIds,
        'admin' // createdBy
      );

      if (result.success) {
        setPoolSuccess(result.message);
        // Reset form
        setPoolFormData({
          sinif: '',
          fenn: '',
          variant: '',
          selectedTeacherIds: []
        });
        // Refresh assignments list
        const allAssignments = await getAllAssignments(selectedExam.collection);
        setAssignments(allAssignments);
        setFilteredAssignments(allAssignments);
        // Close modal after a delay
        setTimeout(() => {
          setShowPoolModal(false);
          setPoolSuccess(null);
        }, 2000);
      } else {
        setPoolError(result.message);
      }
    } catch (error) {
      console.error('Pool yaradılarkən xəta:', error);
      setPoolError('Hovuz yaradılarkən xəta baş verdi');
    } finally {
      setPoolCreating(false);
    }
  };

  // Toggle teacher selection for pool
  const handleToggleTeacherSelection = (teacherId: string) => {
    setPoolFormData(prev => ({
      ...prev,
      selectedTeacherIds: prev.selectedTeacherIds.includes(teacherId)
        ? prev.selectedTeacherIds.filter(id => id !== teacherId)
        : [...prev.selectedTeacherIds, teacherId]
    }));
  };

  // Handle freeze/unfreeze teacher in pool
  const handleToggleTeacherInPoolStatus = async (assignmentId: string, teacherId: string, currentIsActive: boolean) => {
    try {
      const success = await toggleTeacherInPool(assignmentId, teacherId, !currentIsActive);
      if (success) {
        // Refresh assignments
        const selectedExam = getSelectedExam();
        if (selectedExam) {
          const allAssignments = await getAllAssignments(selectedExam.collection);
          setAssignments(allAssignments);
          setFilteredAssignments(allAssignments);
        }
      }
    } catch (error) {
      console.error('Müəllim statusu dəyişdirilərkən xəta:', error);
    }
  };

  // Render pool teacher list for an assignment
  const renderPoolTeacherList = (assignment: YoklaAssignment) => {
    if (assignment.assignmentType !== 'pool' || !assignment.poolConfig) return null;

    return (
      <PoolTeacherList>
        <div style={{ fontSize: '14px', fontWeight: '700', color: '#7c3aed', marginBottom: '12px' }}>
          🏊 Hovuz Müəllimləri ({assignment.poolConfig.teachers.filter(t => t.isActive).length} aktiv / {assignment.poolConfig.teachers.length} ümumi)
        </div>
        {assignment.poolConfig.teachers.map(teacher => (
          <PoolTeacherItem key={teacher.id} $isActive={teacher.isActive}>
            <div className="teacher-info">
              <span className="name">{teacher.name}</span>
              <span className="status">{teacher.isActive ? 'Aktiv' : 'Dondurulmuş'}</span>
            </div>
            <FreezeButton
              $isFrozen={!teacher.isActive}
              onClick={() => handleToggleTeacherInPoolStatus(assignment.id!, teacher.id, teacher.isActive)}
            >
              {teacher.isActive ? '🔒 Dondur' : '🔓 Aktivləşdir'}
            </FreezeButton>
          </PoolTeacherItem>
        ))}
      </PoolTeacherList>
    );
  };

  const exportToExcel = async () => {
    try {
      const selectedExam = getSelectedExam();
      const collectionFilter = selectedExam?.collection || 'scanned2610';
      const examType = selectedExam?.type === 'buraxilis' ? 'buraxilis' : 'blok';
      
      // Tüm verileri al (stats dahil - frontend'deki ile aynı olmalı)
      const [allAssignments, allTeachers, statsData] = await Promise.all([
        getAllAssignments(collectionFilter),
        getAllTeachers(),
        getYoxlamaStats(collectionFilter, examType)
      ]);

      const workbook = XLSX.utils.book_new();

      if (examType === 'buraxilis') {
        // BURAXILIŞ MODU - 2 tab
        
        // 1. Müəllim Sual Statistikası (Admin hariç + Admin boş sual)
        const teacherQuestionStatsData: (string | number)[][] = [
          ['Müəllim Adı', 'Müəllim ID', 'Fənn', 'Tayin Edilən Sual Sayısı', 'Toplam Yoxlanmış Sual Sayısı', 'Tamamlanma Faizi']
        ];

        // Admin atamalarını ayır (bunları hocaya atama gibi saymıyoruz)
        const adminAssignments = allAssignments.filter(a => a.hocaId === 'admin');
        const nonAdminAssignments = allAssignments.filter(a => a.hocaId !== 'admin' && (a as any).type !== 'apellyasiya');
        
        // Sadece admin olmayan hocalar
        const nonAdminTeachers = allTeachers.filter(t => t.id !== 'admin');
        
        let totalAssignedQuestions = 0;
        let totalCheckedQuestions = 0;
        let adminEmptyPaperCount = 0;

        nonAdminTeachers.forEach(teacher => {
          const teacherAssignments = nonAdminAssignments.filter(
            a => a.hocaId === teacher.id
          );
          
          let assignedQuestionsCount = 0;
          let checkedQuestionsCount = 0;

          teacherAssignments.forEach(assignment => {
            // Tayin edilən sual sayısı: questionProgress içindeki total değerlerini kullan
            // Bu, admin boş kağızları hariç tutan doğru total değeridir
            if (assignment.questionNumbers && assignment.questionNumbers.length > 0) {
              // Her sual üçün questionProgress'ten total ve completed değerlerini al
              assignment.questionNumbers.forEach((qNum: string) => {
                const qProgress = assignment.questionProgress?.[qNum];
                if (qProgress) {
                  // Total: Bu soru için tayin edilən öğrenci sayısı (admin boş kağızlar hariç)
                  const qTotal = qProgress.total || 0;
                  // Completed: Bu hocanın yoxladığı sayı
                  const qCompleted = qProgress.completed || 0;
                  
                  assignedQuestionsCount += qTotal;
                  checkedQuestionsCount += qCompleted;
                }
              });
            }
          });

          totalAssignedQuestions += assignedQuestionsCount;
          totalCheckedQuestions += checkedQuestionsCount;

          const percentage = assignedQuestionsCount > 0 
            ? Math.round((checkedQuestionsCount / assignedQuestionsCount) * 100) 
            : 0;

          if (assignedQuestionsCount > 0 || checkedQuestionsCount > 0) {
            teacherQuestionStatsData.push([
              teacher.name,
              teacher.id,
              teacher.fenn || '-',
              assignedQuestionsCount,
              checkedQuestionsCount,
              `%${percentage}`
            ]);
          }
        });

        // Admin boş sual işaretlemelerini hesapla (ayrı satır olarak)
        adminAssignments.forEach(assignment => {
          if (assignment.questionNumbers && assignment.questionNumbers.length > 0) {
            assignment.questionNumbers.forEach((qNum: string) => {
              const qProgress = assignment.questionProgress?.[qNum];
              if (qProgress) {
                adminEmptyPaperCount += qProgress.completed || 0;
              }
            });
          } else {
            // Eğer questionNumbers yoksa, completedCount'u kullan
            adminEmptyPaperCount += assignment.completedCount || 0;
          }
        });

        // Ümumi total sətri - Frontend'deki stats ile aynı olmalı
        // Frontend'de: "Toplam Yoklama (Fənn Bazlı)" = stats.totalFennBased
        teacherQuestionStatsData.push(['', '', '', '', '', '']);
        
        // Frontend'deki stats verilerini kullan (aynı mantık)
        const fennBasedTotal = statsData?.totalFennBased?.total || totalCheckedQuestions;
        const fennBasedCompleted = statsData?.totalFennBased?.completed || totalCheckedQuestions;
        const fennBasedPercentage = fennBasedTotal > 0 
          ? Math.round((fennBasedCompleted / fennBasedTotal) * 100) 
          : 0;
        
        teacherQuestionStatsData.push([
          'ÜMUMİ TOTAL (Müəllimlər)', 
          '', 
          '', 
          fennBasedTotal, 
          fennBasedCompleted,
          `%${fennBasedPercentage}`
        ]);

        // Admin boş sual işaretlemeleri (AYRI SATIR - toplam atamaya dahil değil)
        // Frontend'deki stats'tan al
        const adminEmptyPapers = statsData?.adminEmptyPapers || adminEmptyPaperCount;
        if (adminEmptyPapers > 0) {
          teacherQuestionStatsData.push(['', '', '', '', '', '']);
          teacherQuestionStatsData.push([
            'ADMIN BOŞ SUAL İŞARƏTLƏMƏLƏRİ',
            'admin',
            'Admin',
            adminEmptyPapers,
            adminEmptyPapers,
            '%100'
          ]);
        }

        const teacherQuestionStatsSheet = XLSX.utils.aoa_to_sheet(teacherQuestionStatsData);
        XLSX.utils.book_append_sheet(workbook, teacherQuestionStatsSheet, 'Müəllim Sual Statistikası');

        // 2. Apellyasiya Statistikası
        const apellyasiyaStatsData: (string | number)[][] = [
          ['Müəllim Adı', 'Müəllim ID', 'Toplam Apellyasiya Sayısı', 'Tamamlanmış Apellyasiya Sayısı', 'Tamamlanma Faizi']
        ];

        // Apellyasiya atamalarını yüklə
        let apellyasiyaAssignments: any[] = [];
        try {
          const { getDoc, doc } = await import('firebase/firestore');
          const apellyasiyaQuery = query(
            collection(db, 'yoxla-s'),
            where('type', '==', 'apellyasiya'),
            where('collectionName', '==', collectionFilter)
          );
          const apellyasiyaSnapshot = await getDocs(apellyasiyaQuery);

          // Collection filter kontrolü
          const apellyasiyaPromises = apellyasiyaSnapshot.docs.map(async (apDoc) => {
            const apData = apDoc.data();
            // Collection filter kontrolü
            if (collectionFilter && apData.collectionName !== collectionFilter) {
              return null;
            }
            
            try {
              const studentDoc = await getDoc(doc(db, apData.collectionName, apData.studentDocumentId));
              if (studentDoc.exists()) {
                const studentData = studentDoc.data();
                const qField = `${apData.questionNumber}_c`;
                const gradingData: any = studentData?.[qField];
                
                const isApellyasiyaCompleted = gradingData && 
                                              typeof gradingData === 'object' && 
                                              (gradingData.apellyasiya_result === true || 
                                               gradingData.apellyasiya_status === true);
                
                return {
                  id: apDoc.id,
                  ...apData,
                  type: 'apellyasiya',
                  apellyasiya_status: isApellyasiyaCompleted ? 'completed' : 'pending',
                  status: isApellyasiyaCompleted ? 'completed' : 'active',
                  hocaId: apData.hocaId,
                  hocaName: apData.hocaName
                };
              }
            } catch (error) {
              console.error(`❌ Student document yüklənərkən xəta:`, error);
            }
            
            return {
              id: apDoc.id,
              ...apData,
              type: 'apellyasiya',
              apellyasiya_status: apData.apellyasiya_status || 'pending',
              status: apData.apellyasiya_status === 'completed' ? 'completed' : 'active',
              hocaId: apData.hocaId,
              hocaName: apData.hocaName
            };
          });
          
          const results = await Promise.all(apellyasiyaPromises);
          apellyasiyaAssignments = results.filter(a => a !== null);
        } catch (error) {
          console.error('Apellyasiya atamaları yüklənərkən xəta:', error);
        }
        
        // Her hocaya göre grupla
        const apellyasiyaByTeacher: { [teacherId: string]: { total: number; completed: number } } = {};
        
        apellyasiyaAssignments.forEach(assignment => {
          const teacherId = assignment.hocaId;
          if (!teacherId) return;
          
          if (!apellyasiyaByTeacher[teacherId]) {
            apellyasiyaByTeacher[teacherId] = { total: 0, completed: 0 };
          }
          apellyasiyaByTeacher[teacherId].total += 1;
          
          const isCompleted = assignment.apellyasiya_status === 'completed' || 
                             assignment.status === 'completed';
          if (isCompleted) {
            apellyasiyaByTeacher[teacherId].completed += 1;
          }
        });

        // Her hocanın statistikasını ekle
        Object.keys(apellyasiyaByTeacher).forEach(teacherId => {
          const teacher = allTeachers.find(t => t.id === teacherId);
          const stats = apellyasiyaByTeacher[teacherId];
          const percentage = stats.total > 0 
            ? Math.round((stats.completed / stats.total) * 100) 
            : 0;

          apellyasiyaStatsData.push([
            teacher ? teacher.name : (apellyasiyaAssignments.find(a => a.hocaId === teacherId)?.hocaName || teacherId),
            teacherId,
            stats.total,
            stats.completed,
            `%${percentage}`
          ]);
        });

        // Ümumi total
        const totalApellyasiya = Object.values(apellyasiyaByTeacher).reduce((sum, s) => sum + s.total, 0);
        const totalCompletedApellyasiya = Object.values(apellyasiyaByTeacher).reduce((sum, s) => sum + s.completed, 0);
        const totalApellyasiyaPercentage = totalApellyasiya > 0 
          ? Math.round((totalCompletedApellyasiya / totalApellyasiya) * 100) 
          : 0;

        if (totalApellyasiya > 0) {
          apellyasiyaStatsData.push(['', '', '', '', '']);
          apellyasiyaStatsData.push([
            'ÜMUMİ TOTAL',
            '',
            totalApellyasiya,
            totalCompletedApellyasiya,
            `%${totalApellyasiyaPercentage}`
          ]);
        }

        const apellyasiyaStatsSheet = XLSX.utils.aoa_to_sheet(apellyasiyaStatsData);
        XLSX.utils.book_append_sheet(workbook, apellyasiyaStatsSheet, 'Apellyasiya Statistikası');

        // 3. Detaylı Atamalar Tab'ı (Frontend card'ları gibi)
        const detailedAssignmentsData: (string | number)[][] = [
          ['Fənn', 'Müəllim Adı', 'Müəllim ID', 'Sinif', 'Variant', 'Status', 'Ümumi Öğrenci Sayısı', 'Ümumi Yoxlanmış', 'Ümumi Tamamlanma Faizi', 'Sual Nömrəsi', 'Sual Üçün Total', 'Sual Üçün Yoxlanmış', 'Sual Üçün Tamamlanma Faizi']
        ];

        // Admin hariç tüm atamaları (apellyasiya hariç)
        const detailedNonAdminAssignments = nonAdminAssignments.filter(a => (a as any).type !== 'apellyasiya');
        
        detailedNonAdminAssignments.forEach(assignment => {
          const totalCount = assignment.totalCount || 0;
          const completedCount = assignment.completedCount || 0;
          const overallPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
          
          // Eğer questionNumbers varsa, her soru için ayrı satır
          if (assignment.questionNumbers && assignment.questionNumbers.length > 0) {
            assignment.questionNumbers.forEach((qNum: string, index: number) => {
              const qProgress = assignment.questionProgress?.[qNum];
              const questionTotal = qProgress?.total ?? 0;
              const questionCompleted = qProgress?.completed ?? 0;
              const questionPercentage = questionTotal > 0 ? Math.round((questionCompleted / questionTotal) * 100) : 0;
              
              detailedAssignmentsData.push([
                index === 0 ? displayFenn(assignment.fenn) : '', // İlk satırda fenn göster
                index === 0 ? assignment.hocaName || '' : '', // İlk satırda müəllim adı
                index === 0 ? assignment.hocaId : '', // İlk satırda müəllim ID
                index === 0 ? assignment.sinif : '', // İlk satırda sinif
                index === 0 ? (assignment.variant || assignment.qrup || '') : '', // İlk satırda variant
                index === 0 ? (assignment.status === 'active' ? 'Aktiv' : assignment.status === 'completed' ? 'Tamamlandı' : 'Pasif') : '', // İlk satırda status
                index === 0 ? totalCount : '', // İlk satırda ümumi öğrenci sayısı
                index === 0 ? completedCount : '', // İlk satırda ümumi yoxlanmış
                index === 0 ? `%${overallPercentage}` : '', // İlk satırda ümumi tamamlanma faizi
                qNum, // Sual nömrəsi
                questionTotal, // Sual üçün total
                questionCompleted, // Sual üçün yoxlanmış
                `%${questionPercentage}` // Sual üçün tamamlanma faizi
              ]);
            });
          } else {
            // Eğer questionNumbers yoksa, tek satır
            detailedAssignmentsData.push([
              displayFenn(assignment.fenn),
              assignment.hocaName || '',
              assignment.hocaId,
              assignment.sinif,
              assignment.variant || assignment.qrup || '',
              assignment.status === 'active' ? 'Aktiv' : assignment.status === 'completed' ? 'Tamamlandı' : 'Pasif',
              totalCount,
              completedCount,
              `%${overallPercentage}`,
              '-',
              totalCount,
              completedCount,
              `%${overallPercentage}`
            ]);
          }
          
          // Her atama arasında boş satır ekle
          detailedAssignmentsData.push(['', '', '', '', '', '', '', '', '', '', '', '', '']);
        });

        const detailedAssignmentsSheet = XLSX.utils.aoa_to_sheet(detailedAssignmentsData);
        XLSX.utils.book_append_sheet(workbook, detailedAssignmentsSheet, 'Detaylı Atamalar');

        // 4. Müəllim Sual Statistikası (Sinif Bazlı)
        const teacherGradeStatsData: (string | number)[][] = [
          ['Müəllim Adı', 'Müəllim ID', 'Fənn', 'Tayin Edilən Sual Sayısı', 'Toplam Yoxlanmış Sual Sayısı', '8. Sinif Yoxlanmış', '9. Sinif Yoxlanmış', '10. Sinif Yoxlanmış', '11. Sinif Yoxlanmış', 'Tamamlanma Faizi']
        ];

        let totalAssignedByGrade = 0;
        let totalCheckedByGrade = 0;
        let totalGrade8 = 0;
        let totalGrade9 = 0;
        let totalGrade10 = 0;
        let totalGrade11 = 0;

        nonAdminTeachers.forEach(teacher => {
          const teacherAssignments = nonAdminAssignments.filter(
            a => a.hocaId === teacher.id
          );
          
          let assignedQuestionsCount = 0;
          let checkedQuestionsCount = 0;
          let grade8Count = 0;
          let grade9Count = 0;
          let grade10Count = 0;
          let grade11Count = 0;

          teacherAssignments.forEach(assignment => {
            // Sinif değerini al
            const sinif = assignment.sinif ? String(assignment.sinif).replace('. sinif', '').trim() : '';
            const gradeNum = parseInt(sinif) || 0;
            
            if (assignment.questionNumbers && assignment.questionNumbers.length > 0) {
              // Her sual üçün questionProgress'ten total ve completed değerlerini al
              assignment.questionNumbers.forEach((qNum: string) => {
                const qProgress = assignment.questionProgress?.[qNum];
                if (qProgress) {
                  const qTotal = qProgress.total || 0;
                  const qCompleted = qProgress.completed || 0;
                  
                  assignedQuestionsCount += qTotal;
                  checkedQuestionsCount += qCompleted;
                  
                  // Sinif bazında yoxlanmış sayısını ekle
                  if (gradeNum === 8) {
                    grade8Count += qCompleted;
                  } else if (gradeNum === 9) {
                    grade9Count += qCompleted;
                  } else if (gradeNum === 10) {
                    grade10Count += qCompleted;
                  } else if (gradeNum === 11) {
                    grade11Count += qCompleted;
                  }
                }
              });
            }
          });

          totalAssignedByGrade += assignedQuestionsCount;
          totalCheckedByGrade += checkedQuestionsCount;
          totalGrade8 += grade8Count;
          totalGrade9 += grade9Count;
          totalGrade10 += grade10Count;
          totalGrade11 += grade11Count;

          const percentage = assignedQuestionsCount > 0 
            ? Math.round((checkedQuestionsCount / assignedQuestionsCount) * 100) 
            : 0;

          if (assignedQuestionsCount > 0 || checkedQuestionsCount > 0) {
            teacherGradeStatsData.push([
              teacher.name,
              teacher.id,
              teacher.fenn || '-',
              assignedQuestionsCount,
              checkedQuestionsCount,
              grade8Count,
              grade9Count,
              grade10Count,
              grade11Count,
              `%${percentage}`
            ]);
          }
        });

        // Ümumi total sətri
        teacherGradeStatsData.push(['', '', '', '', '', '', '', '', '', '']);
        const totalPercentage = totalAssignedByGrade > 0 
          ? Math.round((totalCheckedByGrade / totalAssignedByGrade) * 100) 
          : 0;
        teacherGradeStatsData.push([
          'ÜMUMİ TOTAL (Müəllimlər)', 
          '', 
          '', 
          totalAssignedByGrade, 
          totalCheckedByGrade,
          totalGrade8,
          totalGrade9,
          totalGrade10,
          totalGrade11,
          `%${totalPercentage}`
        ]);

        const teacherGradeStatsSheet = XLSX.utils.aoa_to_sheet(teacherGradeStatsData);
        XLSX.utils.book_append_sheet(workbook, teacherGradeStatsSheet, 'Müəllim Sual (Sinif Bazlı)');

      } else {
        // BLOK MODU - Basitleştirilmiş 2 tab
        
        // 1. Müəllimlər və Yoxladıqları
        const teacherData: (string | number)[][] = [
          ['Müəllim Adı', 'Müəllim ID', 'Fənn', 'Status', 'Tapşırıq Sayı', 'Yoxlanmış Sayı', 'Ümumi Sayı', 'Tamamlanma Faizi']
        ];

        allTeachers.forEach(teacher => {
          const teacherAssignments = allAssignments.filter(a => a.hocaId === teacher.id);
          const totalAssignments = teacherAssignments.length;
          const totalChecked = teacherAssignments.reduce((sum, a) => sum + (a.completedCount || 0), 0);
          const totalCount = teacherAssignments.reduce((sum, a) => sum + (a.totalCount || 0), 0);
          const percentage = totalCount > 0 ? Math.round((totalChecked / totalCount) * 100) : 0;

          teacherData.push([
            teacher.name,
            teacher.id,
            teacher.fenn || '-',
            teacher.isActive !== false ? 'Aktiv' : 'Pasif',
            totalAssignments,
            totalChecked,
            totalCount,
            `%${percentage}`
          ]);
        });

        const teacherSheet = XLSX.utils.aoa_to_sheet(teacherData);
        XLSX.utils.book_append_sheet(workbook, teacherSheet, 'Müəllimlər');

        // 2. Bütün Tapşırıqlar
        const assignmentsData: (string | number)[][] = [
          ['Müəllim Adı', 'Müəllim ID', 'Fənn', 'Sinif', 'Qrup', 'Status', 'Yoxlanmış', 'Ümumi', 'Tamamlanma Faizi']
        ];

        allAssignments.forEach(assignment => {
          const totalCount = assignment.totalCount || 0;
          const completedCount = assignment.completedCount || 0;
          const percentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

          assignmentsData.push([
            assignment.hocaName || '',
            assignment.hocaId,
            assignment.fenn,
            assignment.sinif,
            assignment.qrup || '-',
            assignment.status === 'active' ? 'Aktiv' : assignment.status === 'completed' ? 'Tamamlandı' : 'Pasif',
            completedCount,
            totalCount,
            `%${percentage}`
          ]);
        });

        const assignmentsSheet = XLSX.utils.aoa_to_sheet(assignmentsData);
        XLSX.utils.book_append_sheet(workbook, assignmentsSheet, 'Tapşırıqlar');
      }

      // Excel faylını yüklə
      const fileName = `Tapshiriqlar_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      
      alert('Excel faylı uğurla yükləndi!');
    } catch (error) {
      console.error('Export edilərkən xəta:', error);
      alert('Export edilərkən xəta baş verdi. Zəhmət olmasa yenidən cəhd edin.');
    }
  };

  // Export Blok statistics to Excel
  const exportBlokStatisticsToExcel = async () => {
    try {
      const selectedExam = getSelectedExam();

      // Apply filters to get visible groups
      const filteredGroups = Array.from(blokStatistics.entries())
        .filter(([groupKey, stats]) => {
          if (sinifFilter && stats.grade !== sinifFilter) return false;
          if (qrupFilter && stats.qrup !== qrupFilter) return false;
          if (altqrupFilter && stats.subGroup !== altqrupFilter) return false;
          return true;
        });

      if (filteredGroups.length === 0) {
        alert('Export üçün məlumat yoxdur.');
        return;
      }

      const workbook = XLSX.utils.book_new();

      // Sheet 1: Summary statistics
      const summaryData = [
        ['Blok Statistikası - Ümumi Məlumat'],
        [''],
        ['Imtahan Adı', selectedExam?.name || ''],
        ['Tarix', new Date().toLocaleString('az-AZ')],
        [''],
        ['Toplam Şagird', buraxilisStudents.length],
        ['Toplam Qrup', filteredGroups.length],
        [''],
        ...filteredGroups.map(([_, stats]) => [
          stats.groupKey,
          stats.studentCount,
          stats.overall.totalQuestions,
          stats.overall.totalTeacherChecked,
          stats.overall.totalAdminVerified,
          stats.overall.totalEmptyDetected
        ])
      ];

      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Ümumi');

      // Sheet 2: Per-fənn statistics for each group
      const fennData = [['Qrup', 'Fənn', 'Toplam Sual', 'Müəllim Yoxlanıb', 'Admin Təsdiqlədi', 'Boş Qeyd Olunub']];
      filteredGroups.forEach(([groupKey, stats]) => {
        Object.entries(stats.fennler).forEach(([fennKey, fennStats]) => {
          fennData.push([
            groupKey,
            fennKey,
            String(fennStats.totalQuestions),
            String(fennStats.teacherChecked),
            String(fennStats.adminVerified),
            String(fennStats.emptyDetected)
          ]);
        });
      });

      const fennSheet = XLSX.utils.aoa_to_sheet(fennData);
      XLSX.utils.book_append_sheet(workbook, fennSheet, 'Fənn Statistikası');

      // Sheet 3: Per-question breakdown
      const questionData = [['Qrup', 'Fənn', 'Sual', 'Toplam', 'Yoxlanıb', 'Faiz']];
      filteredGroups.forEach(([groupKey, stats]) => {
        Object.entries(stats.fennler).forEach(([fennKey, fennStats]) => {
          Object.entries(fennStats.questions).forEach(([qNum, qStats]) => {
            const percentage = qStats.total > 0 ? ((qStats.graded / qStats.total) * 100).toFixed(1) : '0.0';
            questionData.push([
              groupKey,
              fennKey,
              qNum,
              String(qStats.total),
              String(qStats.graded),
              `${percentage}%`
            ]);
          });
        });
      });

      const questionSheet = XLSX.utils.aoa_to_sheet(questionData);
      XLSX.utils.book_append_sheet(workbook, questionSheet, 'Sual Bölgüsü');

      // Sheet 4: Student list
      const studentData = [['Şagird Adı', 'Şagird ID', 'Sinif', 'Qrup', 'Altqrup', 'Yoxlanıb', 'Toplam', 'Faiz']];
      filteredGroups.forEach(([groupKey, _]) => {
        const group = blokGroups.find(g => g.groupKey === groupKey);
        if (group) {
          group.students.forEach(student => {
            const studentQuestions = getActualBlokQuestions(student);
            const studentChecked = studentQuestions.filter(q => q.checked_c).length;
            const studentTotal = studentQuestions.length;
            const studentPercentage = studentTotal > 0 ? ((studentChecked / studentTotal) * 100).toFixed(1) : '0.0';

            studentData.push([
              `${student.name} ${student.surname}`,
              student.studentId || 'N/A',
              student.grade || '',
              student.specialtyGroup || '',
              student.subGroup || '',
              String(studentChecked),
              String(studentTotal),
              `${studentPercentage}%`
            ]);
          });
        }
      });

      const studentSheet = XLSX.utils.aoa_to_sheet(studentData);
      XLSX.utils.book_append_sheet(workbook, studentSheet, 'Şagirdlər');

      // Generate filename
      const filterSuffix = [sinifFilter, qrupFilter, altqrupFilter].filter(Boolean).join('-') || 'all';
      const fileName = `Blok_Statistikası_${filterSuffix}_${new Date().toISOString().split('T')[0]}.xlsx`;

      XLSX.writeFile(workbook, fileName);
      alert('Blok statistikası uğurla ixrac edildi!');
    } catch (error) {
      console.error('Export edilərkən xəta:', error);
      alert('Export edilərkən xəta baş verdi.');
    }
  };

  // Export Blok apellyasiyas to Excel
  const exportBlokApellyasiyasToExcel = async () => {
    try {
      if (allBlokApellyasiyas.length === 0) {
        alert('İxrac üçün apellyasiya yoxdur.');
        return;
      }

      // Apply filters
      const filteredApell = allBlokApellyasiyas.filter(a => {
        if (apellyasiyaStatusFilter !== 'all' && a.status !== apellyasiyaStatusFilter) return false;
        if (apellyasiyaFennFilter && a.fenn !== apellyasiyaFennFilter) return false;
        if (apellyasiyaQrupFilter && !qrupEquals(a.qrup, apellyasiyaQrupFilter)) return false;
        return true;
      });

      const workbook = XLSX.utils.book_new();

      // Main sheet with all apellyasiyas
      const apellData = [
        ['Şagird Adı', 'Şagird ID', 'Sinif', 'Qrup', 'Altqrup', 'Sual', 'Fənn',
         'Əvvəlki Bal', 'Yeni Bal', 'Bal Dəyişikliyi',
         'Orijinal Müəllim', 'Review Müəllim', 'Status', 'Tarix']
      ];

      filteredApell.forEach(a => {
        apellData.push([
          `${a.studentName} ${a.studentSurname}`,
          a.studentId,
          a.grade,
          a.qrup,
          a.subGroup || '',
          a.questionNum,
          a.fenn,
          String(a.originalScore),
          a.newScore !== undefined ? String(a.newScore) : '',
          a.scoreChange !== undefined ? String(a.scoreChange) : '',
          a.originalTeacher || '',
          a.reviewTeacher || '',
          a.status === 'pending' ? 'Gözləyən' : a.status === 'approved' ? 'Təsdiqlənib' : 'Ləğv Edilib',
          new Date(a.date).toLocaleDateString('az-AZ')
        ]);
      });

      const mainSheet = XLSX.utils.aoa_to_sheet(apellData);
      XLSX.utils.book_append_sheet(workbook, mainSheet, 'Apellyasiyalar');

      // Create separate sheets for each status
      const statusGroups = {
        pending: filteredApell.filter(a => a.status === 'pending'),
        approved: filteredApell.filter(a => a.status === 'approved'),
        rejected: filteredApell.filter(a => a.status === 'rejected')
      };

      Object.entries(statusGroups).forEach(([status, apells]) => {
        if (apells.length > 0) {
          const statusData = [
            ['Şagird Adı', 'Şagird ID', 'Sinif', 'Qrup', 'Altqrup', 'Sual', 'Fənn',
             'Əvvəlki Bal', 'Yeni Bal', 'Bal Dəyişikliyi',
             'Orijinal Müəllim', 'Review Müəllim', 'Tarix']
          ];

          apells.forEach(a => {
            statusData.push([
              `${a.studentName} ${a.studentSurname}`,
              a.studentId,
              a.grade,
              a.qrup,
              a.subGroup || '',
              a.questionNum,
              a.fenn,
              String(a.originalScore),
              a.newScore !== undefined ? String(a.newScore) : '',
              a.scoreChange !== undefined ? String(a.scoreChange) : '',
              a.originalTeacher || '',
              a.reviewTeacher || '',
              new Date(a.date).toLocaleDateString('az-AZ')
            ]);
          });

          const statusSheet = XLSX.utils.aoa_to_sheet(statusData);
          const sheetName = status === 'pending' ? 'Gözləyən' :
                           status === 'approved' ? 'Təsdiqlənib' :
                           'Ləğv Edilib';
          XLSX.utils.book_append_sheet(workbook, statusSheet, sheetName);
        }
      });

      // Summary sheet
      const summaryData = [
        ['Blok Apellyasiya Hesabatı'],
        [''],
        ['Tarix', new Date().toLocaleString('az-AZ')],
        [''],
        ['Toplam Apellyasiya', filteredApell.length],
        ['Gözləyən', statusGroups.pending.length],
        ['Təsdiqlənib', statusGroups.approved.length],
        ['Ləğv Edilib', statusGroups.rejected.length],
        [''],
        ['Fənn üzrə bölgü:']
      ];

      // Add per-fənn breakdown
      const fennBreakdown: { [fenn: string]: number } = {};
      filteredApell.forEach(a => {
        fennBreakdown[a.fenn] = (fennBreakdown[a.fenn] || 0) + 1;
      });

      Object.entries(fennBreakdown)
        .sort(([, a], [, b]) => b - a)
        .forEach(([fenn, count]) => {
          summaryData.push([fenn, count]);
        });

      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, 'Xülasə');

      // Generate filename
      const fileName = `Blok_Apellyasiyalar_${new Date().toISOString().split('T')[0]}.xlsx`;

      XLSX.writeFile(workbook, fileName);
      alert('Blok apellyasiyaları uğurla ixrac edildi!');
    } catch (error) {
      console.error('Export edilərkən xəta:', error);
      alert('Export edilərkən xəta baş verdi.');
    }
  };

  // İstatistikler (stats'dan al)
  // For overview tab, use filtered assignments (exclude apellyasiyas)
  const totalAssignments = activeTab === 'overview'
    ? assignments.filter(a => (a as any).type !== 'apellyasiya').length
    : assignments.length;
  const activeAssignments = activeTab === 'overview'
    ? assignments.filter(a => (a as any).type !== 'apellyasiya' && a.status === 'active').length
    : assignments.filter(a => a.status === 'active').length;
  
  // Admin boş kağıt sayısı - stats'dan direkt al
  const adminEmptyPaperCount = stats?.adminEmptyPapers || 0;
  const adminEmptyPaperAssignments = [];
  
  // Stats'tan değerleri al
  const fennBasedCompleted = stats?.totalFennBased?.completed || 0;
  const fennBasedTotal = stats?.totalFennBased?.total || 0;
  const fennBasedRemaining = fennBasedTotal - fennBasedCompleted;
  
  const systemBasedCompleted = stats?.totalSystemBased?.completed || 0;
  const systemBasedTotal = stats?.totalSystemBased?.total || 0;

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <LoadingSpinner />
          <div>Atamalar yükleniyor...</div>
        </LoadingContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title><Icon name="assignments" size={20} style={{ marginRight: 8 }} /> Atama Yönetimi</Title>
        <ButtonGroup>
          {isBuraxilis && (
            <PoolButton onClick={() => setShowPoolModal(true)}>
              <Icon name="add" size={16} style={{ marginRight: 8 }} /> Hovuz Yarat
            </PoolButton>
          )}
          <ExportButton onClick={exportToExcel}>
            <Icon name="archive" size={16} style={{ marginRight: 8 }} /> Excel Export
          </ExportButton>
          <Button onClick={() => navigate('/admin/dashboard')}>
            ← Geri Dön
          </Button>
        </ButtonGroup>
      </Header>

      {(isBuraxilis || (!isBuraxilis && hasNewBlokFormat)) && (
        <TabContainer>
           <TabButton
             active={activeTab === 'overview'}
             onClick={() => setActiveTab('overview')}
           >
             Ümumi Baxış
           </TabButton>
           <TabButton
             active={activeTab === 'assignments'}
             onClick={() => setActiveTab('assignments')}
           >
             Müəllim Tapşırıqları
           </TabButton>
           <TabButton
             active={activeTab === 'apellyasiyas'}
             onClick={() => setActiveTab('apellyasiyas')}
           >
             Apellyasiyalar ({assignments.filter(a => (a as any).type === 'apellyasiya').length})
           </TabButton>
           <TabButton
             active={activeTab === 'statistics'}
             onClick={() => setActiveTab('statistics')}
           >
             {isBuraxilis ? 'Sinif/Variant/Xdil' : 'Statistika'}
           </TabButton>
        </TabContainer>
      )}

      {/* Show format indicator for Blok exams */}
      {!isBuraxilis && (
        <>
          <div style={{
            padding: '12px 16px',
            background: hasNewBlokFormat
              ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
              : 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
            color: 'white',
            borderRadius: '12px',
            marginBottom: '20px',
            fontSize: '14px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}>
          <span>
            {hasNewBlokFormat ? 'Normal' : 'Köhnə (Legacy)'}
          </span>
          {!hasNewBlokFormat && (
            <span style={{ fontSize: '12px', opacity: 0.9, marginLeft: '8px' }}>
              - Yeni format xüsusiyyətləri mövcud deyil
            </span>
          )}
        </div>
        </>
      )}

      {activeTab === 'overview' && (
        <>
          {(() => {
            const apellInFiltered = filteredAssignments.filter(a => (a as any).type === 'apellyasiya');
            if (apellInFiltered.length > 0) {
              console.warn('⚠️ DEFENSIVE FILTER: Removed', apellInFiltered.length, 'apellyasiyas from overview tab render');
              console.log('🔍 Apellyasiyas that were filtered:', apellInFiltered);
            }
            return null;
          })()}
          <StatsGrid>
        <StatCard color="linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)">
          <StatLabel>Toplam Atama</StatLabel>
          <StatValue>{totalAssignments}</StatValue>
          <StatDescription>{activeAssignments} aktif</StatDescription>
        </StatCard>

        <StatCard color="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)">
          <StatLabel>Toplam Yoklama (Fənn Bazlı)</StatLabel>
          <StatValue>{fennBasedCompleted}/{fennBasedTotal}</StatValue>
          <StatDescription>%{stats?.totalFennBased?.percentage || 0} tamamlandı • Her fənn ayrı sayılır</StatDescription>
        </StatCard>

        <StatCard color="linear-gradient(135deg, #ef4444 0%, #dc2626 100%)">
          <StatLabel>Kalan Yoklamalar</StatLabel>
          <StatValue>{fennBasedRemaining}</StatValue>
          <StatDescription>{fennBasedTotal > 0 ? Math.round((fennBasedRemaining / fennBasedTotal) * 100) : 0}% qalıb</StatDescription>
        </StatCard>

        <StatCard color="linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)">
          <StatLabel>Admin Boş Kağız Qeydi</StatLabel>
          <StatValue>{adminEmptyPaperCount}</StatValue>
          <StatDescription>Boş sual sayısı</StatDescription>
        </StatCard>

        <StatCard color="linear-gradient(135deg, #10b981 0%, #059669 100%)">
          <StatLabel>Cəmi Yoxlama (Admin + Teacher)</StatLabel>
          <StatValue>{systemBasedCompleted}/{systemBasedTotal}</StatValue>
          <StatDescription>
            %{stats?.totalSystemBased?.percentage || 0} tamamlandı
            <span style={{ fontSize: '11px', opacity: 0.8, display: 'block', marginTop: '4px' }}>
              Admin: {stats?.adminEmptyPapers || 0} + Teacher: {systemBasedCompleted - (stats?.adminEmptyPapers || 0)}
            </span>
          </StatDescription>
        </StatCard>
      </StatsGrid>

      {/* Blok Student Groups Summary - REMOVED from Overview, only in Statistics tab */}

      <FilterSection>
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '12px' }}>
            🔍 Filtrlər
          </div>
        </div>
        <FilterGrid>
          <Select value={fennFilter} onChange={(e) => setFennFilter(e.target.value)}>
            <option value="">Bütün fənnlər</option>
            {(
              isBuraxilis
                ? (() => {
                    const blankCtx = blankCtxFromExam(getSelectedExam());
                    const fromConfig = [8, 9, 10, 11].flatMap((g) =>
                      getBuraxilisSubjectsForGrade(g, blankCtx).map((c) => c.subject)
                    );
                    const fromAssignments = assignments.filter(a => a.examType === 'buraxilis').map(a => a.fenn);
                    return Array.from(new Set([...fromConfig, ...fromAssignments])).sort();
                  })()
                : ALL_FENNLER
            ).map(fenn => (
              <option key={fenn} value={fenn}>{displayFenn(fenn)}</option>
            ))}
          </Select>
          
          <Select value={sinifFilter} onChange={(e) => setSinifFilter(e.target.value)}>
            <option value="">Bütün siniflər</option>
            {SINIFLAR.map(sinif => (
              <option key={sinif} value={sinif}>{sinif}. sinif</option>
            ))}
          </Select>
          
          {isBuraxilis ? (
            <Select value={variantFilter} onChange={(e) => setVariantFilter(e.target.value)}>
              <option value="">Bütün variantlar</option>
              {['A', 'B', 'C', 'D'].map(variant => (
                <option key={variant} value={variant}>Variant {variant}</option>
              ))}
            </Select>
          ) : (
            <Select value={qrupFilter} onChange={(e) => setQrupFilter(e.target.value)}>
              <option value="">Bütün qruplar</option>
              {QRUPLAR.map(qrup => (
                <option key={qrup} value={qrup}>Qrup {qrup}</option>
              ))}
            </Select>
          )}
          
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">Bütün statuslar</option>
            <option value="active">Aktiv</option>
            <option value="completed">Tamamlandı</option>
            <option value="passive">Passiv</option>
          </Select>
          
          <Select value={progressFilter} onChange={(e) => setProgressFilter(e.target.value)}>
            <option value="">Bütün İləriləmələr</option>
            <option value="completed">%100 tamamlandı</option>
            <option value="not-started">%0 (başlanmayıb)</option>
            <option value="in-progress">Qismən tamamlanmış</option>
          </Select>
        </FilterGrid>

        <ViewToggle>
          <SearchInput
            placeholder="Müəllim adı, ID və ya fənn üzrə axtar..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <Select
            value={sortField}
            onChange={(e) => setSortField(e.target.value as 'createdAt' | 'fenn')}
            style={{ maxWidth: 180 }}
          >
            <option value="createdAt">Yaradılma tarixi</option>
            <option value="fenn">Fənn adı (A‑Z)</option>
          </Select>
          <Select
            value={sortDirection}
            onChange={(e) => setSortDirection(e.target.value as 'asc' | 'desc')}
            style={{ maxWidth: 140 }}
          >
            <option value="desc">Yeni → Köhnə</option>
            <option value="asc">Köhnə → Yeni</option>
          </Select>
          <ViewToggleButton
            type="button"
            active={viewMode === 'cards'}
            onClick={() => setViewMode('cards')}
          >
            Kart görünüşü
          </ViewToggleButton>
          <ViewToggleButton
            type="button"
            active={viewMode === 'list'}
            onClick={() => setViewMode('list')}
          >
            Siyahı görünüşü
          </ViewToggleButton>
        </ViewToggle>

        {(fennFilter || sinifFilter || qrupFilter || variantFilter || statusFilter || progressFilter) && (
          <div style={{ marginTop: '16px', textAlign: 'center' }}>
            <button
              onClick={() => {
                setFennFilter('');
                setSinifFilter('');
                setQrupFilter('');
                setVariantFilter('');
                setStatusFilter('');
                setProgressFilter('');
              }}
              style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                color: '#475569',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                transition: 'all 0.2s ease'
              }}
            >
              <><Icon name="refresh" size={16} /> Filtrləri Təmizlə</>
            </button>
          </div>
        )}
      </FilterSection>

          {filteredAssignments.filter(a => (a as any).type !== 'apellyasiya').length === 0 ? (
            <EmptyState>
              Heç atama tapılmadı
            </EmptyState>
          ) : viewMode === 'cards' ? (
            <AssignmentsList>
              {filteredAssignments.filter(a => (a as any).type !== 'apellyasiya').map(assignment => {
                const totalCount = assignment.totalCount || 0;
                const completedCount = assignment.completedCount || 0;
                const progress = totalCount > 0
                  ? (completedCount / totalCount) * 100
                  : 0;

                const isApellyasiya = (assignment as any).type === 'apellyasiya';
                const apellyasiyaStatus = isApellyasiya ? ((assignment as any).apellyasiya_status === 'completed' ? 'completed' : 'active') : assignment.status;

                // Detect empty assignment (no students assigned)
                const isEmptyAssignment = !isApellyasiya && (() => {
                  if ((assignment.examType === 'buraxilis' || assignment.examType === 'Blok') &&
                      assignment.questionNumbers && assignment.questionNumbers.length > 0) {
                    let totalQTotal = 0;
                    assignment.questionNumbers.forEach((q: string) => {
                      totalQTotal += assignment.questionProgress?.[q]?.total ?? totalCount;
                    });
                    return totalQTotal === 0;
                  }
                  return totalCount === 0;
                })();

                return (
                  <AssignmentCard
                    key={assignment.id}
                    status={apellyasiyaStatus}
                    style={isApellyasiya ? {
                      border: '2px solid #f59e0b',
                      background: 'linear-gradient(135deg, #fffbeb, #fef3c7)'
                    } : isEmptyAssignment ? {
                      background: 'linear-gradient(135deg, #fffbeb, #fef9c3)',
                      borderColor: '#fcd34d',
                    } : {}}
                  >
                    <AssignmentHeader>
                      <AssignmentTitle>
                        {isEmptyAssignment && (
                          <span style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            background: '#f59e0b',
                            color: 'white',
                            fontSize: '11px',
                            fontWeight: 700,
                            padding: '3px 10px',
                            borderRadius: '6px',
                            marginBottom: '8px',
                            marginRight: '0'
                          }}>⚠ Boş atama</span>
                        )}
                        {isApellyasiya && (
                          <span style={{
                            display: 'inline-block',
                            background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '700',
                            marginRight: '8px',
                            textTransform: 'uppercase'
                          }}>
                            📝 Apellyasiya
                          </span>
                        )}
                        {assignment.assignmentType === 'pool' && (
                          <PoolBadgeStyled>
                            🏊 Hovuz
                          </PoolBadgeStyled>
                        )}
                        {displayFenn(assignment.fenn)}
                      </AssignmentTitle>
                      <AssignmentSubtitle>{assignment.hocaName || 'Müəllim'}</AssignmentSubtitle>
                      <StatusBadge status={apellyasiyaStatus}>
                        {isApellyasiya
                          ? ((assignment as any).apellyasiya_status === 'completed' ? 'Təsdiqləndi' : 'Gözləyir')
                          : (apellyasiyaStatus === 'completed' ? 'Tamamlandı' : 
                             apellyasiyaStatus === 'passive' ? 'Passiv' : 'Aktiv')}
                      </StatusBadge>
                    </AssignmentHeader>
                    {/* Pool Teacher Management */}
                    {renderPoolTeacherList(assignment)}
                    {isApellyasiya && (
                      <div style={{
                        padding: '16px',
                        background: '#fffbeb',
                        borderRadius: '8px',
                        marginBottom: '12px',
                        borderLeft: '4px solid #f59e0b',
                        fontSize: '13px',
                        color: '#78350f'
                      }}>
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ fontSize: '14px', fontWeight: '700', color: '#92400e', marginBottom: '8px' }}>
                            Apellyasiya Məlumatları:
                          </div>
                          <div style={{ lineHeight: '1.8' }}>
                            <div><strong>Sual:</strong> {(assignment as any).questionNumber}</div>
                            <div><strong>Şagird ID:</strong> {(assignment as any).studentId}</div>
                            {(assignment as any).studentName && (
                              <div><strong>Şagird:</strong> {(assignment as any).studentName}</div>
                            )}
                            <div style={{ marginTop: '8px' }}>
                              <strong>Səbəb:</strong> {(assignment as any).apellyasiya_səbəbi}
                            </div>
                            {(assignment as any).əvvəlki_yoxlama && (
                              <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #fde68a' }}>
                                <div><strong>Əvvəlki bal:</strong> {(assignment as any).əvvəlki_yoxlama.score}</div>
                                <div><strong>Əvvəlki müəllim:</strong> {(assignment as any).əvvəlki_yoxlama.hoca}</div>
                                {(assignment as any).əvvəlki_yoxlama.timestamp && (
                                  <div style={{ fontSize: '11px', color: '#92400e', marginTop: '4px' }}>
                                    Tarix: {new Date(
                                      (assignment as any).əvvəlki_yoxlama.timestamp.toDate ? 
                                      (assignment as any).əvvəlki_yoxlama.timestamp.toDate() : 
                                      (assignment as any).əvvəlki_yoxlama.timestamp
                                    ).toLocaleString('az-AZ')}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Apellyasiya nəticəsi (tamamlanmışsa) */}
                        {(() => {
                          const apellyasiyaResult = (assignment as any).apellyasiyaResult;
                          if (apellyasiyaResult && apellyasiyaResult.apellyasiya_status === true) {
                            return (
                              <div style={{
                                marginTop: '12px',
                                padding: '12px',
                                background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)',
                                borderRadius: '8px',
                                borderLeft: '4px solid #10b981'
                              }}>
                                <div style={{ fontSize: '13px', fontWeight: '700', color: '#166534', marginBottom: '8px' }}>
                                  ✅ Apellyasiya Nəticəsi:
                                </div>
                                {apellyasiyaResult.previous_score && apellyasiyaResult.new_score && (
                                  <div style={{ fontSize: '13px', color: '#166534', marginBottom: '6px' }}>
                                    <strong>Əvvəlki bal:</strong> {apellyasiyaResult.previous_score} → 
                                    <strong style={{ color: apellyasiyaResult.previous_score !== apellyasiyaResult.new_score ? '#059669' : '#166534' }}>
                                      {' '}Yeni bal: {apellyasiyaResult.new_score}
                                    </strong>
                                    {apellyasiyaResult.previous_score !== apellyasiyaResult.new_score && (
                                      <span style={{ marginLeft: '8px', color: '#059669', fontWeight: '600' }}>
                                        (Dəyişdi)
                                      </span>
                                    )}
                                  </div>
                                )}
                                {apellyasiyaResult.apellyasiya_note && (
                                  <div style={{ fontSize: '13px', color: '#166534', marginBottom: '6px' }}>
                                    <strong>Müəllim qeydi:</strong> {apellyasiyaResult.apellyasiya_note}
                                  </div>
                                )}
                                {apellyasiyaResult.apellyasiya_hoca && apellyasiyaResult.apellyasiya_hocaId !== apellyasiyaResult.hocaId && (
                                  <div style={{ fontSize: '13px', color: '#166534', marginBottom: '6px' }}>
                                    <strong>Apellyasiyanı yoxlayan:</strong> {apellyasiyaResult.apellyasiya_hoca}
                                  </div>
                                )}
                                {apellyasiyaResult.apellyasiya_onaylanma_tarihi && (
                                  <div style={{ fontSize: '12px', color: '#166534', marginTop: '6px' }}>
                                    Təsdiqlənmə tarixi: {new Date(
                                      apellyasiyaResult.apellyasiya_onaylanma_tarihi.toDate ? 
                                      apellyasiyaResult.apellyasiya_onaylanma_tarihi.toDate() : 
                                      apellyasiyaResult.apellyasiya_onaylanma_tarihi
                                    ).toLocaleString('az-AZ')}
                                  </div>
                                )}
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    )}

                    <InfoGrid>
                      <InfoItem>
                        <InfoLabel>Sinif</InfoLabel>
                        <InfoValue>{assignment.sinif}</InfoValue>
                      </InfoItem>
                      {assignment.examType === 'buraxilis' ? (
                        <>
                          <InfoItem>
                            <InfoLabel>Variant</InfoLabel>
                            <InfoValue>{assignment.variant || assignment.qrup}</InfoValue>
                          </InfoItem>
                          {assignment.questionNumbers && assignment.questionNumbers.length > 0 && (
                            <InfoItem style={{ gridColumn: '1 / -1' }}>
                              <InfoLabel>Suallar</InfoLabel>
                              <InfoValue style={{
                                display: 'flex',
                                gap: '4px',
                                flexWrap: 'wrap',
                                fontSize: '12px'
                              }}>
                                {assignment.questionNumbers.map((q: string) => (
                                  <span key={q} style={{
                                    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                    color: 'white',
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    fontWeight: '600'
                                  }}>
                                    {q}
                                  </span>
                                ))}
                              </InfoValue>
                            </InfoItem>
                          )}
                          {assignment.studentIdPrefixFilter?.enabled && assignment.studentIdPrefixFilter.prefixes.length > 0 && (
                            <InfoItem style={{ gridColumn: '1 / -1' }}>
                              <InfoLabel>İş nömrəsi prefiksi</InfoLabel>
                              <InfoValue style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                                {assignment.studentIdPrefixFilter.prefixes.map((prefix: string) => (
                                  <span key={prefix} style={{
                                    background: '#eef2ff',
                                    color: '#4338ca',
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    border: '1px solid #c7d2fe'
                                  }}>
                                    {prefix}
                                  </span>
                                ))}
                              </InfoValue>
                            </InfoItem>
                          )}
                        </>
                      ) : (
                        <InfoItem>
                          <InfoLabel>Qrup</InfoLabel>
                          <InfoValue>{assignment.qrup}</InfoValue>
                        </InfoItem>
                      )}
                    </InfoGrid>

                    <ProgressSection>
                      {/* Buraxılış and Blok with questionNumbers: Hər sual üçün ayrıca irəliləyiş */}
                      {(assignment.examType === 'buraxilis' || assignment.examType === 'Blok') && assignment.questionNumbers && assignment.questionNumbers.length > 0 && (
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: '#64748b', marginBottom: '8px' }}>
                            Sual-sual İləriləmə
                          </div>
                          {assignment.questionNumbers.map((q: string) => {
                            const qProgress = assignment.questionProgress?.[q];
                            const questionCompleted = qProgress?.completed ?? 0;
                            const questionTotal = qProgress?.total ?? totalCount;
                            const questionPercentage = questionTotal > 0 ? (questionCompleted / questionTotal) * 100 : 0;

                            return (
                              <QuestionProgressItem key={q}>
                                <QuestionBadge>{q}</QuestionBadge>
                                <QuestionProgressBar percentage={questionPercentage} />
                                <QuestionProgressText>
                                  {questionCompleted}/{questionTotal}
                                </QuestionProgressText>
                              </QuestionProgressItem>
                            );
                          })}
                        </div>
                      )}

                      {/* Ümumi progress bar */}
                      <ProgressLabel>
                        <span>{(assignment.examType === 'buraxilis' || assignment.examType === 'Blok') && assignment.questionNumbers ? 'Ümumi İləriləmə' : 'İləriləmə'}</span>
                        <span style={{ fontWeight: 600, color: '#1e293b' }}>
                          {(() => {
                            if ((assignment.examType === 'buraxilis' || assignment.examType === 'Blok') && assignment.questionNumbers) {
                              let totalQCompleted = 0;
                              let totalQTotal = 0;
                              assignment.questionNumbers.forEach((q: string) => {
                                const qProgress = assignment.questionProgress?.[q];
                                totalQCompleted += qProgress?.completed ?? 0;
                                totalQTotal += qProgress?.total ?? totalCount;
                              });
                              return `${totalQCompleted}/${totalQTotal}`;
                            }
                            return `${completedCount}/${totalCount}`;
                          })()}
                        </span>
                      </ProgressLabel>
                      {(() => {
                        let calculatedProgress = progress;
                        if ((assignment.examType === 'buraxilis' || assignment.examType === 'Blok') && assignment.questionNumbers) {
                          let totalQCompleted = 0;
                          let totalQTotal = 0;
                          assignment.questionNumbers.forEach((q: string) => {
                            const qProgress = assignment.questionProgress?.[q];
                            totalQCompleted += qProgress?.completed ?? 0;
                            totalQTotal += qProgress?.total ?? totalCount;
                          });
                          calculatedProgress = totalQTotal > 0 ? (totalQCompleted / totalQTotal) * 100 : 0;
                        }
                        return (
                          <>
                            <ProgressBar percentage={calculatedProgress} />
                            <div style={{ 
                              marginTop: '8px', 
                              fontSize: '12px', 
                              color: '#64748b',
                              textAlign: 'right'
                            }}>
                              %{Math.round(calculatedProgress)} tamamlandı
                            </div>
                          </>
                        );
                      })()}
                    </ProgressSection>

                    <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
                      <WhatsAppButton onClick={() => sendWhatsAppMessage(assignment)}>
                        💬 WhatsApp Mesajı Göndər
                      </WhatsAppButton>
                      {assignment.status !== 'completed' && (
                        <ActionButton
                          variant={assignment.status === 'active' ? 'danger' : 'primary'}
                          onClick={() => handleToggleStatus(assignment.id, assignment.status || 'active')}
                        >
                          {assignment.status === 'active' ? '⏸️ Passiv et' : '▶️ Aktiv et'}
                        </ActionButton>
                      )}
                    </div>
                  </AssignmentCard>
                );
              })}
            </AssignmentsList>
          ) : (
            <StatisticsTable>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Fənn</TableHeader>
                    <TableHeader>Müəllim</TableHeader>
                    <TableHeader>Sinif</TableHeader>
                    <TableHeader>{isBuraxilis ? 'Variant' : 'Qrup'}</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Yoxlanmış / Ümumi</TableHeader>
                    <TableHeader>İləriləmə</TableHeader>
                  </TableRow>
                </TableHead>
                <tbody>
                  {filteredAssignments.filter(a => (a as any).type !== 'apellyasiya').map(assignment => {
                    const totalCount = assignment.totalCount || 0;
                    const completedCount = assignment.completedCount || 0;
                    let progress = totalCount > 0 
                      ? (completedCount / totalCount) * 100 
                      : 0;

                    // Buraxılış and Blok için questionProgress'ten hesapla
                    if ((assignment.examType === 'buraxilis' || assignment.examType === 'Blok') && assignment.questionNumbers && assignment.questionNumbers.length > 0) {
                      let totalQCompleted = 0;
                      let totalQTotal = 0;
                      assignment.questionNumbers.forEach((q: string) => {
                        const qProgress = assignment.questionProgress?.[q];
                        totalQCompleted += qProgress?.completed ?? 0;
                        totalQTotal += qProgress?.total ?? totalCount;
                      });
                      progress = totalQTotal > 0 ? (totalQCompleted / totalQTotal) * 100 : 0;
                    }

                    return (
                      <TableRow key={assignment.id}>
                        <TableCell>
                          <div>{displayFenn(assignment.fenn)}</div>
                          {assignment.studentIdPrefixFilter?.enabled && assignment.studentIdPrefixFilter.prefixes.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginTop: '3px' }}>
                              {assignment.studentIdPrefixFilter.prefixes.map((prefix: string) => (
                                <span key={prefix} style={{ background: '#eef2ff', color: '#4f46e5', padding: '1px 5px', borderRadius: '3px', fontSize: '10px', fontWeight: 500, border: '1px solid #c7d2fe' }}>{prefix}</span>
                              ))}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>{assignment.hocaName || 'Müəllim'}</TableCell>
                        <TableCell>{assignment.sinif}</TableCell>
                        <TableCell>{assignment.examType === 'buraxilis' ? (assignment.variant || assignment.qrup) : assignment.qrup}</TableCell>
                        <TableCell>
                          <StatusBadge status={assignment.status}>
                            {assignment.status === 'completed' ? 'Tamamlandı' : 
                             assignment.status === 'passive' ? 'Passiv' : 'Aktiv'}
                          </StatusBadge>
                        </TableCell>
                        <TableCell>
                          {(assignment.examType === 'buraxilis' || assignment.examType === 'Blok') && assignment.questionNumbers && assignment.questionNumbers.length > 0 ? (
                            (() => {
                              let totalQCompleted = 0;
                              let totalQTotal = 0;
                              assignment.questionNumbers.forEach((q: string) => {
                                const qProgress = assignment.questionProgress?.[q];
                                totalQCompleted += qProgress?.completed ?? 0;
                                totalQTotal += qProgress?.total ?? totalCount;
                              });
                              return `${totalQCompleted}/${totalQTotal}`;
                            })()
                          ) : (
                            `${completedCount}/${totalCount}`
                          )}
                        </TableCell>
                        <TableCell>
                          <ProgressCell>
                            <MiniProgressBar percentage={progress} />
                            <PercentageText>%{Math.round(progress)}</PercentageText>
                          </ProgressCell>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </tbody>
              </Table>
            </StatisticsTable>
          )}
        </>
      )}

      {/* Apellyasiya Tab */}
      {activeTab === 'apellyasiyas' && isBuraxilis && (
        <>
          <div style={{ 
            background: 'white', 
            borderRadius: '16px', 
            padding: '20px', 
            marginBottom: '24px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b' }}>
              📝 Apellyasiya Yönetimi
            </div>
            <ExportButton onClick={exportApellyasiyasToExcel}>
              Apellyasiya Nəticələrini Excel Export Et
            </ExportButton>
          </div>

          {/* View Mode Toggle Buttons for Buraxılış Apellyasiyas */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
            marginBottom: '16px'
          }}>
            <button
              onClick={() => setViewMode('cards')}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                border: 'none',
                background: viewMode === 'cards' ? '#3b82f6' : '#e2e8f0',
                color: viewMode === 'cards' ? 'white' : '#475569',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Kartlar
            </button>
            <button
              onClick={() => setViewMode('list')}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                border: 'none',
                background: viewMode === 'list' ? '#3b82f6' : '#e2e8f0',
                color: viewMode === 'list' ? 'white' : '#475569',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Siyahı
            </button>
            <button
              onClick={() => setViewMode('teachers')}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                border: 'none',
                background: viewMode === 'teachers' ? '#3b82f6' : '#e2e8f0',
                color: viewMode === 'teachers' ? 'white' : '#475569',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Müəllimlər
            </button>
          </div>

          <FilterSection>
            <FilterGrid>
              <div>
                <FilterLabel>Fənn</FilterLabel>
                <FilterSelect value={fennFilter} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFennFilter(e.target.value)}>
                  <option value="">Hamısı</option>
                  {Array.from(new Set(assignments.filter(a => (a as any).type === 'apellyasiya').map(a => a.fenn))).map(f => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </FilterSelect>
              </div>
              <div>
                <FilterLabel>Vəziyyət</FilterLabel>
                <FilterSelect value={statusFilter} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setStatusFilter(e.target.value)}>
                  <option value="">Hamısı</option>
                  <option value="active">Gözləyir</option>
                  <option value="completed">Təsdiqləndi</option>
                </FilterSelect>
              </div>
              <div>
                <FilterLabel>Bal Dəyişikliyi</FilterLabel>
                <FilterSelect value={scoreChangeFilter} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setScoreChangeFilter(e.target.value)}>
                  <option value="">Hamısı</option>
                  <option value="increased">📈 Balı artanlar</option>
                  <option value="decreased">📉 Balı azalanlar</option>
                  <option value="unchanged">➡️ Dəyişməyənlər</option>
                </FilterSelect>
              </div>
              <div>
                <FilterLabel>Axtar (Müəllim, Şagird ID)</FilterLabel>
                <SearchInput
                  type="text"
                  placeholder="Axtarış..."
                  value={searchQuery}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                />
              </div>
            </FilterGrid>
            {(fennFilter || statusFilter || scoreChangeFilter) && (
              <div style={{ marginTop: '16px', textAlign: 'center' }}>
               <button
                onClick={() => {
                    setFennFilter('');
                    setStatusFilter('');
                    setScoreChangeFilter('');
                }}
                style={{
                  padding: '10px 20px',
                    background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#475569',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Filtrləri Təmizlə
                </button>
              </div>
            )}
          </FilterSection>

          {/* Teacher-Grouped View for Buraxılış */}
          {viewMode === 'teachers' ? (
            buraxilisTeacherGroups.length === 0 ? (
              <EmptyTeachersState>
                <div className="icon">👥</div>
                <div className="title">Heç müəllim apellyasiyası tapılmadı</div>
                <div className="subtitle">
                  {buraxilisStatusForGrouping !== 'all' || fennFilter || variantFilter
                    ? 'Zəhmət olmasa filtrləri dəyişdirin'
                    : 'Hazırda sistemdə apellyasiya yoxdur'}
                </div>
              </EmptyTeachersState>
            ) : (
              <TeachersContainer>
                {buraxilisTeacherGroups.map(group => {
                  const isExpanded = expandedTeacherGroups.has(group.teacherId);

                  // Determine progress color based on completion percentage
                  const progressColor = group.completionPercentage >= 70
                    ? '#10b981'  // green
                    : group.completionPercentage >= 40
                    ? '#f59e0b'  // amber
                    : '#ef4444'; // red

                  // Get teacher initial for avatar
                  const teacherInitial = group.teacherName.charAt(0).toUpperCase();

                  return (
                    <TeacherGroupCard key={group.teacherId}>
                      {/* Teacher Header with Stats */}
                      <TeacherGroupHeader
                        onClick={() => toggleTeacherGroup(group.teacherId)}
                        role="button"
                        tabIndex={0}
                        aria-expanded={isExpanded}
                        aria-label={`Müəllim qrupunu genişləndir: ${group.teacherName}`}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleTeacherGroup(group.teacherId);
                          }
                        }}
                      >
                        <TeacherInfoSection>
                          <TeacherAvatar>{teacherInitial}</TeacherAvatar>
                          <TeacherNameSection>
                            <TeacherName>{group.teacherName}</TeacherName>
                            <TeacherId>ID: {group.teacherId}</TeacherId>
                            <TeacherStatsRow>
                              <TeacherStatItem variant="total">
                                <div className="stat-value">{group.totalApellyasiyas}</div>
                                <div className="stat-label">Cəmi</div>
                              </TeacherStatItem>
                              <TeacherStatItem variant="completed">
                                <div className="stat-value">{group.completedCount}</div>
                                <div className="stat-label">Təsdiqlənib</div>
                              </TeacherStatItem>
                              <TeacherStatItem variant="pending">
                                <div className="stat-value">{group.pendingCount}</div>
                                <div className="stat-label">Gözləyən</div>
                              </TeacherStatItem>
                              <TeacherStatItem variant="rejected">
                                <div className="stat-value">{group.rejectedCount}</div>
                                <div className="stat-label">Ləğv edilib</div>
                              </TeacherStatItem>
                            </TeacherStatsRow>
                            <OverallProgressBar percentage={group.completionPercentage} color={progressColor} />
                            <TeacherProgressLabel>
                              <span>{Math.round(group.completionPercentage)}% tamamlandı</span>
                              <span>{group.completedCount}/{group.totalApellyasiyas}</span>
                            </TeacherProgressLabel>
                          </TeacherNameSection>
                        </TeacherInfoSection>
                        <ExpandCollapseButton
                          expanded={isExpanded}
                          aria-label={isExpanded ? 'Yığışdır' : 'Genişləndir'}
                        >
                          ▼
                        </ExpandCollapseButton>
                      </TeacherGroupHeader>

                      {/* Student Subgroups */}
                      <StudentSubgroup expanded={isExpanded}>
                        {group.subgroups
                          .filter(subgroup => subgroup.count > 0)
                          .map(subgroup => {
                            const subgroupExpanded = isSubgroupExpanded(group.teacherId, subgroup.status);
                            const statusLabel = subgroup.status === 'pending' ? 'Gözləyən' :
                                             subgroup.status === 'completed' ? 'Təsdiqləndi' : 'Ləğv edilib';

                            return (
                              <div key={`${group.teacherId}-${subgroup.status}`}>
                                <StudentSubgroupHeader
                                  variant={subgroup.status}
                                  onClick={() => toggleStudentSubgroup(group.teacherId, subgroup.status)}
                                  role="button"
                                  tabIndex={0}
                                  aria-expanded={subgroupExpanded}
                                  aria-label={`${statusLabel} altqrupunu genişləndir`}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      toggleStudentSubgroup(group.teacherId, subgroup.status);
                                    }
                                  }}
                                >
                                  <SubgroupTitle variant={subgroup.status}>
                                    {subgroup.status === 'pending' && '⏳'}
                                    {subgroup.status === 'completed' && <Icon name="sync" size={14} />}
                                    {subgroup.status === 'rejected' && <Icon name="archive" size={14} />}
                                    {statusLabel} ({subgroup.count})
                                  </SubgroupTitle>
                                  <SubgroupProgress>
                                    <SubgroupMiniProgress count={subgroup.count} total={group.totalApellyasiyas} />
                                    <span>{subgroup.count}/{group.totalApellyasiyas}</span>
                                  </SubgroupProgress>
                                  <ExpandCollapseButton
                                    expanded={subgroupExpanded}
                                    aria-label={subgroupExpanded ? 'Yığışdır' : 'Genişləndir'}
                                  >
                                    ▼
                                  </ExpandCollapseButton>
                                </StudentSubgroupHeader>

                                {/* Student Cards within Subgroup */}
                                <StudentSubgroup expanded={subgroupExpanded}>
                                  {subgroup.students.map(student => (
                                    <StudentApellyasiyaCard
                                      key={student.id}
                                      onClick={() => {
                                        // For Buraxılış, we don't have BlokApellyasiya type, so we'll just show the student info
                                        // The modal logic can be adapted if needed
                                      }}
                                      role="button"
                                      tabIndex={0}
                                      aria-label={`Şagird detallarını göstər: ${student.studentName} ${student.studentSurname || ''}`}
                                    >
                                      <StudentDetailsGrid>
                                        <StudentDetailItem>
                                          <StudentDetailLabel>Şagird</StudentDetailLabel>
                                          <StudentDetailValue>
                                            {student.studentName} {student.studentSurname || ''}
                                          </StudentDetailValue>
                                        </StudentDetailItem>
                                        <StudentDetailItem>
                                          <StudentDetailLabel>Şagird ID</StudentDetailLabel>
                                          <StudentDetailValue>{student.studentId}</StudentDetailValue>
                                        </StudentDetailItem>
                                        <StudentDetailItem>
                                          <StudentDetailLabel>Sinif / Variant</StudentDetailLabel>
                                          <StudentDetailValue>
                                            {student.grade} {student.sinif && `• ${student.sinif}`}
                                            {student.qrup && ` • ${student.qrup}`}
                                          </StudentDetailValue>
                                        </StudentDetailItem>
                                        <StudentDetailItem>
                                          <StudentDetailLabel>Sual</StudentDetailLabel>
                                          <StudentDetailValue>{student.questionNum}</StudentDetailValue>
                                        </StudentDetailItem>
                                        <StudentDetailItem>
                                          <StudentDetailLabel>Fənn</StudentDetailLabel>
                                          <StudentDetailValue>{student.fenn}</StudentDetailValue>
                                        </StudentDetailItem>
                                        <StudentDetailItem>
                                          <StudentDetailLabel>Status</StudentDetailLabel>
                                          <ApellyasiyaStatusBadge status={student.status}>
                                            {student.status === 'pending' ? 'Gözləyən' :
                                             student.status === 'approved' ? 'Təsdiqləndi' : 'Ləğv edilib'}
                                          </ApellyasiyaStatusBadge>
                                        </StudentDetailItem>
                                      </StudentDetailsGrid>

                                      <StudentScoresRow>
                                        <StudentDetailLabel>Orijinal müəllim:</StudentDetailLabel>
                                        <StudentDetailValue>{student.originalTeacher || '-'}</StudentDetailValue>

                                        {student.reviewTeacher && student.reviewTeacher !== student.originalTeacher && (
                                          <>
                                            <StudentDetailLabel style={{ marginLeft: '16px' }}>Review müəllim:</StudentDetailLabel>
                                            <StudentDetailValue>{student.reviewTeacher}</StudentDetailValue>
                                          </>
                                        )}

                                        {student.newScore !== undefined ? (
                                          <ScoreComparison>
                                            <OriginalScore>{student.originalScore}</OriginalScore>
                                            <span> → </span>
                                            <NewScore change={student.scoreChange || 0}>{student.newScore}</NewScore>
                                            <ScoreChangeIndicator change={student.scoreChange || 0}>
                                              {student.scoreChange && student.scoreChange > 0 && '▲'}
                                              {student.scoreChange && student.scoreChange < 0 && '▼'}
                                              {(!student.scoreChange || student.scoreChange === 0) && '='}
                                              {student.scoreChange !== undefined && Math.abs(student.scoreChange)}
                                            </ScoreChangeIndicator>
                                          </ScoreComparison>
                                        ) : (
                                          <ScoreComparison>
                                            <span style={{ fontWeight: 600, color: '#1e293b' }}>
                                              Bal: {student.originalScore}
                                            </span>
                                          </ScoreComparison>
                                        )}

                                        <StudentDetailLabel style={{ marginLeft: 'auto' }}>Tarix:</StudentDetailLabel>
                                        <StudentDetailValue style={{ fontSize: '13px' }}>
                                          {new Date(student.date).toLocaleDateString('az-AZ')}
                                        </StudentDetailValue>
                                      </StudentScoresRow>
                                    </StudentApellyasiyaCard>
                                  ))}
                                </StudentSubgroup>
                              </div>
                            );
                          })}
                      </StudentSubgroup>
                    </TeacherGroupCard>
                  );
                })}
              </TeachersContainer>
            )
          ) : filteredAssignments.length === 0 ? (
            <EmptyState>
              Heç apellyasiya tapılmadı
            </EmptyState>
          ) : viewMode === 'cards' ? (
            <AssignmentsList>
              {filteredAssignments.map(assignment => {
                const isApellyasiya = (assignment as any).type === 'apellyasiya';
                const apellyasiyaStatus = isApellyasiya ? ((assignment as any).apellyasiya_status === 'completed' ? 'completed' : 'active') : assignment.status;
                const apellyasiyaResult = (assignment as any).apellyasiyaResult;
                
                return (
                  <AssignmentCard 
                    key={assignment.id} 
                    status={apellyasiyaStatus}
                    style={{
                      border: '2px solid #f59e0b',
                      background: 'linear-gradient(135deg, #fffbeb, #fef3c7)'
                    }}
                  >
                    <AssignmentHeader>
                      <AssignmentTitle>
                        <span style={{
                          display: 'inline-block',
                          background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '700',
                          marginRight: '8px',
                          textTransform: 'uppercase'
                        }}>
                          📝 Apellyasiya
                        </span>
                        {displayFenn(assignment.fenn)}
                      </AssignmentTitle>
                      {assignment.studentIdPrefixFilter?.enabled && assignment.studentIdPrefixFilter.prefixes.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginBottom: '4px' }}>
                          <span style={{ fontSize: '10px', color: '#6366f1', fontWeight: 600, alignSelf: 'center', marginRight: '1px' }}>Prefiks:</span>
                          {assignment.studentIdPrefixFilter.prefixes.map((prefix: string) => (
                            <span key={prefix} style={{ background: '#eef2ff', color: '#4f46e5', padding: '1px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 500, border: '1px solid #c7d2fe' }}>{prefix}</span>
                          ))}
                        </div>
                      )}
                      <AssignmentSubtitle>{assignment.hocaName || 'Müəllim'}</AssignmentSubtitle>
                      <StatusBadge status={apellyasiyaStatus}>
                        {apellyasiyaStatus === 'completed' ? 'Təsdiqləndi' : 'Gözləyir'}
                      </StatusBadge>
                    </AssignmentHeader>

                    {/* Apellyasiya detayları */}
                    <div style={{
                      padding: '16px',
                      background: '#fffbeb',
                      borderRadius: '8px',
                      marginBottom: '12px',
                      borderLeft: '4px solid #f59e0b',
                      fontSize: '13px',
                      color: '#78350f'
                    }}>
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ fontSize: '14px', fontWeight: '700', color: '#92400e', marginBottom: '8px' }}>
                          Apellyasiya Məlumatları:
                        </div>
                        <div style={{ lineHeight: '1.8' }}>
                          <div><strong>Sual:</strong> {(assignment as any).questionNumber}</div>
                          <div><strong>Şagird ID:</strong> {(assignment as any).studentId}</div>
                          {(assignment as any).studentName && (
                            <div><strong>Şagird:</strong> {(assignment as any).studentName}</div>
                          )}
                          <div style={{ marginTop: '8px' }}>
                            <strong>Səbəb:</strong> {(assignment as any).apellyasiya_səbəbi}
                          </div>
                          {(assignment as any).əvvəlki_yoxlama && (
                            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #fde68a' }}>
                              <div><strong>Əvvəlki bal:</strong> {(assignment as any).əvvəlki_yoxlama.score}</div>
                              <div><strong>Əvvəlki müəllim:</strong> {(assignment as any).əvvəlki_yoxlama.hoca}</div>
                              {(assignment as any).əvvəlki_yoxlama.timestamp && (
                                <div style={{ fontSize: '11px', color: '#92400e', marginTop: '4px' }}>
                                  Tarix: {new Date(
                                    (assignment as any).əvvəlki_yoxlama.timestamp.toDate ? 
                                    (assignment as any).əvvəlki_yoxlama.timestamp.toDate() : 
                                    (assignment as any).əvvəlki_yoxlama.timestamp
                                  ).toLocaleString('az-AZ')}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Apellyasiya nəticəsi (tamamlanmışsa) */}
                      {apellyasiyaResult && apellyasiyaResult.apellyasiya_status === true && (
                        <div style={{
                          marginTop: '12px',
                          padding: '12px',
                          background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)',
                          borderRadius: '8px',
                          borderLeft: '4px solid #10b981'
                        }}>
                          <div style={{ fontSize: '13px', fontWeight: '700', color: '#166534', marginBottom: '8px' }}>
                            ✅ Apellyasiya Nəticəsi:
                          </div>
                          {apellyasiyaResult.previous_score && apellyasiyaResult.new_score && (
                            <div style={{ fontSize: '13px', color: '#166534', marginBottom: '6px' }}>
                              <strong>Əvvəlki bal:</strong> {apellyasiyaResult.previous_score} → 
                              <strong style={{ color: apellyasiyaResult.previous_score !== apellyasiyaResult.new_score ? '#059669' : '#166534' }}>
                                {' '}Yeni bal: {apellyasiyaResult.new_score}
                              </strong>
                              {apellyasiyaResult.previous_score !== apellyasiyaResult.new_score && (
                                <span style={{ marginLeft: '8px', color: '#059669', fontWeight: '600' }}>
                                  (Dəyişdi)
                                </span>
                              )}
                            </div>
                          )}
                          {apellyasiyaResult.apellyasiya_note && (
                            <div style={{ fontSize: '13px', color: '#166534', marginBottom: '6px' }}>
                              <strong>Müəllim qeydi:</strong> {apellyasiyaResult.apellyasiya_note}
                            </div>
                          )}
                          {apellyasiyaResult.apellyasiya_hoca && apellyasiyaResult.apellyasiya_hocaId !== apellyasiyaResult.hocaId && (
                            <div style={{ fontSize: '13px', color: '#166534', marginBottom: '6px' }}>
                              <strong>Apellyasiyanı yoxlayan:</strong> {apellyasiyaResult.apellyasiya_hoca}
                            </div>
                          )}
                          {apellyasiyaResult.apellyasiya_onaylanma_tarihi && (
                            <div style={{ fontSize: '12px', color: '#166534', marginTop: '6px' }}>
                              Təsdiqlənmə tarixi: {new Date(
                                apellyasiyaResult.apellyasiya_onaylanma_tarihi.toDate ? 
                                apellyasiyaResult.apellyasiya_onaylanma_tarihi.toDate() : 
                                apellyasiyaResult.apellyasiya_onaylanma_tarihi
                              ).toLocaleString('az-AZ')}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <InfoGrid>
                      <InfoItem>
                        <InfoLabel>Sinif</InfoLabel>
                        <InfoValue>{assignment.sinif}</InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>Variant</InfoLabel>
                        <InfoValue>{assignment.variant || assignment.qrup}</InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>İlərləmə</InfoLabel>
                        <InfoValue>
                          {assignment.completedCount || 0} / {assignment.totalCount || 0}
                          <span style={{ marginLeft: '8px', fontSize: '12px', color: '#64748b' }}>
                            ({Math.round((assignment.completedCount || 0) / (assignment.totalCount || 1) * 100)}% tamamlandı)
                          </span>
                        </InfoValue>
                      </InfoItem>
                    </InfoGrid>
                    <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end' }}>
                      <WhatsAppButton onClick={() => sendWhatsAppMessage(assignment)}>
                        💬 WhatsApp Mesajı Göndər
                      </WhatsAppButton>
                    </div>
                  </AssignmentCard>
                );
              })}
            </AssignmentsList>
          ) : (
            <AssignmentsTable>
              <thead>
                <tr>
                  <th>Fənn</th>
                  <th>Müəllim</th>
                  <th>Sual</th>
                  <th>Şagird ID</th>
                  <th>Şagird</th>
                  <th>Vəziyyət</th>
                  <th>İlərləmə</th>
                </tr>
              </thead>
              <tbody>
                {filteredAssignments.map(assignment => {
                  const isApellyasiya = (assignment as any).type === 'apellyasiya';
                  const apellyasiyaStatus = isApellyasiya ? ((assignment as any).apellyasiya_status === 'completed' ? 'completed' : 'active') : assignment.status;
                  
                  return (
                    <tr key={assignment.id}>
                      <td>{displayFenn(assignment.fenn)}</td>
                      <td>{assignment.hocaName || 'Müəllim'}</td>
                      <td>{(assignment as any).questionNumber}</td>
                      <td>{(assignment as any).studentId}</td>
                      <td>{(assignment as any).studentName || '-'}</td>
                      <td>
                        <StatusBadge status={apellyasiyaStatus}>
                          {apellyasiyaStatus === 'completed' ? 'Təsdiqləndi' : 'Gözləyir'}
                        </StatusBadge>
                      </td>
                      <td>
                        {assignment.completedCount || 0} / {assignment.totalCount || 0}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </AssignmentsTable>
          )}
        </>
      )}

      {activeTab === 'assignments' && isBuraxilis && (
        <>
          <FilterSection>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '12px' }}>
                🔍 Filtrlər (Yalnız Müəllim Tapşırıqları)
              </div>
            </div>
            <FilterGrid>
              <Select value={fennFilter} onChange={(e) => setFennFilter(e.target.value)}>
                <option value="">Tüm Fənnlər</option>
                {ALL_FENNLER.map(fenn => (
                  <option key={fenn} value={fenn}>{fenn}</option>
                ))}
              </Select>
              
              <Select value={sinifFilter} onChange={(e) => setSinifFilter(e.target.value)}>
                <option value="">Tüm Siniflər</option>
                {SINIFLAR.map(sinif => (
                  <option key={sinif} value={sinif}>{sinif}. Sinif</option>
                ))}
              </Select>
              
              <Select value={variantFilter} onChange={(e) => setVariantFilter(e.target.value)}>
                <option value="">Tüm Variantlar</option>
                {['A', 'B', 'C', 'D'].map(variant => (
                  <option key={variant} value={variant}>Variant {variant}</option>
                ))}
              </Select>
              
              <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">Tüm Statuslar</option>
                <option value="active">Aktif</option>
                <option value="completed">Tamamlandı</option>
                <option value="passive">Pasif</option>
              </Select>
              
              <Select value={progressFilter} onChange={(e) => setProgressFilter(e.target.value)}>
                <option value="">Tüm İlərləmələr</option>
                <option value="completed">%100 Tamamlandı</option>
                <option value="not-started">%0 (Başlanmamış)</option>
                <option value="in-progress">Qismən Tamamlanmış</option>
              </Select>
            </FilterGrid>
            
            {(fennFilter || sinifFilter || variantFilter || statusFilter || progressFilter) && (
              <div style={{ marginTop: '16px', textAlign: 'center' }}>
                <button
                  onClick={() => {
                    setFennFilter('');
                    setSinifFilter('');
                    setVariantFilter('');
                    setStatusFilter('');
                    setProgressFilter('');
                  }}
                  style={{
                    padding: '10px 20px',
                    background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#475569',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <><Icon name="refresh" size={16} /> Filtrləri Təmizlə</>
                </button>
              </div>
            )}
          </FilterSection>

          {filteredAssignments.length === 0 ? (
            <EmptyState>
              Hiç müəllim tapşırığı bulunamadı
            </EmptyState>
          ) : (
            <AssignmentsList>
              {filteredAssignments.map(assignment => {
                const totalCount = assignment.totalCount || 0;
                const completedCount = assignment.completedCount || 0;
                let progress = totalCount > 0 
                  ? (completedCount / totalCount) * 100 
                  : 0;

                // Buraxılış için questionProgress'ten hesapla
                let totalQCompleted = completedCount;
                let totalQTotal = totalCount;
                if (assignment.questionNumbers && assignment.questionNumbers.length > 0) {
                  totalQCompleted = 0;
                  totalQTotal = 0;
                  assignment.questionNumbers.forEach((q: string) => {
                    const qProgress = assignment.questionProgress?.[q];
                    totalQCompleted += qProgress?.completed ?? 0;
                    totalQTotal += qProgress?.total ?? totalCount;
                  });
                  progress = totalQTotal > 0 ? (totalQCompleted / totalQTotal) * 100 : 0;
                }

                return (
                  <AssignmentCard key={assignment.id} status={assignment.status}>
                    <AssignmentHeader>
                      <AssignmentTitle>{displayFenn(assignment.fenn)}</AssignmentTitle>
                      {assignment.studentIdPrefixFilter?.enabled && assignment.studentIdPrefixFilter.prefixes.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginTop: '3px' }}>
                          <span style={{ fontSize: '10px', color: '#6366f1', fontWeight: 600, alignSelf: 'center', marginRight: '1px' }}>Prefiks:</span>
                          {assignment.studentIdPrefixFilter.prefixes.map((prefix: string) => (
                            <span key={prefix} style={{ background: '#eef2ff', color: '#4f46e5', padding: '1px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 500, border: '1px solid #c7d2fe' }}>{prefix}</span>
                          ))}
                        </div>
                      )}
                      <AssignmentSubtitle>{assignment.hocaName || 'Öğretmen'}</AssignmentSubtitle>
                      <StatusBadge status={assignment.status}>
                        {assignment.status === 'completed' ? 'Tamamlandı' : 
                         assignment.status === 'passive' ? 'Pasif' : 'Aktif'}
                      </StatusBadge>
                    </AssignmentHeader>

                    <InfoGrid>
                      <InfoItem>
                        <InfoLabel>Sınıf</InfoLabel>
                        <InfoValue>{assignment.sinif}</InfoValue>
                      </InfoItem>
                      <InfoItem>
                        <InfoLabel>Variant</InfoLabel>
                        <InfoValue>{assignment.variant || assignment.qrup}</InfoValue>
                      </InfoItem>
                      {assignment.questionNumbers && assignment.questionNumbers.length > 0 && (
                        <InfoItem style={{ gridColumn: '1 / -1' }}>
                          <InfoLabel>Suallar</InfoLabel>
                          <InfoValue style={{ 
                            display: 'flex', 
                            gap: '4px', 
                            flexWrap: 'wrap',
                            fontSize: '12px'
                          }}>
                            {assignment.questionNumbers.map((q: string) => (
                              <span key={q} style={{
                                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                color: 'white',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontWeight: '600'
                              }}>
                                {q}
                              </span>
                            ))}
                          </InfoValue>
                        </InfoItem>
                      )}
                    </InfoGrid>

                    <ProgressSection>
                      {assignment.questionNumbers && assignment.questionNumbers.length > 0 && (
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: '#64748b', marginBottom: '8px' }}>
                        Sual-sual İləriləmə
                          </div>
                          {assignment.questionNumbers.map((q: string) => {
                            const qProgress = assignment.questionProgress?.[q];
                            const questionCompleted = qProgress?.completed ?? 0;
                            const questionTotal = qProgress?.total ?? totalCount;
                            const questionPercentage = questionTotal > 0 ? (questionCompleted / questionTotal) * 100 : 0;
                            
                            return (
                              <QuestionProgressItem key={q}>
                                <QuestionBadge>{q}</QuestionBadge>
                                <QuestionProgressBar percentage={questionPercentage} />
                                <QuestionProgressText>
                                  {questionCompleted}/{questionTotal}
                                </QuestionProgressText>
                              </QuestionProgressItem>
                            );
                          })}
                        </div>
                      )}
                      
                      <ProgressLabel>
                        <span>Ümumi İləriləmə</span>
                        <span style={{ fontWeight: 600, color: '#1e293b' }}>
                          {totalQCompleted}/{totalQTotal}
                        </span>
                      </ProgressLabel>
                      <ProgressBar percentage={progress} />
                      <div style={{ 
                        marginTop: '8px', 
                        fontSize: '12px', 
                        color: '#64748b',
                        textAlign: 'right'
                      }}>
                        %{Math.round(progress)} tamamlandı
                      </div>
                    </ProgressSection>

                    <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
                      <WhatsAppButton onClick={() => sendWhatsAppMessage(assignment)}>
                        💬 WhatsApp Mesajı Göndər
                      </WhatsAppButton>
                      {assignment.status !== 'completed' && (
                        <ActionButton
                          variant={assignment.status === 'active' ? 'danger' : 'primary'}
                          onClick={() => handleToggleStatus(assignment.id, assignment.status || 'active')}
                        >
                          {assignment.status === 'active' ? '⏸️ Pasif Et' : '▶️ Aktif Et'}
                        </ActionButton>
                      )}
                    </div>
                  </AssignmentCard>
                );
              })}
            </AssignmentsList>
          )}
        </>
      )}

      {/* Blok Apellyasiya Tab - NEW format */}
      {activeTab === 'apellyasiyas' && !isBuraxilis && hasNewBlokFormat && (
        <BlokApellyasiyaTableContainer>
          {/* Summary Statistics */}
          <BlokApellyasiyaSummary>
            <BlokApellyasiyaSummaryCard variant="total">
              <div className="count">{allBlokApellyasiyas.length}</div>
              <div className="label">Toplam</div>
            </BlokApellyasiyaSummaryCard>
            <BlokApellyasiyaSummaryCard variant="pending">
              <div className="count">{allBlokApellyasiyas.filter(a => a.status === 'pending').length}</div>
              <div className="label">Gözləyən</div>
            </BlokApellyasiyaSummaryCard>
            <BlokApellyasiyaSummaryCard variant="approved">
              <div className="count">{allBlokApellyasiyas.filter(a => a.status === 'approved').length}</div>
              <div className="label">Təsdiqlənib</div>
            </BlokApellyasiyaSummaryCard>
            <BlokApellyasiyaSummaryCard variant="rejected">
              <div className="count">{allBlokApellyasiyas.filter(a => a.status === 'rejected').length}</div>
              <div className="label">Ləğv Edilib</div>
            </BlokApellyasiyaSummaryCard>
          </BlokApellyasiyaSummary>

          {/* Task 5.2: View Mode Toggle Buttons for Apellyasiyas Tab */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
            marginBottom: '16px'
          }}>
            <button
              onClick={() => setViewMode('cards')}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                border: 'none',
                background: viewMode === 'cards' ? '#3b82f6' : '#e2e8f0',
                color: viewMode === 'cards' ? 'white' : '#475569',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Kartlar
            </button>
            <button
              onClick={() => setViewMode('list')}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                border: 'none',
                background: viewMode === 'list' ? '#3b82f6' : '#e2e8f0',
                color: viewMode === 'list' ? 'white' : '#475569',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Siyahı
            </button>
            <button
              onClick={() => setViewMode('teachers')}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                border: 'none',
                background: viewMode === 'teachers' ? '#3b82f6' : '#e2e8f0',
                color: viewMode === 'teachers' ? 'white' : '#475569',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              Müəllimlər
            </button>
          </div>

          {/* Filter Controls */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            <div style={{
              display: 'flex',
              gap: '12px',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
            <div>
              <label style={{ fontSize: '13px', fontWeight: '600', color: '#64748b', marginRight: '8px' }}>Status:</label>
              <select
                value={apellyasiyaStatusFilter}
                onChange={(e) => setApellyasiyaStatusFilter(e.target.value as any)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '2px solid #e2e8f0',
                  fontSize: '14px',
                  background: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="all">Hamısı</option>
                <option value="pending">Gözləyən</option>
                <option value="approved">Təsdiqlənib</option>
                <option value="rejected">Ləğv Edilib</option>
              </select>
            </div>

            <div>
              <label style={{ fontSize: '13px', fontWeight: '600', color: '#64748b', marginRight: '8px' }}>Fənn:</label>
              <select
                value={apellyasiyaFennFilter}
                onChange={(e) => setApellyasiyaFennFilter(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '2px solid #e2e8f0',
                  fontSize: '14px',
                  background: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="">Hamısı</option>
                {Array.from(new Set(allBlokApellyasiyas.map(a => a.fenn))).sort().map(fenn => (
                  <option key={fenn} value={fenn}>{fenn}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ fontSize: '13px', fontWeight: '600', color: '#64748b', marginRight: '8px' }}>Qrup:</label>
              <select
                value={apellyasiyaQrupFilter}
                onChange={(e) => setApellyasiyaQrupFilter(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '2px solid #e2e8f0',
                  fontSize: '14px',
                  background: 'white',
                  cursor: 'pointer'
                }}
              >
                <option value="">Hamısı</option>
                <option value="I">I</option>
                <option value="II">II</option>
                <option value="III">III</option>
                <option value="IV">IV</option>
              </select>
            </div>

            <button
              onClick={() => {
                setApellyasiyaStatusFilter('all');
                setApellyasiyaFennFilter('');
                setApellyasiyaQrupFilter('');
              }}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '600',
                border: 'none',
                background: '#6b7280',
                color: 'white',
                cursor: 'pointer'
              }}
            >
              Təmizlə
            </button>
            </div>

            <button
                onClick={exportBlokApellyasiyasToExcel}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  border: 'none',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                  transition: 'all 0.2s'
                }}
              >
                📥 Excel-ə İxrac
              </button>
          </div>

          {/* ============================================ */}
          {/* Task 6: TEACHER-GROUPED VIEW RENDERING */}
          {/* ============================================ */}

          {viewMode === 'teachers' ? (
            /* Task 6.1-6.5: Render teacher-grouped view */
            teacherGroups.length === 0 ? (
              <EmptyTeachersState>
                <div className="icon">👥</div>
                <div className="title">Heç müəllim apellyasiyası tapılmadı</div>
                <div className="subtitle">
                  {apellyasiyaStatusFilter !== 'all' || apellyasiyaFennFilter || apellyasiyaQrupFilter
                    ? 'Zəhmət olmasa filtrləri dəyişdirin'
                    : 'Hazırda sistemdə apellyasiya yoxdur'}
                </div>
              </EmptyTeachersState>
            ) : (
              <TeachersContainer>
                {teacherGroups.map(group => {
                  const isExpanded = expandedTeacherGroups.has(group.teacherId);

                  // Task 7: Determine progress color based on completion percentage
                  const progressColor = group.completionPercentage >= 70
                    ? '#10b981'  // green
                    : group.completionPercentage >= 40
                    ? '#f59e0b'  // amber
                    : '#ef4444'; // red

                  // Get teacher initial for avatar
                  const teacherInitial = group.teacherName.charAt(0).toUpperCase();

                  return (
                    <TeacherGroupCard key={group.teacherId}>
                      {/* Teacher Header with Stats */}
                      <TeacherGroupHeader
                        onClick={() => toggleTeacherGroup(group.teacherId)}
                        role="button"
                        tabIndex={0}
                        aria-expanded={isExpanded}
                        aria-label={`Müəllim qrupunu genişləndir: ${group.teacherName}`}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleTeacherGroup(group.teacherId);
                          }
                        }}
                      >
                        <TeacherInfoSection>
                          <TeacherAvatar>{teacherInitial}</TeacherAvatar>
                          <TeacherNameSection>
                            <TeacherName>{group.teacherName}</TeacherName>
                            <TeacherId>ID: {group.teacherId}</TeacherId>
                            <TeacherStatsRow>
                              <TeacherStatItem variant="total">
                                <div className="stat-value">{group.totalApellyasiyas}</div>
                                <div className="stat-label">Cəmi</div>
                              </TeacherStatItem>
                              <TeacherStatItem variant="completed">
                                <div className="stat-value">{group.completedCount}</div>
                                <div className="stat-label">Təsdiqlənib</div>
                              </TeacherStatItem>
                              <TeacherStatItem variant="pending">
                                <div className="stat-value">{group.pendingCount}</div>
                                <div className="stat-label">Gözləyən</div>
                              </TeacherStatItem>
                              <TeacherStatItem variant="rejected">
                                <div className="stat-value">{group.rejectedCount}</div>
                                <div className="stat-label">Ləğv edilib</div>
                              </TeacherStatItem>
                            </TeacherStatsRow>
                            <OverallProgressBar percentage={group.completionPercentage} color={progressColor} />
                            <TeacherProgressLabel>
                              <span>{Math.round(group.completionPercentage)}% tamamlandı</span>
                              <span>{group.completedCount}/{group.totalApellyasiyas}</span>
                            </TeacherProgressLabel>
                          </TeacherNameSection>
                        </TeacherInfoSection>
                        <ExpandCollapseButton
                          expanded={isExpanded}
                          aria-label={isExpanded ? 'Yığışdır' : 'Genişləndir'}
                        >
                          ▼
                        </ExpandCollapseButton>
                      </TeacherGroupHeader>

                      {/* Student Subgroups */}
                      <StudentSubgroup expanded={isExpanded}>
                        {group.subgroups
                          .filter(subgroup => subgroup.count > 0)
                          .map(subgroup => {
                            const subgroupExpanded = isSubgroupExpanded(group.teacherId, subgroup.status);
                            const statusLabel = subgroup.status === 'pending' ? 'Gözləyən' :
                                             subgroup.status === 'completed' ? 'Təsdiqləndi' : 'Ləğv edilib';

                            return (
                              <div key={`${group.teacherId}-${subgroup.status}`}>
                                <StudentSubgroupHeader
                                  variant={subgroup.status}
                                  onClick={() => toggleStudentSubgroup(group.teacherId, subgroup.status)}
                                  role="button"
                                  tabIndex={0}
                                  aria-expanded={subgroupExpanded}
                                  aria-label={`${statusLabel} altqrupunu genişləndir`}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      toggleStudentSubgroup(group.teacherId, subgroup.status);
                                    }
                                  }}
                                >
                                  <SubgroupTitle variant={subgroup.status}>
                                    {subgroup.status === 'pending' && '⏳'}
                                    {subgroup.status === 'completed' && '✓'}
                                    {subgroup.status === 'rejected' && '✗'}
                                    {statusLabel} ({subgroup.count})
                                  </SubgroupTitle>
                                  <SubgroupProgress>
                                    <SubgroupMiniProgress count={subgroup.count} total={group.totalApellyasiyas} />
                                    <span>{subgroup.count}/{group.totalApellyasiyas}</span>
                                  </SubgroupProgress>
                                  <ExpandCollapseButton
                                    expanded={subgroupExpanded}
                                    aria-label={subgroupExpanded ? 'Yığışdır' : 'Genişləndir'}
                                  >
                                    ▼
                                  </ExpandCollapseButton>
                                </StudentSubgroupHeader>

                                {/* Student Cards within Subgroup */}
                                <StudentSubgroup expanded={subgroupExpanded}>
                                  {subgroup.students.map(student => (
                                    <StudentApellyasiyaCard
                                      key={student.id}
                                      onClick={() => {
                                        // Show detail modal for student (reusing existing modal logic)
                                        setSelectedApellyasiya({
                                          ...student,
                                          type: 'apellyasiya',
                                          totalCount: 1,
                                          completedCount: student.status === 'approved' ? 1 : 0
                                        } as BlokApellyasiya);
                                      }}
                                      role="button"
                                      tabIndex={0}
                                      aria-label={`Şagird detallarını göstər: ${student.studentName} ${student.studentSurname}`}
                                    >
                                      <StudentDetailsGrid>
                                        <StudentDetailItem>
                                          <StudentDetailLabel>Şagird</StudentDetailLabel>
                                          <StudentDetailValue>
                                            {student.studentName} {student.studentSurname}
                                          </StudentDetailValue>
                                        </StudentDetailItem>
                                        <StudentDetailItem>
                                          <StudentDetailLabel>Şagird ID</StudentDetailLabel>
                                          <StudentDetailValue>{student.studentId}</StudentDetailValue>
                                        </StudentDetailItem>
                                        <StudentDetailItem>
                                          <StudentDetailLabel>Sinif / Qrup</StudentDetailLabel>
                                          <StudentDetailValue>
                                            {student.grade} {student.qrup && `• ${student.qrup}`}
                                            {student.subGroup && ` • ${student.subGroup}`}
                                          </StudentDetailValue>
                                        </StudentDetailItem>
                                        <StudentDetailItem>
                                          <StudentDetailLabel>Sual</StudentDetailLabel>
                                          <StudentDetailValue>{student.questionNum}</StudentDetailValue>
                                        </StudentDetailItem>
                                        <StudentDetailItem>
                                          <StudentDetailLabel>Fənn</StudentDetailLabel>
                                          <StudentDetailValue>{student.fenn}</StudentDetailValue>
                                        </StudentDetailItem>
                                        <StudentDetailItem>
                                          <StudentDetailLabel>Status</StudentDetailLabel>
                                          <ApellyasiyaStatusBadge status={student.status}>
                                            {student.status === 'pending' ? 'Gözləyən' :
                                             student.status === 'approved' ? 'Təsdiqləndi' : 'Ləğv edilib'}
                                          </ApellyasiyaStatusBadge>
                                        </StudentDetailItem>
                                      </StudentDetailsGrid>

                                      <StudentScoresRow>
                                        <StudentDetailLabel>Orijinal müəllim:</StudentDetailLabel>
                                        <StudentDetailValue>{student.originalTeacher || '-'}</StudentDetailValue>

                                        {student.reviewTeacher && student.reviewTeacher !== student.originalTeacher && (
                                          <>
                                            <StudentDetailLabel style={{ marginLeft: '16px' }}>Review müəllim:</StudentDetailLabel>
                                            <StudentDetailValue>{student.reviewTeacher}</StudentDetailValue>
                                          </>
                                        )}

                                        {student.newScore !== undefined ? (
                                          <ScoreComparison>
                                            <OriginalScore>{student.originalScore}</OriginalScore>
                                            <span> → </span>
                                            <NewScore change={student.scoreChange || 0}>{student.newScore}</NewScore>
                                            <ScoreChangeIndicator change={student.scoreChange || 0}>
                                              {student.scoreChange && student.scoreChange > 0 && '▲'}
                                              {student.scoreChange && student.scoreChange < 0 && '▼'}
                                              {(!student.scoreChange || student.scoreChange === 0) && '='}
                                              {student.scoreChange !== undefined && Math.abs(student.scoreChange)}
                                            </ScoreChangeIndicator>
                                          </ScoreComparison>
                                        ) : (
                                          <ScoreComparison>
                                            <span style={{ fontWeight: 600, color: '#1e293b' }}>
                                              Bal: {student.originalScore}
                                            </span>
                                          </ScoreComparison>
                                        )}

                                        <StudentDetailLabel style={{ marginLeft: 'auto' }}>Tarix:</StudentDetailLabel>
                                        <StudentDetailValue style={{ fontSize: '13px' }}>
                                          {new Date(student.date).toLocaleDateString('az-AZ')}
                                        </StudentDetailValue>
                                      </StudentScoresRow>
                                    </StudentApellyasiyaCard>
                                  ))}
                                </StudentSubgroup>
                              </div>
                            );
                          })}
                      </StudentSubgroup>
                    </TeacherGroupCard>
                  );
                })}
              </TeachersContainer>
            )
          ) : (
            /* Original Table View */
            <>
              {/* Apellyasiya Table */}
              {allBlokApellyasiyas.filter(a => {
                if (apellyasiyaStatusFilter !== 'all' && a.status !== apellyasiyaStatusFilter) return false;
                if (apellyasiyaFennFilter && a.fenn !== apellyasiyaFennFilter) return false;
                if (apellyasiyaQrupFilter && !qrupEquals(a.qrup, apellyasiyaQrupFilter)) return false;
                return true;
              }).length === 0 ? (
                <div style={{
                  padding: '40px',
                  textAlign: 'center',
                  color: '#64748b',
                  fontSize: '14px'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📝</div>
                  Apellyasiya tapılmadı
                </div>
              ) : (
                <BlokApellyasiyaTable>
                  <BlokApellyasiyaTableHeader>
                    <div>Şagird</div>
                    <div>Sual</div>
                    <div>Fənn</div>
                    <div>Qrup</div>
                    <div>Altqrup</div>
                    <div>Bal Dəyişikliyi</div>
                    <div>Status</div>
                    <div>Müəllim</div>
                    <div>Tarix</div>
                    <div>Əməliyyatlar</div>
                  </BlokApellyasiyaTableHeader>

                  {allBlokApellyasiyas
                    .filter(a => {
                      if (apellyasiyaStatusFilter !== 'all' && a.status !== apellyasiyaStatusFilter) return false;
                      if (apellyasiyaFennFilter && a.fenn !== apellyasiyaFennFilter) return false;
                      if (apellyasiyaQrupFilter && !qrupEquals(a.qrup, apellyasiyaQrupFilter)) return false;
                      return true;
                    })
                    .map((apell, idx) => (
                  <BlokApellyasiyaRow
                    key={idx}
                    onClick={() => setSelectedApellyasiya(apell)}
                  >
                    <div style={{ fontWeight: '500' }}>
                      {apell.studentName} {apell.studentSurname}
                    </div>
                    <div>{apell.questionNum}</div>
                    <div>{displayFenn(apell.fenn)}</div>
                    <div>{apell.qrup}</div>
                    <div>{apell.subGroup || '-'}</div>
                    <div>
                      {apell.newScore !== undefined ? (
                        <>
                          <span style={{ textDecoration: 'line-through', color: '#94a3b8', marginRight: '4px' }}>
                            {apell.originalScore}
                          </span>
                          <BlokScoreChange change={apell.scoreChange}>
                            → {apell.newScore}
                            {apell.scoreChange !== undefined && (
                              <span style={{ fontSize: '11px', marginLeft: '4px' }}>
                                ({apell.scoreChange > 0 ? '+' : ''}{apell.scoreChange})
                              </span>
                            )}
                          </BlokScoreChange>
                        </>
                      ) : (
                        <span style={{ color: '#94a3b8' }}>{apell.originalScore}</span>
                      )}
                    </div>
                    <div>
                      <BlokApellyasiyaStatus status={apell.status}>
                        {apell.status === 'pending' ? 'Gözləyən' :
                         apell.status === 'approved' ? 'Təsdiqlənib' : 'Ləğv Edilib'}
                      </BlokApellyasiyaStatus>
                    </div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>
                      {apell.reviewTeacher || apell.originalTeacher || '-'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>
                      {new Date(apell.date).toLocaleDateString('az-AZ')}
                    </div>
                    <div>
                      <WhatsAppButton
                        onClick={(e) => {
                          e.stopPropagation();

                          // Find teacher by name to get their ID
                          const teacherName = apell.reviewTeacher || apell.originalTeacher;
                          const teacher = teachers.find(t => t.name === teacherName);

                          if (!teacher || !teacher.phoneNumber) {
                            alert('Bu müəllimin telefon nömrəsi sistemdə qeydiyyata alınmayıb.');
                            return;
                          }

                          const assignmentForWhatsApp = {
                            type: 'apellyasiya',
                            hocaId: teacher.id,
                            hocaName: teacher.name,
                            fenn: apell.fenn,
                            sinif: apell.grade,
                            qrup: apell.qrup,
                            questionNumber: apell.questionNum,
                            studentName: `${apell.studentName} ${apell.studentSurname}`,
                            status: apell.status
                          };
                          sendWhatsAppMessage(assignmentForWhatsApp);
                        }}
                        style={{ padding: '6px 12px', fontSize: '11px' }}
                      >
                        💬 WhatsApp
                      </WhatsAppButton>
                    </div>
                  </BlokApellyasiyaRow>
                ))}
            </BlokApellyasiyaTable>
          )}
          </>
          )}

          {/* Detail Modal (simplified for now - can be enhanced in Task 4.5) */}
          {selectedApellyasiya && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }} onClick={() => setSelectedApellyasiya(null)}>
              <div style={{
                background: 'white',
                borderRadius: '16px',
                padding: '24px',
                maxWidth: '600px',
                width: '90%',
                maxHeight: '80vh',
                overflowY: 'auto'
              }} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h2 style={{ fontSize: '18px', fontWeight: '700', margin: 0 }}>
                    Apellyasiya Detalları
                  </h2>
                  <button
                    onClick={() => setSelectedApellyasiya(null)}
                    style={{
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: 'none',
                      background: '#f3f4f6',
                      cursor: 'pointer',
                      fontSize: '16px'
                    }}
                  >
                    ✕
                  </button>
                </div>

                <div style={{ fontSize: '14px', color: '#1e293b' }}>
                  <div style={{ marginBottom: '12px' }}>
                    <strong>Şagird:</strong> {selectedApellyasiya.studentName} {selectedApellyasiya.studentSurname}
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <strong>Sinif:</strong> {selectedApellyasiya.grade} | <strong>Qrup:</strong> {selectedApellyasiya.qrup}
                    {selectedApellyasiya.subGroup && <span> | <strong>Altqrup:</strong> {selectedApellyasiya.subGroup}</span>}
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <strong>Sual:</strong> {selectedApellyasiya.questionNum} | <strong>Fənn:</strong> {displayFenn(selectedApellyasiya.fenn)}
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <strong>Əvvəlki bal:</strong> {selectedApellyasiya.originalScore}
                    {selectedApellyasiya.newScore !== undefined && (
                      <> → <strong>Yeni bal:</strong> {selectedApellyasiya.newScore}</>
                    )}
                  </div>
                  {selectedApellyasiya.scoreChange !== undefined && (
                    <div style={{ marginBottom: '12px' }}>
                      <strong>Dəyişiklik:</strong> <BlokScoreChange change={selectedApellyasiya.scoreChange}>
                        {selectedApellyasiya.scoreChange > 0 ? '+' : ''}{selectedApellyasiya.scoreChange} bal
                      </BlokScoreChange>
                    </div>
                  )}
                  <div style={{ marginBottom: '12px' }}>
                    <strong>Orijinal müəllim:</strong> {selectedApellyasiya.originalTeacher || 'Məlumat yoxdur'}
                  </div>
                  {selectedApellyasiya.reviewTeacher && (
                    <div style={{ marginBottom: '12px' }}>
                      <strong>Review müəllim:</strong> {selectedApellyasiya.reviewTeacher}
                    </div>
                  )}
                  <div style={{ marginBottom: '12px' }}>
                    <strong>Status:</strong>{' '}
                    <BlokApellyasiyaStatus status={selectedApellyasiya.status}>
                      {selectedApellyasiya.status === 'pending' ? 'Gözləyən' :
                       selectedApellyasiya.status === 'approved' ? 'Təsdiqlənib' : 'Ləğv Edilib'}
                    </BlokApellyasiyaStatus>
                  </div>
                  <div>
                    <strong>Tarix:</strong> {new Date(selectedApellyasiya.date).toLocaleString('az-AZ')}
                  </div>
                </div>
              </div>
            </div>
          )}
        </BlokApellyasiyaTableContainer>
      )}

      {/* Blok Apellyasiya Tab - LEGACY format: Not available */}
      {activeTab === 'apellyasiyas' && !isBuraxilis && !hasNewBlokFormat && (
        <LegacyModeMessage>
          <LegacyModeIcon>!</LegacyModeIcon>
          <LegacyModeTitle>Bu Xüsusiyyət Mövcud Deyil</LegacyModeTitle>
          <LegacyModeText>
            Bu xüsusiyyət yalnız yeni Blok formatında mövcuddur. Köhnə formatdakı imtahan nəticələrini "Ümumi Baxış" tabında görə bilərsiniz.
          </LegacyModeText>
          <LegacyModeButton onClick={() => setActiveTab('overview')}>
            Ümumi Baxışa Qayıt
          </LegacyModeButton>
        </LegacyModeMessage>
      )}

      {/* Statistics Tab - Buraxılısh */}
      {activeTab === 'statistics' && isBuraxilis && (
        <>
          {/* Teacher Statistics Section */}
          <TeacherStatsContainer>
            <TeacherStatsHeader onClick={() => setIsTeacherStatsExpanded(!isTeacherStatsExpanded)}>
              <TeacherStatsHeaderTitle>
                <span>👥 Müəllim Statistikası</span>
              </TeacherStatsHeaderTitle>
              <TeacherStatsHeaderIcon expanded={isTeacherStatsExpanded}>
                ▼
              </TeacherStatsHeaderIcon>
            </TeacherStatsHeader>
            <TeacherStatsContent expanded={isTeacherStatsExpanded}>
              {(() => {
                const teacherStats = calculateTeacherStatistics();
                const overallTotalTeachers = teacherStats.length;
                const overallTotalAssignments = teacherStats.reduce((sum, t) => sum + t.totalAssignments, 0);
                const overallTotalQuestions = teacherStats.reduce((sum, t) => sum + t.totalQuestions, 0);
                const overallCompletedQuestions = teacherStats.reduce((sum, t) => sum + t.completedQuestions, 0);
                const overallRemainingQuestions = teacherStats.reduce((sum, t) => sum + t.remainingQuestions, 0);

                return (
                  <>
                    <TeacherStatsSummary>
                      <TeacherStatsSummaryItem>
                        <TeacherStatsSummaryLabel>Ümumi Müəllim</TeacherStatsSummaryLabel>
                        <TeacherStatsSummaryValue>{overallTotalTeachers}</TeacherStatsSummaryValue>
                      </TeacherStatsSummaryItem>
                      <TeacherStatsSummaryItem>
                        <TeacherStatsSummaryLabel>Ümumi Tapşırıq</TeacherStatsSummaryLabel>
                        <TeacherStatsSummaryValue>{overallTotalAssignments}</TeacherStatsSummaryValue>
                      </TeacherStatsSummaryItem>
                      <TeacherStatsSummaryItem>
                        <TeacherStatsSummaryLabel>Ümumi Sual</TeacherStatsSummaryLabel>
                        <TeacherStatsSummaryValue>{overallTotalQuestions}</TeacherStatsSummaryValue>
                      </TeacherStatsSummaryItem>
                      <TeacherStatsSummaryItem>
                        <TeacherStatsSummaryLabel>Tamamlanan</TeacherStatsSummaryLabel>
                        <TeacherStatsSummaryValue style={{ color: '#10b981' }}>{overallCompletedQuestions}</TeacherStatsSummaryValue>
                      </TeacherStatsSummaryItem>
                      <TeacherStatsSummaryItem>
                        <TeacherStatsSummaryLabel>Qalan</TeacherStatsSummaryLabel>
                        <TeacherStatsSummaryValue style={{ color: '#ef4444' }}>{overallRemainingQuestions}</TeacherStatsSummaryValue>
                      </TeacherStatsSummaryItem>
                    </TeacherStatsSummary>
                    <TeacherStatsGrid>
                      {teacherStats.map((stat) => (
                        <TeacherStatsCard key={stat.teacherId}>
                          <TeacherStatsCardHeader>
                            <TeacherStatsCardName>{stat.teacherName}</TeacherStatsCardName>
                            <TeacherStatsCardProgress percentage={stat.progressPercentage}>
                              {Math.round(stat.progressPercentage)}%
                            </TeacherStatsCardProgress>
                          </TeacherStatsCardHeader>
                          <TeacherStatsCardNumbers>
                            <TeacherStatsCardNumberItem>
                              <TeacherStatsCardNumberLabel>Tapşırıq</TeacherStatsCardNumberLabel>
                              <TeacherStatsCardNumberValue>{stat.totalAssignments}</TeacherStatsCardNumberValue>
                            </TeacherStatsCardNumberItem>
                            <TeacherStatsCardNumberItem>
                              <TeacherStatsCardNumberLabel>Ümumi Sual</TeacherStatsCardNumberLabel>
                              <TeacherStatsCardNumberValue>{stat.totalQuestions}</TeacherStatsCardNumberValue>
                            </TeacherStatsCardNumberItem>
                            <TeacherStatsCardNumberItem>
                              <TeacherStatsCardNumberLabel style={{ color: '#10b981' }}>Tamamlanan</TeacherStatsCardNumberLabel>
                              <TeacherStatsCardNumberValue style={{ color: '#10b981' }}>{stat.completedQuestions}</TeacherStatsCardNumberValue>
                            </TeacherStatsCardNumberItem>
                            <TeacherStatsCardNumberItem>
                              <TeacherStatsCardNumberLabel style={{ color: '#ef4444' }}>Qalan</TeacherStatsCardNumberLabel>
                              <TeacherStatsCardNumberValue style={{ color: '#ef4444' }}>{stat.remainingQuestions}</TeacherStatsCardNumberValue>
                            </TeacherStatsCardNumberItem>
                          </TeacherStatsCardNumbers>
                          <TeacherStatsProgressBar percentage={stat.progressPercentage}>
                            <TeacherStatsProgressFill percentage={stat.progressPercentage} />
                          </TeacherStatsProgressBar>
                          <TeacherStatsAssignmentsList>
                            <TeacherStatsAssignmentsTitle>Tapşırıqlar:</TeacherStatsAssignmentsTitle>
                            {stat.assignments.map((assignment, idx) => (
                              <TeacherStatsAssignmentItem key={idx}>
                                <TeacherStatsAssignmentInfo>
                                  {gradeToRoman(assignment.sinif)} sinif - {assignment.qrupOrVariant} {isBuraxilis ? 'variant' : 'qrup'} - {displayFenn(assignment.fenn)}
                                </TeacherStatsAssignmentInfo>
                                <TeacherStatsAssignmentCount>{assignment.questionCount} sual</TeacherStatsAssignmentCount>
                              </TeacherStatsAssignmentItem>
                            ))}
                          </TeacherStatsAssignmentsList>
                        </TeacherStatsCard>
                      ))}
                    </TeacherStatsGrid>
                  </>
                );
              })()}
            </TeacherStatsContent>
          </TeacherStatsContainer>

          <div style={{ 
            background: 'white', 
            borderRadius: '16px', 
            padding: '20px', 
            marginBottom: '24px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>
              📊 Sinif/Variant/Xarici Dil Statistikası
            </div>
            <div style={{ fontSize: '14px', color: '#64748b' }}>
              Admin boş kağıtlar da daxil - Ümumi statistika
            </div>
          </div>

          <AssignmentsList>
            {(() => {
              console.log('🔍 Statistics Tab - Starting per grade/variant/xdil card generation (admin + müəllim)...');
              console.log('🔍 Total buraxilis students available:', buraxilisStudents.length);

              const grades = [8, 9, 10, 11];
              const variants = ['A', 'B'];
              const cards: JSX.Element[] = [];
              
              grades.forEach(grade => {
                variants.forEach(variant => {
                  console.log(`\n🔍 Checking combination: Grade ${grade}, Variant ${variant} (all xdil)`);

                  // Bu sinif/variant üçün bütün tələbələr (bütün xdil-lər birləşik)
                  const relevantStudents = buraxilisStudents.filter(s =>
                    String(s.grade) === String(grade) &&
                    s.variant === variant
                  );

                  console.log(`   ✅ Found ${relevantStudents.length} students for this grade/variant`);

                  if (relevantStudents.length === 0) {
                    console.log('   ❌ Skipping - no students found');
                    return;
                  }

                  const totalStudents = relevantStudents.length;
                  const subjectConfigs = getBuraxilisSubjectsForGrade(
                    grade,
                    blankCtxFromExam(getSelectedExam())
                  );
                  const cardKey = `${grade}-${variant}`;
                  const isExpanded = expandedStatsCards.includes(cardKey);

                    type SubjectStat = {
                      subject: string;
                      questions: string[];
                      totalQuestions: number;
                      totalChecked: number;
                      totalTeacherChecked: number;
                      totalAdminEmpty: number;
                      studentCount: number; // Xdil üçün öğrenci sayısı
                      perQuestion: {
                        [qNum: string]: {
                          totalChecked: number;
                          teacherChecked: number;
                          adminEmpty: number;
                        };
                      };
                    };

                  const subjectStats: SubjectStat[] = [];

                  subjectConfigs.forEach(subjectCfg => {
                      const questions = subjectCfg.questions || [];
                      if (questions.length === 0) return;

                      // Xarici dillər üçün: eyni sinif/variant daxilində xdil-ə görə filterlə (İngilis dili, Rus dili, Fransız dili, Alman dili)
                      const foreignLanguages = ['İngilis dili', 'Rus dili', 'Fransız dili', 'Alman dili'];
                      if (foreignLanguages.includes(subjectCfg.subject)) {
                        // normalizeXdil fonksiyonu
                        const normalizeXdil = (xdil: string | null | undefined): string => {
                          if (!xdil) return '';
                          const normalized = xdil.trim();
                          if (normalized === 'İngilis' || normalized === 'ingilis' || normalized === 'İNGİLİS') return 'İngilis dili';
                          if (normalized === 'Fransız' || normalized === 'fransız' || normalized === 'FRANSIZ') return 'Fransız dili';
                          if (normalized === 'Alman' || normalized === 'alman' || normalized === 'ALMAN') return 'Alman dili';
                          if (normalized === 'Rus' || normalized === 'rus' || normalized === 'RUS') return 'Rus dili';
                          return normalized;
                        };

                        // Yalnız bu xarici dil üçün tələbələri filterlə
                        const xdilStudents = relevantStudents.filter(s => normalizeXdil(s.xdil) === subjectCfg.subject);
                        if (xdilStudents.length === 0) return;

                        const questionsPerStudent = questions.length;

                        const totalCheckedCount: { [key: string]: number } = {};
                        const teacherCheckedCount: { [key: string]: number } = {};
                        const adminEmptyCount: { [key: string]: number } = {};

                        xdilStudents.forEach(student => {
                          questions.forEach(qNum => {
                            const qField = `${qNum}_c`;
                            const qValue: any = (student as any)[qField];

                            if (qValue && typeof qValue === 'object') {
                              const isAdminEmpty =
                                qValue.hocaId === 'admin' &&
                                qValue.note &&
                                qValue.note.includes('Boş sual - Admin tərəfindən təsdiqləndi');

                              totalCheckedCount[qNum] = (totalCheckedCount[qNum] || 0) + 1;

                              if (isAdminEmpty) {
                                adminEmptyCount[qNum] = (adminEmptyCount[qNum] || 0) + 1;
                              } else if (qValue.hocaId && qValue.hocaId !== 'admin') {
                                teacherCheckedCount[qNum] = (teacherCheckedCount[qNum] || 0) + 1;
                              }
                            }
                          });
                        });

                        const totalQuestions = xdilStudents.length * questionsPerStudent;
                        const totalChecked = Object.values(totalCheckedCount).reduce((sum, v) => sum + v, 0);
                        const totalTeacherChecked = Object.values(teacherCheckedCount).reduce((sum, v) => sum + v, 0);
                        const totalAdminEmpty = Object.values(adminEmptyCount).reduce((sum, v) => sum + v, 0);

                        const perQuestion: SubjectStat['perQuestion'] = {};
                        questions.forEach(qNum => {
                          perQuestion[qNum] = {
                            totalChecked: totalCheckedCount[qNum] || 0,
                            teacherChecked: teacherCheckedCount[qNum] || 0,
                            adminEmpty: adminEmptyCount[qNum] || 0
                          };
                        });

                        subjectStats.push({
                          subject: subjectCfg.subject,
                          questions,
                          totalQuestions,
                          totalChecked,
                          totalTeacherChecked,
                          totalAdminEmpty,
                          studentCount: xdilStudents.length,
                          perQuestion
                        });
                      } else {
                        // Azərbaycan dili və Riyaziyyat: bütün xdil-lər birlikdə (sinif+variant üzrə)
                        const questionsPerStudent = questions.length;

                        const totalCheckedCount: { [key: string]: number } = {};
                        const teacherCheckedCount: { [key: string]: number } = {};
                        const adminEmptyCount: { [key: string]: number } = {};

                        relevantStudents.forEach(student => {
                          questions.forEach(qNum => {
                            const qField = `${qNum}_c`;
                            const qValue: any = (student as any)[qField];

                            if (qValue && typeof qValue === 'object') {
                              const isAdminEmpty =
                                qValue.hocaId === 'admin' &&
                                qValue.note &&
                                qValue.note.includes('Boş sual - Admin tərəfindən təsdiqləndi');

                              totalCheckedCount[qNum] = (totalCheckedCount[qNum] || 0) + 1;

                              if (isAdminEmpty) {
                                adminEmptyCount[qNum] = (adminEmptyCount[qNum] || 0) + 1;
                              } else if (qValue.hocaId && qValue.hocaId !== 'admin') {
                                teacherCheckedCount[qNum] = (teacherCheckedCount[qNum] || 0) + 1;
                              }
                            }
                          });
                        });

                        const totalQuestions = totalStudents * questionsPerStudent;
                        const totalChecked = Object.values(totalCheckedCount).reduce((sum, v) => sum + v, 0);
                        const totalTeacherChecked = Object.values(teacherCheckedCount).reduce((sum, v) => sum + v, 0);
                        const totalAdminEmpty = Object.values(adminEmptyCount).reduce((sum, v) => sum + v, 0);

                        const perQuestion: SubjectStat['perQuestion'] = {};
                        questions.forEach(qNum => {
                          perQuestion[qNum] = {
                            totalChecked: totalCheckedCount[qNum] || 0,
                            teacherChecked: teacherCheckedCount[qNum] || 0,
                            adminEmpty: adminEmptyCount[qNum] || 0
                          };
                        });

                        subjectStats.push({
                          subject: subjectCfg.subject,
                          questions,
                          totalQuestions,
                          totalChecked,
                          totalTeacherChecked,
                          totalAdminEmpty,
                          studentCount: totalStudents, // Digər fənnlər üçün sinif+variant üzrə ümumi tələbə sayı
                          perQuestion
                        });
                      }
                    });

                  if (subjectStats.length === 0) {
                    return;
                  }

                  // Ümumi (ultra) statistikalar – bütün fənnlər birlikdə
                  const ultraTotalQuestions = subjectStats.reduce(
                    (sum, s) => sum + s.totalQuestions,
                    0
                  );
                  const ultraTotalChecked = subjectStats.reduce(
                    (sum, s) => sum + s.totalChecked,
                    0
                  );
                  const ultraTeacherChecked = subjectStats.reduce(
                    (sum, s) => sum + s.totalTeacherChecked,
                    0
                  );
                  const ultraAdminEmpty = subjectStats.reduce(
                    (sum, s) => sum + s.totalAdminEmpty,
                    0
                  );

                  const ultraPercentage =
                    ultraTotalQuestions > 0
                      ? (ultraTotalChecked / ultraTotalQuestions) * 100
                      : 0;

                  cards.push(
                    <AssignmentCard key={cardKey} status="active">
                      <AssignmentHeader>
                        <AssignmentTitle>Sinif {grade} • Variant {variant}</AssignmentTitle>
                        <AssignmentSubtitle>Üç fənn birlikdə: Xarici dil, Azərbaycan dili, Riyaziyyat</AssignmentSubtitle>
                      </AssignmentHeader>

                      <InfoGrid>
                        <InfoItem>
                          <InfoLabel>Tələbə Sayı</InfoLabel>
                          <InfoValue>{totalStudents}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                          <InfoLabel>Ümumi Sual Sayı</InfoLabel>
                          <InfoValue>
                            {subjectStats.reduce(
                              (sum, s) => sum + s.questions.length,
                              0
                            )}
                          </InfoValue>
                        </InfoItem>
                        <InfoItem style={{ gridColumn: '1 / -1' }}>
                          <InfoLabel>Fənnlər və suallar</InfoLabel>
                          <InfoValue
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '4px',
                              fontSize: '12px'
                            }}
                          >
                            {subjectStats.map(s => (
                              <div key={s.subject}>
                                <span style={{ fontWeight: 600 }}>{s.subject}: </span>
                                {s.questions.map(q => (
                                  <span
                                    key={q}
                                    style={{
                                      display: 'inline-block',
                                      marginRight: 4,
                                      marginTop: 2,
                                      background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                      color: 'white',
                                      padding: '2px 8px',
                                      borderRadius: '4px',
                                      fontWeight: '600'
                                    }}
                                  >
                                    {q}
                                  </span>
                                ))}
                              </div>
                            ))}
                          </InfoValue>
                        </InfoItem>
                      </InfoGrid>

                      <ProgressSection>
                        {/* Ultra total progress */}
                        <ProgressLabel>
                          <span style={{ fontWeight: '700' }}>ÜMUMİ ULTRA PROGRESS</span>
                          <span style={{ fontWeight: 600, color: '#1e293b' }}>
                            {ultraTotalChecked}/{ultraTotalQuestions}
                          </span>
                        </ProgressLabel>
                        <div
                          style={{
                            width: '100%',
                            height: 10,
                            background: '#e2e8f0',
                            borderRadius: 5,
                            overflow: 'hidden',
                            display: 'flex',
                            marginTop: 4
                          }}
                          title={`Hoca: ${ultraTeacherChecked}, Admin boş: ${ultraAdminEmpty}, Cəmi yoxlama: ${ultraTotalChecked}/${ultraTotalQuestions}`}
                        >
                          <div
                            style={{
                              width: `${Math.min(
                                ultraTotalQuestions > 0
                                  ? (ultraTeacherChecked / ultraTotalQuestions) * 100
                                  : 0,
                                100
                              )}%`,
                              background: 'linear-gradient(135deg, #3b82f6, #2563eb)'
                            }}
                          />
                          <div
                            style={{
                              width: `${Math.min(
                                ultraTotalQuestions > 0
                                  ? (ultraAdminEmpty / ultraTotalQuestions) * 100
                                  : 0,
                                Math.max(
                                  100 -
                                    (ultraTotalQuestions > 0
                                      ? (ultraTeacherChecked / ultraTotalQuestions) * 100
                                      : 0),
                                  0
                                )
                              )}%`,
                              background: 'linear-gradient(135deg, #facc15, #eab308)'
                            }}
                          />
                        </div>
                        <div
                          style={{
                            marginTop: '8px',
                            fontSize: '12px',
                            color: '#64748b',
                            textAlign: 'right'
                          }}
                        >
                          %{Math.round(ultraPercentage)} tamamlandı • Hoca: {ultraTeacherChecked}, Admin boş: {ultraAdminEmpty}
                        </div>

                        {/* Fənn-fənn detallar */}
                        <div style={{ marginTop: 16, marginBottom: isExpanded ? 16 : 0 }}>
                          <div
                            style={{
                              fontSize: '13px',
                              fontWeight: 600,
                              color: '#64748b',
                              marginBottom: isExpanded ? '8px' : 0,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between'
                            }}
                            onClick={() =>
                              setExpandedStatsCards(prev =>
                                prev.includes(cardKey)
                                  ? prev.filter(k => k !== cardKey)
                                  : [...prev, cardKey]
                              )
                            }
                          >
                            <span>Fənn və sual üzrə detallar</span>
                            <span style={{ fontSize: 12 }}>
                              {isExpanded ? '▲ Gizlət' : '▼ Genişləndir'}
                            </span>
                          </div>

                          {isExpanded &&
                            subjectStats.map(s => (
                              <div
                                key={s.subject}
                                style={{
                                  marginTop: 12,
                                  paddingTop: 8,
                                  borderTop: '1px solid #e2e8f0'
                                }}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: 6,
                                    fontSize: 13,
                                    color: '#475569'
                                  }}
                                >
                                  <span style={{ fontWeight: 600 }}>{s.subject}</span>
                                  <span style={{ fontWeight: 600 }}>
                                    {s.totalChecked}/{s.totalQuestions}
                                  </span>
                                </div>
                                <div
                                  style={{
                                    width: '100%',
                                    height: 8,
                                    background: '#e2e8f0',
                                    borderRadius: 4,
                                    overflow: 'hidden',
                                    display: 'flex',
                                    marginBottom: 8
                                  }}
                                >
                                  <div
                                    style={{
                                      width: `${Math.min(
                                        s.totalQuestions > 0
                                          ? (s.totalTeacherChecked / s.totalQuestions) * 100
                                          : 0,
                                        100
                                      )}%`,
                                      background: 'linear-gradient(135deg, #3b82f6, #2563eb)'
                                    }}
                                  />
                                  <div
                                    style={{
                                      width: `${Math.min(
                                        s.totalQuestions > 0
                                          ? (s.totalAdminEmpty / s.totalQuestions) * 100
                                          : 0,
                                        Math.max(
                                          100 -
                                            (s.totalQuestions > 0
                                              ? (s.totalTeacherChecked / s.totalQuestions) * 100
                                              : 0),
                                          0
                                        )
                                      )}%`,
                                      background: 'linear-gradient(135deg, #facc15, #eab308)'
                                    }}
                                  />
                                </div>

                                {s.questions.map(qNum => {
                                  const perQ = s.perQuestion[qNum];
                                  // Xarici dil üçün qrup ölçüsü: yalnız həmin xdil-də olan tələbə sayı.
                                  // Digər fənnlər üçün sinif+variant üzrə ümumi tələbə sayı.
                                  // Her öğrenci için 1 soru olduğu için, denom = öğrenci sayısı
                                  const denom = s.studentCount || totalStudents;
                                  const qTeacherPerc =
                                    denom > 0 ? (perQ.teacherChecked / denom) * 100 : 0;
                                  const qAdminPerc =
                                    denom > 0 ? (perQ.adminEmpty / denom) * 100 : 0;

                                  return (
                                    <QuestionProgressItem key={`${s.subject}-${qNum}`}>
                                      <QuestionBadge>{qNum}</QuestionBadge>
                                      <div
                                        style={{
                                          flex: 1,
                                          height: 6,
                                          background: '#e2e8f0',
                                          borderRadius: 3,
                                          overflow: 'hidden',
                                          display: 'flex'
                                        }}
                                        title={`Hoca: ${perQ.teacherChecked}, Admin boş: ${perQ.adminEmpty}, Cəmi yoxlama: ${perQ.totalChecked}/${denom}`}
                                      >
                                        <div
                                          style={{
                                            width: `${Math.min(qTeacherPerc, 100)}%`,
                                            background:
                                              'linear-gradient(135deg, #3b82f6, #2563eb)'
                                          }}
                                        />
                                        <div
                                          style={{
                                            width: `${Math.min(
                                              qAdminPerc,
                                              Math.max(100 - qTeacherPerc, 0)
                                            )}%`,
                                            background:
                                              'linear-gradient(135deg, #facc15, #eab308)'
                                          }}
                                        />
                                      </div>
                                      <QuestionProgressText>
                                        Hoca: {perQ.teacherChecked} • Admin: {perQ.adminEmpty} /{' '}
                                        {denom}
                                      </QuestionProgressText>
                                    </QuestionProgressItem>
                                  );
                                })}
                              </div>
                            ))}
                        </div>
                      </ProgressSection>
                    </AssignmentCard>
                  );
                });
              });

              return cards.length > 0 ? cards : (
                <EmptyState>
                  Statistika üçün veri bulunamadı
                </EmptyState>
              );
            })()}
          </AssignmentsList>
        </>
      )}

      {/* Blok Statistics Tab - NEW format - Matching Buraxılış design */}
      {activeTab === 'statistics' && !isBuraxilis && hasNewBlokFormat && (
        <>
          {/* Teacher Statistics Section */}
          <TeacherStatsContainer>
            <TeacherStatsHeader onClick={() => setIsTeacherStatsExpanded(!isTeacherStatsExpanded)}>
              <TeacherStatsHeaderTitle>
                <span>👥 Müəllim Statistikası</span>
              </TeacherStatsHeaderTitle>
              <TeacherStatsHeaderIcon expanded={isTeacherStatsExpanded}>
                ▼
              </TeacherStatsHeaderIcon>
            </TeacherStatsHeader>
            <TeacherStatsContent expanded={isTeacherStatsExpanded}>
              {(() => {
                const teacherStats = calculateTeacherStatistics();
                const overallTotalTeachers = teacherStats.length;
                const overallTotalAssignments = teacherStats.reduce((sum, t) => sum + t.totalAssignments, 0);
                const overallTotalQuestions = teacherStats.reduce((sum, t) => sum + t.totalQuestions, 0);
                const overallCompletedQuestions = teacherStats.reduce((sum, t) => sum + t.completedQuestions, 0);
                const overallRemainingQuestions = teacherStats.reduce((sum, t) => sum + t.remainingQuestions, 0);

                return (
                  <>
                    <TeacherStatsSummary>
                      <TeacherStatsSummaryItem>
                        <TeacherStatsSummaryLabel>Ümumi Müəllim</TeacherStatsSummaryLabel>
                        <TeacherStatsSummaryValue>{overallTotalTeachers}</TeacherStatsSummaryValue>
                      </TeacherStatsSummaryItem>
                      <TeacherStatsSummaryItem>
                        <TeacherStatsSummaryLabel>Ümumi Tapşırıq</TeacherStatsSummaryLabel>
                        <TeacherStatsSummaryValue>{overallTotalAssignments}</TeacherStatsSummaryValue>
                      </TeacherStatsSummaryItem>
                      <TeacherStatsSummaryItem>
                        <TeacherStatsSummaryLabel>Ümumi Sual</TeacherStatsSummaryLabel>
                        <TeacherStatsSummaryValue>{overallTotalQuestions}</TeacherStatsSummaryValue>
                      </TeacherStatsSummaryItem>
                      <TeacherStatsSummaryItem>
                        <TeacherStatsSummaryLabel>Tamamlanan</TeacherStatsSummaryLabel>
                        <TeacherStatsSummaryValue style={{ color: '#10b981' }}>{overallCompletedQuestions}</TeacherStatsSummaryValue>
                      </TeacherStatsSummaryItem>
                      <TeacherStatsSummaryItem>
                        <TeacherStatsSummaryLabel>Qalan</TeacherStatsSummaryLabel>
                        <TeacherStatsSummaryValue style={{ color: '#ef4444' }}>{overallRemainingQuestions}</TeacherStatsSummaryValue>
                      </TeacherStatsSummaryItem>
                    </TeacherStatsSummary>
                    <TeacherStatsGrid>
                      {teacherStats.map((stat) => (
                        <TeacherStatsCard key={stat.teacherId}>
                          <TeacherStatsCardHeader>
                            <TeacherStatsCardName>{stat.teacherName}</TeacherStatsCardName>
                            <TeacherStatsCardProgress percentage={stat.progressPercentage}>
                              {Math.round(stat.progressPercentage)}%
                            </TeacherStatsCardProgress>
                          </TeacherStatsCardHeader>
                          <TeacherStatsCardNumbers>
                            <TeacherStatsCardNumberItem>
                              <TeacherStatsCardNumberLabel>Tapşırıq</TeacherStatsCardNumberLabel>
                              <TeacherStatsCardNumberValue>{stat.totalAssignments}</TeacherStatsCardNumberValue>
                            </TeacherStatsCardNumberItem>
                            <TeacherStatsCardNumberItem>
                              <TeacherStatsCardNumberLabel>Ümumi Sual</TeacherStatsCardNumberLabel>
                              <TeacherStatsCardNumberValue>{stat.totalQuestions}</TeacherStatsCardNumberValue>
                            </TeacherStatsCardNumberItem>
                            <TeacherStatsCardNumberItem>
                              <TeacherStatsCardNumberLabel style={{ color: '#10b981' }}>Tamamlanan</TeacherStatsCardNumberLabel>
                              <TeacherStatsCardNumberValue style={{ color: '#10b981' }}>{stat.completedQuestions}</TeacherStatsCardNumberValue>
                            </TeacherStatsCardNumberItem>
                            <TeacherStatsCardNumberItem>
                              <TeacherStatsCardNumberLabel style={{ color: '#ef4444' }}>Qalan</TeacherStatsCardNumberLabel>
                              <TeacherStatsCardNumberValue style={{ color: '#ef4444' }}>{stat.remainingQuestions}</TeacherStatsCardNumberValue>
                            </TeacherStatsCardNumberItem>
                          </TeacherStatsCardNumbers>
                          <TeacherStatsProgressBar percentage={stat.progressPercentage}>
                            <TeacherStatsProgressFill percentage={stat.progressPercentage} />
                          </TeacherStatsProgressBar>
                          <TeacherStatsAssignmentsList>
                            <TeacherStatsAssignmentsTitle>Tapşırıqlar:</TeacherStatsAssignmentsTitle>
                            {stat.assignments.map((assignment, idx) => (
                              <TeacherStatsAssignmentItem key={idx}>
                                <TeacherStatsAssignmentInfo>
                                  {gradeToRoman(assignment.sinif)} sinif - {assignment.qrupOrVariant} {isBuraxilis ? 'variant' : 'qrup'} - {displayFenn(assignment.fenn)}
                                </TeacherStatsAssignmentInfo>
                                <TeacherStatsAssignmentCount>{assignment.questionCount} sual</TeacherStatsAssignmentCount>
                              </TeacherStatsAssignmentItem>
                            ))}
                          </TeacherStatsAssignmentsList>
                        </TeacherStatsCard>
                      ))}
                    </TeacherStatsGrid>
                  </>
                );
              })()}
            </TeacherStatsContent>
          </TeacherStatsContainer>

          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '20px',
            marginBottom: '24px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>
              📊 Sinif/Qrup/Altqrup Statistikası
            </div>
            <div style={{ fontSize: '14px', color: '#64748b' }}>
              Admin boş kağıtlar da daxil - Ümumi statistika
            </div>
          </div>

          <AssignmentsList>
            {(() => {
              // Get unique grades and qrups from blokStatistics
              const grades = Array.from(new Set(Array.from(blokStatistics.values()).map(s => s.grade))).sort();
              const qruplar = ['I', 'II', 'III', 'IV'];
              const cards: JSX.Element[] = [];

              grades.forEach(grade => {
                qruplar.forEach(qrup => {
                  // Get all altqrups for this grade+qrup combination
                  const groupsForGradeQrup = Array.from(blokStatistics.values())
                    .filter(s => s.grade === grade && qrupEquals(s.qrup, qrup));

                  if (groupsForGradeQrup.length === 0) return;

                  // BLOK MOD: Her şagird üçün 9 sual var (Q28, Q29, Q30, Q58, Q59, Q60, Q88, Q89, Q90)
                  const totalStudents = groupsForGradeQrup.reduce((sum, s) => sum + s.studentCount, 0);
                  const QUESTIONS_PER_STUDENT = 9;
                  const ultraTotalQuestions = totalStudents * QUESTIONS_PER_STUDENT;
                  const ultraTotalChecked = groupsForGradeQrup.reduce(
                    (sum, s) => sum + s.overall.totalTeacherChecked + s.overall.totalAdminVerified,
                    0
                  );
                  const ultraTeacherChecked = groupsForGradeQrup.reduce(
                    (sum, s) => sum + s.overall.totalTeacherChecked,
                    0
                  );
                  // Calculate admin empty directly from student data
                  const ultraAdminEmpty = buraxilisStudents
                    .filter(s => String(s.grade || '') === grade && (s.specialtyGroup || '') === qrup)
                    .reduce((sum, student) => {
                      return sum + ['Q28', 'Q29', 'Q30', 'Q58', 'Q59', 'Q60', 'Q88', 'Q89', 'Q90'].filter(q => {
                        const qData = (student as any)[q];
                        return qData?.double_check_empty === true;
                      }).length;
                    }, 0);

                  const ultraPercentage = ultraTotalQuestions > 0
                    ? (ultraTotalChecked / ultraTotalQuestions) * 100
                    : 0;

                  const cardKey = `${grade}-${qrup}`;
                  const isExpanded = expandedStatsCards.includes(cardKey);

                  // Build fənn stats structure - iterate over fennler map
                  const fennStatsList: Array<{
                    subject: string;
                    questions: string[];
                    totalQuestions: number;
                    totalChecked: number;
                    totalTeacherChecked: number;
                    totalAdminEmpty: number;
                    studentCount: number;
                    perQuestion: { [qNum: string]: { total: number; graded: number; adminEmpty: number } };
                  }> = [];

                  // BLOK modu: Sadəcə 9 sual (Q28, Q29, Q30, Q58, Q59, Q60, Q88, Q89, Q90)
                  const BLOK_QUESTIONS = ['Q28', 'Q29', 'Q30', 'Q58', 'Q59', 'Q60', 'Q88', 'Q89', 'Q90'];

                  // Aggregate fənnlər from ALL subgroups (RK, RI, TC, DT) - no duplicates!
                  const fennMap = new Map<string, {
                    subject: string;
                    questions: string[];
                    totalQuestions: number;
                    totalChecked: number;
                    totalTeacherChecked: number;
                    totalAdminEmpty: number;
                    studentCount: number;
                    perQuestion: { [qNum: string]: { total: number; graded: number; adminEmpty: number } };
                  }>();

                  // Get all students in this grade/qrup combination for admin empty calculation
                  const studentsInGroup = buraxilisStudents.filter(s =>
                    String(s.grade || '') === grade && (s.specialtyGroup || '') === qrup
                  );

                  groupsForGradeQrup.forEach(stats => {
                    // Iterate over all fənn in this group
                    Object.entries(stats.fennler).forEach(([fennName, fennStats]) => {
                      // Filter questions to only include Blok questions
                      const questions = Object.keys(fennStats.questions).filter(q => BLOK_QUESTIONS.includes(q));

                      // Build perQuestion structure with correct admin counts
                      const perQuestion: { [qNum: string]: { total: number; graded: number; adminEmpty: number } } = {};
                      questions.forEach(qNum => {
                        const qStats = fennStats.questions[qNum];

                        // Calculate admin empty directly from students
                        const adminEmptyCount = studentsInGroup.filter(student => {
                          const qData = (student as any)[qNum];
                          return qData?.double_check_empty === true;
                        }).length;

                        perQuestion[qNum] = {
                          total: qStats.total,
                          graded: qStats.graded,
                          adminEmpty: adminEmptyCount
                        };
                      });

                      // Calculate admin empty for this fənn
                      const adminEmptyForFenn = studentsInGroup.reduce((sum, student) => {
                        return sum + questions.filter(q => {
                          const qData = (student as any)[q];
                          return qData?.double_check_empty === true;
                        }).length;
                      }, 0);

                      // If fənn already exists (from another subgroup), aggregate data
                      if (fennMap.has(fennName)) {
                        const existing = fennMap.get(fennName)!;
                        existing.totalChecked += fennStats.teacherChecked + adminEmptyForFenn;
                        existing.totalQuestions += questions.length * stats.studentCount;
                        existing.totalTeacherChecked += fennStats.teacherChecked;
                        existing.totalAdminEmpty += adminEmptyForFenn;
                        existing.studentCount += stats.studentCount;
                        // Merge perQuestion data
                        Object.entries(perQuestion).forEach(([qNum, data]) => {
                          if (!existing.perQuestion[qNum]) {
                            existing.perQuestion[qNum] = data;
                          } else {
                            existing.perQuestion[qNum].total += data.total;
                            existing.perQuestion[qNum].graded += data.graded;
                            existing.perQuestion[qNum].adminEmpty += data.adminEmpty;
                          }
                        });
                      } else {
                        // Add new fənn
                        fennMap.set(fennName, {
                          subject: fennName,
                          questions,
                          totalQuestions: questions.length * stats.studentCount,
                          totalChecked: fennStats.teacherChecked + adminEmptyForFenn,
                          totalTeacherChecked: fennStats.teacherChecked,
                          totalAdminEmpty: adminEmptyForFenn,
                          studentCount: stats.studentCount,
                          perQuestion
                        });
                      }
                    });
                  });

                  // Convert Map to array for rendering
                  fennMap.forEach((value) => {
                    fennStatsList.push(value);
                  });

                  cards.push(
                    <AssignmentCard key={cardKey} status="active">
                      <AssignmentHeader>
                        <AssignmentTitle>Sinif {grade} • Qrup {qrup}</AssignmentTitle>
                        <AssignmentSubtitle>
                          {fennStatsList.map(f => f.subject).join(', ')} • {totalStudents} şagird
                        </AssignmentSubtitle>
                      </AssignmentHeader>

                      <InfoGrid>
                        <InfoItem>
                          <InfoLabel>Tələbə Sayı</InfoLabel>
                          <InfoValue>{totalStudents}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                          <InfoLabel>Ümumi Sual Sayı</InfoLabel>
                          <InfoValue>{ultraTotalQuestions / totalStudents}</InfoValue>
                        </InfoItem>
                        <InfoItem style={{ gridColumn: '1 / -1' }}>
                          <InfoLabel>Fənnlər və suallar</InfoLabel>
                          <InfoValue
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '4px',
                              fontSize: '12px'
                            }}
                          >
                            {fennStatsList.map(s => (
                              <div key={s.subject}>
                                <span style={{ fontWeight: 600 }}>{s.subject}: </span>
                                {s.questions.map((q: string) => (
                                  <span
                                    key={q}
                                    style={{
                                      display: 'inline-block',
                                      marginRight: 4,
                                      marginTop: 2,
                                      background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                                      color: 'white',
                                      padding: '2px 8px',
                                      borderRadius: '4px',
                                      fontWeight: '600'
                                    }}
                                  >
                                    {q}
                                  </span>
                                ))}
                              </div>
                            ))}
                          </InfoValue>
                        </InfoItem>
                      </InfoGrid>

                      <ProgressSection>
                        {/* Ultra total progress */}
                        <ProgressLabel>
                          <span style={{ fontWeight: '700' }}>ÜMUMİ ULTRA PROGRESS</span>
                          <span style={{ fontWeight: 600, color: '#1e293b' }}>
                            {ultraTotalChecked}/{ultraTotalQuestions}
                          </span>
                        </ProgressLabel>
                        <div
                          style={{
                            width: '100%',
                            height: 10,
                            background: '#e2e8f0',
                            borderRadius: 5,
                            overflow: 'hidden',
                            display: 'flex',
                            marginTop: 4
                          }}
                          title={`Hoca: ${ultraTeacherChecked}, Admin boş: ${ultraAdminEmpty}, Cəmi yoxlama: ${ultraTotalChecked}/${ultraTotalQuestions}`}
                        >
                          <div
                            style={{
                              width: `${Math.min(
                                ultraTotalQuestions > 0
                                  ? (ultraTeacherChecked / ultraTotalQuestions) * 100
                                  : 0,
                                100
                              )}%`,
                              background: 'linear-gradient(135deg, #3b82f6, #2563eb)'
                            }}
                          />
                          <div
                            style={{
                              width: `${Math.min(
                                ultraTotalQuestions > 0
                                  ? (ultraAdminEmpty / ultraTotalQuestions) * 100
                                  : 0,
                                Math.max(
                                  100 -
                                    (ultraTotalQuestions > 0
                                      ? (ultraTeacherChecked / ultraTotalQuestions) * 100
                                      : 0),
                                  0
                                )
                              )}%`,
                              background: 'linear-gradient(135deg, #facc15, #eab308)'
                            }}
                          />
                        </div>
                        <div
                          style={{
                            marginTop: '8px',
                            fontSize: '12px',
                            color: '#64748b',
                            textAlign: 'right'
                          }}
                        >
                          %{Math.round(ultraPercentage)} tamamlandı • Hoca: {ultraTeacherChecked}, Admin boş: {ultraAdminEmpty}
                        </div>

                        {/* Fənn-fənn detallar */}
                        <div style={{ marginTop: 16, marginBottom: isExpanded ? 16 : 0 }}>
                          <div
                            style={{
                              fontSize: '13px',
                              fontWeight: 600,
                              color: '#64748b',
                              marginBottom: isExpanded ? '8px' : 0,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between'
                            }}
                            onClick={() =>
                              setExpandedStatsCards(prev =>
                                prev.includes(cardKey)
                                  ? prev.filter(k => k !== cardKey)
                                  : [...prev, cardKey]
                              )
                            }
                          >
                            <span>Fənn və sual üzrə detallar</span>
                            <span style={{ fontSize: 12 }}>
                              {isExpanded ? '▲ Gizlət' : '▼ Genişləndir'}
                            </span>
                          </div>

                          {isExpanded &&
                            fennStatsList.map(s => {
                              const fennPercentage = s.totalQuestions > 0
                                ? (s.totalChecked / s.totalQuestions) * 100
                                : 0;

                              return (
                                <div
                                  key={s.subject}
                                  style={{
                                    marginTop: 12,
                                    paddingTop: 8,
                                    borderTop: '1px solid #e2e8f0'
                                  }}
                                >
                                  <div
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      marginBottom: 6,
                                      fontSize: 13,
                                      color: '#475569'
                                    }}
                                  >
                                    <span style={{ fontWeight: 600 }}>{s.subject}</span>
                                    <span style={{ fontWeight: 600 }}>
                                      {s.totalChecked}/{s.totalQuestions}
                                    </span>
                                  </div>
                                  <div
                                    style={{
                                      width: '100%',
                                      height: 8,
                                      background: '#e2e8f0',
                                      borderRadius: 4,
                                      overflow: 'hidden',
                                      display: 'flex',
                                      marginBottom: 8
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: `${Math.min(
                                          s.totalQuestions > 0
                                            ? (s.totalTeacherChecked / s.totalQuestions) * 100
                                            : 0,
                                          100
                                        )}%`,
                                        background: 'linear-gradient(135deg, #3b82f6, #2563eb)'
                                      }}
                                    />
                                    <div
                                      style={{
                                        width: `${Math.min(
                                          s.totalQuestions > 0
                                            ? (s.totalAdminEmpty / s.totalQuestions) * 100
                                            : 0,
                                          Math.max(
                                            100 -
                                              (s.totalQuestions > 0
                                                ? (s.totalTeacherChecked / s.totalQuestions) * 100
                                                : 0),
                                            0
                                          )
                                        )}%`,
                                        background: 'linear-gradient(135deg, #facc15, #eab308)'
                                      }}
                                    />
                                  </div>

                                  {s.questions.map(qNum => {
                                    const perQ = s.perQuestion[qNum];
                                    const denom = s.studentCount || totalStudents;
                                    const qTeacherPerc = denom > 0 ? (perQ?.graded || 0) / denom * 100 : 0;
                                    const qAdminPerc = denom > 0 ? (perQ?.adminEmpty || 0) / denom * 100 : 0;
                                    const totalChecked = (perQ?.graded || 0) + (perQ?.adminEmpty || 0);

                                    return (
                                      <QuestionProgressItem key={`${s.subject}-${qNum}`}>
                                        <QuestionBadge>{qNum}</QuestionBadge>
                                        <div
                                          style={{
                                            flex: 1,
                                            height: 6,
                                            background: '#e2e8f0',
                                            borderRadius: 3,
                                            overflow: 'hidden',
                                            display: 'flex'
                                          }}
                                          title={`Hoca: ${perQ?.graded || 0}, Admin boş: ${perQ?.adminEmpty || 0}, Cəmi yoxlama: ${totalChecked}/${denom}`}
                                        >
                                          <div
                                            style={{
                                              width: `${Math.min(qTeacherPerc, 100)}%`,
                                              background: 'linear-gradient(135deg, #3b82f6, #2563eb)'
                                            }}
                                          />
                                          <div
                                            style={{
                                              width: `${Math.min(
                                                qAdminPerc,
                                                Math.max(100 - qTeacherPerc, 0)
                                              )}%`,
                                              background: 'linear-gradient(135deg, #facc15, #eab308)'
                                            }}
                                          />
                                        </div>
                                        <QuestionProgressText>
                                          Hoca: {perQ?.graded || 0} • Admin: {perQ?.adminEmpty || 0} /{' '}
                                          {denom}
                                        </QuestionProgressText>
                                      </QuestionProgressItem>
                                    );
                                  })}
                                </div>
                              );
                            })}
                        </div>
                      </ProgressSection>
                    </AssignmentCard>
                  );
                });
              });

              return cards.length > 0 ? cards : (
                <EmptyState>
                  Statistika üçün veri bulunamadı
                </EmptyState>
              );
            })()}
          </AssignmentsList>
        </>
      )}

      {/* Blok Statistics Tab - LEGACY format: Not available */}
      {activeTab === 'statistics' && !isBuraxilis && !hasNewBlokFormat && (
        <LegacyModeMessage>
          <LegacyModeIcon>!</LegacyModeIcon>
          <LegacyModeTitle>Bu Xüsusiyyət Mövcud Deyil</LegacyModeTitle>
          <LegacyModeText>
            Bu xüsusiyyət yalnız yeni Blok formatında mövcuddur. Köhnə formatdakı imtahan nəticələrini "Ümumi Baxış" tabında görə bilərsiniz.
          </LegacyModeText>
          <LegacyModeButton onClick={() => setActiveTab('overview')}>
            Ümumi Baxışa Qayıt
          </LegacyModeButton>
        </LegacyModeMessage>
      )}

      {/* Pool Assignment Creation Modal */}
      {showPoolModal && (
        <ModalOverlay onClick={() => setShowPoolModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h2>🏊 Hovuz Ataması Yarat</h2>
              <ModalCloseButton onClick={() => setShowPoolModal(false)}>Bağla</ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              {poolError && (
                <div style={{
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  color: '#991b1b',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  fontSize: '14px'
                }}>
                  ❌ {poolError}
                </div>
              )}
              {poolSuccess && (
                <div style={{
                  background: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  color: '#166534',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  fontSize: '14px'
                }}>
                  ✅ {poolSuccess}
                </div>
              )}

              <FormGroup>
                <label>Sinif</label>
                <FormSelect
                  value={poolFormData.sinif}
                  onChange={(e) => setPoolFormData(prev => ({
                    ...prev,
                    sinif: e.target.value,
                    fenn: '', // Reset fenn when sinif changes
                    selectedTeacherIds: [] // Reset teacher selection
                  }))}
                >
                  <option value="">Sinif seçin...</option>
                  <option value="8">8</option>
                  <option value="9">9</option>
                  <option value="10">10</option>
                  <option value="11">11</option>
                </FormSelect>
              </FormGroup>

              <FormGroup>
                <label>Fənn</label>
                <FormSelect
                  value={poolFormData.fenn}
                  onChange={(e) => setPoolFormData(prev => ({
                    ...prev,
                    fenn: e.target.value,
                    selectedTeacherIds: [] // Reset teacher selection when fenn changes
                  }))}
                  disabled={!poolFormData.sinif}
                >
                  <option value="">Fənn seçin...</option>
                  {getPoolSubjectsForSinif(poolFormData.sinif).map(f => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </FormSelect>
              </FormGroup>

              <FormGroup>
                <label>Variant</label>
                <FormSelect
                  value={poolFormData.variant}
                  onChange={(e) => setPoolFormData(prev => ({ ...prev, variant: e.target.value }))}
                >
                  <option value="">Variant seçin...</option>
                  <option value="A">A</option>
                  <option value="B">B</option>
                </FormSelect>
              </FormGroup>

              {/* Auto-selected question numbers display */}
              {poolFormData.sinif && poolFormData.fenn && (
                <FormGroup>
                  <label>Sual Nömrələri (avtomatik seçildi)</label>
                  <div style={{
                    background: '#f0f9ff',
                    border: '1px solid #bae6fd',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    fontSize: '14px',
                    color: '#0369a1',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }}>
                    {getPoolQuestionsForSelection(poolFormData.sinif, poolFormData.fenn).map(q => (
                      <span key={q} style={{
                        background: '#0284c7',
                        color: 'white',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: 500
                      }}>{q}</span>
                    ))}
                  </div>
                </FormGroup>
              )}

              <FormGroup>
                <label>Müəllimlər ({poolFormData.selectedTeacherIds.length} seçildi)</label>
                <TeacherCheckboxList>
                  {teachers
                    .filter(t => {
                      // Filter teachers by selected fenn if pool fenn is selected
                      if (!poolFormData.fenn) return true;
                      const teacherFenn = t.fenn || '';
                      const teacherFennList = teacherFenn.split(',').map((f: string) => f.trim());

                      // Normalize pool fenn to match teacher.fenn format
                      // Foreign languages in pool use "dili" suffix, but teacher.fenn uses base names
                      let normalizedPoolFenn = poolFormData.fenn;
                      if (['İngilis dili', 'Rus dili', 'Alman dili', 'Fransız dili'].includes(poolFormData.fenn)) {
                        normalizedPoolFenn = poolFormData.fenn.replace(' dili', '');
                      }

                      return teacherFennList.includes(normalizedPoolFenn);
                    })
                    .map(teacher => (
                      <TeacherCheckboxItem key={teacher.id}>
                        <input
                          type="checkbox"
                          checked={poolFormData.selectedTeacherIds.includes(teacher.id)}
                          onChange={() => handleToggleTeacherSelection(teacher.id)}
                        />
                        <div className="teacher-info">
                          <div className="name">{teacher.name}</div>
                          <div className="fenn">{displayFenn(teacher.fenn)}</div>
                        </div>
                      </TeacherCheckboxItem>
                    ))
                  }
                </TeacherCheckboxList>
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <ModalButton onClick={() => setShowPoolModal(false)}>
                Ləğv et
              </ModalButton>
              <ModalButton
                $variant="primary"
                onClick={handleCreatePool}
                disabled={poolCreating}
              >
                {poolCreating ? 'Yaradılır...' : '🏊 Hovuz Yarat'}
              </ModalButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}

    </Container>
  );
};

export default AssignmentManagement;
